import React, { useState, useEffect, useCallback, useMemo, useRef, use } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { GoogleMap, useLoadScript, Marker } from "@react-google-maps/api";
import { showSuccess, showError } from "toast/toast";
import { useNavigate, useParams, Link, useLocation } from "react-router-dom";
import Select, { SingleValue, components } from "react-select";
import { AsyncPaginate, LoadOptions } from "react-select-async-paginate";
import { Addresses, PaymentInfoItem, PaymentMethod, PaymentGateway, Warehouse, Zone, Rack, Bin } from "types";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe, Stripe } from "@stripe/stripe-js";
import PaymentFormManualOrder from "../../components/PaymentFormManualOrder";
import { REMOVAL_ORDER_CANCELLED_STATUS_CODE, RemovalStatusBadge } from "components/Removalstatus";
import { StatusBadge } from "components/Productlocationstatus";
import InstallationStatusTracker from "components/InstallationStatusTracker";
import axios from "axios";
import SectionHeader from "components/SectionPillPropsIcons";
import ProofSection, { ProofFile } from "../../components/ProofOfInstallation";
import OrderLog, { buildRemovalLogEventsFromOrder } from "../../components/OrderLog";
import ProductSelection from "assets/images/brands/ProductSelection.png"
import Doller from "assets/images/brands/doller.png"
import PaymentPanel from "components/PaymentOptionsCard";
import Instructions from "components/Instructions";
import type { InvoiceType } from "components/InvoiceDocument";
import { Button } from "react-bootstrap";
import DeleteConfirmModal from "components/ToolTips/DeleteConfirmModal";
import SpecialFeeui from "components/Tax/InstallationandremovalSpecialfeeui";
import { OrderDiscountCodeField } from "components/orderResolve";
import {
    applyRemovalWorkOrderResolveResponse,
    buildRemovalPatchFromResolved,
    buildRemovalResolvePayload,
    getOrderDiscountFieldDisplay,
    getResolvedDiscountDisplay,
    mapInstallationSpecialFeeForResolve,
    mapInstallationSpecialFeeForSubmit,
    mapSavedInstallationSpecialFeeToUi,
    normalizeInstallationSelectedFee,
    overlayRemovalSubmitFromResolved,
    postRemovalOrderResolve,
    type InstallationResolveAddress,
    type OrderResolveAction,
    type ResolvedRemovalOrder,
} from "orderResolve";
import PONumber from "components/PONumber";
import RefreshIconButton from "components/RefreshIconButton";
import {
    requestInstallationTaxRefund,
    type RoleType,
    type Mode,
    type Product as TaxProduct,
} from "components/Tax/INstallationandRemovalsTaxEngine";
// TaxCloud helpers + types
import {
    toTaxCloudAddressFromObject,
    upsertTaxCloudCartMulti,
    createTaxCloudOrder,
    completeTaxCloudOrder,
    GetVersionOrdernumber,
    PostVersionOrdernumber,
    type TaxCloudAddress,
} from "components/Tax/InstallationandRemovalTaxcloud";
// Active tax type
import { getActiveTaxType } from "components/Tax/manualtax";
import { sendTriggerEmails } from "utils/emailService";
import { resolveSendQuoteEmailTrigger } from "components/SalesOrderDetails/resolveSendQuoteEmailTrigger";
import { sendRemovalOrderTriggerEmail } from "utils/salesOrderTriggerEmailEngine";
import { safeConsole, createSafeNavigate } from "utils/envUtils";
import { handleApiError } from "utils/errorHandler";
import { buildSpecialFeeCostBreakdownDisplay } from "utils/specialFeeCostBreakdownDisplay";
import { getAuthCookie } from "../../utils/authCookies";
import { createSendLogEntry } from "api/postTypeApiCalls";
import {
    buildInvoiceEmailParamsForSend,
    buildInvoiceSendRecipientPlan,
    sendInvoiceTriggerEmails,
} from "utils/invoiceEmailHelpers";
import SendOrderEmailRecipientsModal, {
    appendAdditionalRecipientEmails,
    mergeAdditionalIntoInvoiceCustomerRecipients,
} from "components/SendOrderEmailRecipientsModal";
import {
    buildRemovalOrderInvoicePdfAttachment,
    buildRemovalOrderPdfAttachment,
} from "utils/installationOrderPdfAttachment";
import { resolveInstallationBrokerageTeamMemberEmail } from "utils/brokerageTeamMemberEmail";
import { formatOrderReadyPaymentDisplay, buildOrderReadyLineItemsHtml, buildOrderReadyAddressFieldRowHtml, buildOrderReadyCustomerDisplayName, fetchMarketplaceVendorCompanyName } from "components/SalesOrderDetails/buildYourOrderReadyEmailParams";
import { fetchPublicOrderQuoteModeSetting } from "components/proejctsandquoteapicalls";

/** Maps API `licenseType` to `emailConfig` keys for `sendTriggerEmails`. */
function resolveEmailTemplateLicenseType(licenseType: string | undefined): string {
    const lt = String(licenseType || "");
    if (lt === "BROKERAGE_FIRM") return "BROKERAGE_FIRM";
    if (lt === "BROKERAGE_TEAM_MEMBER") return "BROKERAGE_TEAM_MEMBER";
    if (lt === "BROKERAGE_AGENT") return "BROKERAGE_AGENT";
    if (lt === "AGENT") return "AGENT";
    if (lt === "SIGNAGE_FIRM") return "SIGNAGE_FIRM";
    if (lt === "SIGNAGE_TEAM_MEMBER") return "SIGNAGE_TEAM_MEMBER";
    return "AGENT";
}

function getRemovalLineItemId(item: { _id?: unknown; line_item_id?: string } | null | undefined): string {
    if (!item) return "";
    const fromStored = String(item.line_item_id ?? "").trim();
    if (fromStored) return fromStored;
    const raw = item._id;
    if (raw == null || raw === "") return "";
    if (typeof raw === "object") {
        const oid = (raw as { $oid?: string }).$oid;
        if (oid) return String(oid).trim();
        const str = (raw as { toString?: () => string }).toString?.();
        return str && str !== "[object Object]" ? String(str).trim() : "";
    }
    return String(raw).trim();
}

function normalizeRemovalLineItemFromApi<T extends Record<string, unknown>>(item: T): T & { line_item_id?: string } {
    const lineItemId = getRemovalLineItemId(item as { _id?: unknown; line_item_id?: string });
    return {
        ...item,
        ...(lineItemId ? { line_item_id: lineItemId } : {}),
    };
}

/** Include `_id` only for lines already saved on the removal order. */
function withRemovalLineItemIdForPayload<T extends Record<string, unknown>>(item: T): Omit<T, "_id"> & { _id?: string } {
    const lineItemId = getRemovalLineItemId(item as { _id?: unknown; line_item_id?: string });
    const { _id: _omit, ...rest } = item as T & { _id?: string };
    if (!lineItemId) return rest;
    return { ...rest, _id: lineItemId };
}

/** Same pattern as installation `buildInstallationYourOrderReadyEmailParams`, for removal + `/removalsummary`. */
async function buildRemovalYourOrderReadyEmailParams(order: any, token: string) {
    const baseUrl = (
        process.env.REACT_APP_BASE_URL ||
        (typeof window !== "undefined" ? window.location.origin : "") ||
        ""
    ).replace(/\/+$/, "");
    const orderIdForPay = String(order?._id || order?.id || "").trim();
    const items = Array.isArray(order?.line_items) ? order.line_items : [];
    const firstOrderItem = items[0] || {};
    const orderReadyItemName = String(firstOrderItem?.product_name || "Removal Service");
    const orderReadyQty = String(firstOrderItem?.quantity || 1);
    const orderReadyTotal = `$${Number(order?.total_amount || 0).toFixed(2)}`;
    const customerName = buildOrderReadyCustomerDisplayName(order);

    let projectIdFromQuery = String(order?.project_id || "").trim();
    let accessProjectIdForPay = projectIdFromQuery;
    let accessTokenForPay = "";
    const payUserIdForStripeLinks = String(getAuthCookie("userId") || "").trim();

    if (token && projectIdFromQuery) {
        try {
            const { fetchProjectPublicAccess } = await import("components/proejctsandquoteapicalls");
            const accessRes = await fetchProjectPublicAccess(token, projectIdFromQuery);
            const accessData: any = (accessRes as any)?.data || {};
            const rawAccess: any = accessRes as any;
            accessProjectIdForPay = String(
                accessData.project_id ||
                accessData.projectId ||
                rawAccess?.project_id ||
                projectIdFromQuery ||
                ""
            ).trim();
            accessTokenForPay = String(
                accessData.access_token ||
                accessData.accessToken ||
                rawAccess?.access_token ||
                rawAccess?.accessToken ||
                ""
            ).trim();
            const publicUrlValue = String(
                accessData.public_url || accessData.publicUrl || rawAccess?.public_url || ""
            ).trim();
            if (!accessTokenForPay && publicUrlValue) {
                try {
                    const parsed = publicUrlValue.startsWith("http")
                        ? new URL(publicUrlValue)
                        : new URL(publicUrlValue, window.location.origin);
                    accessTokenForPay = String(
                        parsed.searchParams.get("access_token") || parsed.searchParams.get("token") || ""
                    ).trim();
                } catch {
                    /* ignore */
                }
            }
        } catch (e) {
            safeConsole.warn("fetchProjectPublicAccess (removal order summary email):", e);
        }
    }

    let viewPayUrl = `${baseUrl}/removalsummary?project_id=${encodeURIComponent(accessProjectIdForPay)}&order_id=${encodeURIComponent(orderIdForPay)}&access_token=${encodeURIComponent(accessTokenForPay)}${payUserIdForStripeLinks ? `&supplier_id=${encodeURIComponent(payUserIdForStripeLinks)}` : ""
        }`;

    try {
        const { createStripePaymentIntent, fetchPaymentGatewaysCreatedByUserId } = await import(
            "components/proejctsandquoteapicalls"
        );
        const payUserId = payUserIdForStripeLinks;
        const orderTotal = Number(order?.total_amount || 0);
        const amountCents = Math.max(0, Math.round(orderTotal * 100));
        let stripeClientSecret = "";
        if (token && payUserId && amountCents > 0) {
            const pi = await createStripePaymentIntent(token, payUserId, {
                amount: amountCents,
                currency: "usd",
            });
            stripeClientSecret = String((pi as any)?.client_secret || (pi as any)?.clientSecret || "").trim();
        }
        let stripePublishableKeyForUrl = "";
        if (token && payUserId) {
            const gateways = await fetchPaymentGatewaysCreatedByUserId(token, payUserId);
            const stripeGw = gateways.find((g: any) => String(g?.gateway || "").toLowerCase() === "stripe");
            stripePublishableKeyForUrl = String(stripeGw?.publishable_key || "").trim();
        }
        const withClientSecret = (u: string) => {
            if (!stripeClientSecret || !u) return u;
            const sep = u.includes("?") ? "&" : "?";
            return `${u}${sep}client_secret=${encodeURIComponent(stripeClientSecret)}`;
        };
        const withPublishableKey = (u: string) => {
            if (!stripePublishableKeyForUrl || !u) return u;
            const sep = u.includes("?") ? "&" : "?";
            return `${u}${sep}publishable_key=${encodeURIComponent(stripePublishableKeyForUrl)}`;
        };
        viewPayUrl = withPublishableKey(withClientSecret(viewPayUrl));
    } catch (e) {
        safeConsole.warn("Stripe helpers (removal order summary email):", e);
    }

    const { alreadyPaid, remainingBalance } = formatOrderReadyPaymentDisplay(order);
    const marketplaceCompanyName = await fetchMarketplaceVendorCompanyName(
        token,
        String(getAuthCookie("userId") || order?.signage_id || order?.seller_owner_node_id || "").trim()
    );

    return {
        customerName,
        orderReadyItemName,
        orderReadyQty,
        orderReadyTotal,
        alreadyPaid,
        remainingBalance,
        publicViewPayUrl: viewPayUrl,
        lineItems: buildOrderReadyLineItemsHtml(items),
        addressFieldRow: buildOrderReadyAddressFieldRowHtml(
            "Installation Address",
            order?.removal_details?.removal_address ||
                order?.installation_details?.removal_address ||
                order?.shipping_address
        ),
        email_signature_display_name:
            marketplaceCompanyName || String(order?.signage_company_name || "").trim(),
    };
}

const mapContainerStyle = {
    width: "100%",
    height: "350px",
    borderRadius: "12px",
    overflow: "hidden",
    // boxShadow: "0 0 10px rgba(0,0,0,0.1)", // Soft shadow
    border: "1px solid #dee2e6",          // Light border
};

const center = {
    lat: 40.712776, // Default latitude (e.g., New York City)
    lng: -74.005974, // Default longitude
};

/** Show a readable label for generated custom-product SKUs. */
function displayRemovalWorkOrderSku(item: { sku?: string; custom_sku?: string; inventory_source?: string }): string {
    if (String(item.inventory_source) !== "custom") return String(item.sku ?? "");

    const customSku = String(item.custom_sku ?? item.sku ?? "").trim();
    if (!customSku) return "";

    if (/^custom_sku(?:_|$)/i.test(customSku)) {
        return "Custom SKU";
    }

    return customSku;
}

type Selected = {
    value: string;
    label: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
}

type Combo = {
    warehouse_id: string;
    warehouse_name: string;
    zone_id: string;
    zone_name: string;
    rack_id: string;
    rack_name: string;
    bin_id: string;
    bin_name: string;
};

// Select2 types for signage team members (installers)
type SignageTeamMemberOption = {
    value: string;
    label: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
};

// Select2 types for brokerage team members
type BrokerageTeamMemberOption = {
    value: string;
    label: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    offices: any[]; // Array of office objects from API
};

// Select2 types for team members
type TeamMemberOption = {
    value: string;
    label: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    offices: any[]; // Array of office objects from API
};
const DropdownIndicator = (props: any) => {
    return (
        <components.DropdownIndicator {...props}>
            <i className="ri-arrow-down-s-line fs-5" style={{ color: '#6c757d' }}></i>
        </components.DropdownIndicator>
    );
};

// Custom styles to ensure dropdown indicator is always visible
const customSelectStyles = {
    /* hide all indicators when disabled */
    indicatorsContainer: (provided: any, state: any) => ({
        ...provided,
        display: state.isDisabled ? "none" : "flex",
    }),
    dropdownIndicator: (provided: any, state: any) => ({
        ...provided,
        display: state.isDisabled ? "none" : "flex",
    }),
    indicatorSeparator: () => ({ display: "none" }),

    /* keep bg and cursor right; do NOT fade */
    control: (provided: any, state: any) => ({
        ...provided,
        backgroundColor: state.isDisabled ? "#f2f2f2" : provided.backgroundColor,
        cursor: state.isDisabled ? "not-allowed" : "pointer",
        opacity: 1,
    }),

    /* ——— make text BLACK when disabled ——— */
    singleValue: (provided: any, state: any) => ({
        ...provided,
        color: state.isDisabled ? "#000" : provided.color,
    }),
    valueContainer: (provided: any, state: any) => ({
        ...provided,
        color: state.isDisabled ? "#000" : provided.color,
    }),
    input: (provided: any, state: any) => ({
        ...provided,
        color: state.isDisabled ? "#000" : provided.color,
        WebkitTextFillColor: state.isDisabled ? "#000" : undefined, // Safari
    }),
    placeholder: (provided: any, state: any) => ({
        ...provided,
        /* if field has no value, keep placeholder readable too */
        color: state.isDisabled ? "#000" : provided.color,
    }),

    /* if you ever use isMulti, keep chips readable */
    multiValue: (p: any, s: any) => ({
        ...p,
        backgroundColor: s.isDisabled ? "#e5e7eb" : p.backgroundColor,
    }),
    multiValueLabel: (p: any, s: any) => ({
        ...p,
        color: s.isDisabled ? "#000" : p.color,
    }),
    multiValueRemove: (p: any, s: any) => ({
        ...p,
        display: s.isDisabled ? "none" : p.display,
    }),
};


type Order = any; // keep loose to be drop-in with your current shape
type TimelineItem = {
    type: string;
    subtitle: React.ReactNode | string;
    at: string | number | Date;
    by?: string;
};

type Props = {
    order: Order | null | undefined;

    // header actions
    onBack: () => void;
    onDownloadInvoice: () => void;
    onOrderStatus: (status: number) => void;

    // order log
    timelineItems: TimelineItem[];
    colors: string[];
    parseDate: (d: any) => string;

    // OPTIONAL plug-in area for your PaymentPanel + Stripe block
    rightTopPanel?: React.ReactNode;

    // product table helpers (optional; we'll fall back gracefully)
    baseUrl?: string; // e.g., process.env.REACT_APP_BASE_URL

    // Dynamic routing support
    baseRoute?: string; // e.g., "/signage", "/realtor", "/teammember", "/signageteammember", "/agent"
};

type InstallationTaxEngineHandle = {
    recalculate: (forceType?: "manual" | "taxcloud", options?: { allowRefund?: boolean }) => Promise<boolean>;
};

function resolveRemovalOrderDiscount(
    removalOrder: any,
    formikValues: any,
    preferFormikOnly: boolean
): { code: string; amount: number } {
    if (preferFormikOnly) {
        return {
            code: String(formikValues?.discount_code ?? "").trim(),
            amount: Math.max(0, Number(formikValues?.discount_amount ?? formikValues?.discount ?? 0)),
        };
    }
    const order = removalOrder as any;
    const code = String(order?.discount_code ?? formikValues?.discount_code ?? "").trim();
    const amount = Math.max(
        0,
        Number(order?.discount_amount ?? order?.discount ?? formikValues?.discount_amount ?? formikValues?.discount ?? 0)
    );
    return { code, amount };
}

function getSubmitRemovalDiscount(values: any): { code: string; amount: number } {
    const code = String(values?.discount_code ?? "").trim();
    const amount = Math.max(0, Number(values?.discount_amount ?? values?.discount ?? 0));
    return { code, amount };
}

const SignageRemovalWorkOrder = () => {
    const { id } = useParams(); // get ID from URL
    const location = useLocation();
    const type = location.state?.type ?? "view"; // "view"
    const mode = (type || "View").toLowerCase() as "view" | "edit";
    const isEdit = mode === "edit";
    const Viewmode = !isEdit;
    const isSignageTeammember = location.pathname.includes('/signageteammember');
    const baseRoute = isSignageTeammember ? '/signageteammember' : '/signage';
    const token = getAuthCookie("sessionToken");
    const userId = getAuthCookie("userId");
    const userName = getAuthCookie("userName");
    const licenseType = getAuthCookie("licenseType");

    // State for parent signage firm (for signageteammember login)
    const [parentSignageId, setParentSignageId] = useState<string | null>(null);
    const [parentSignageName, setParentSignageName] = useState<string | null>(null);
    const signageId = (isSignageTeammember && parentSignageId) ? parentSignageId : (userId || "");
    const [loading, setLoading] = useState(true);
    const [showReportTaxConfirmModal, setShowReportTaxConfirmModal] = useState(false);
    const [error, setError] = useState<string>("");
    const [preview, setPreview] = useState<string | null>(null);
    const [formSubmitting, setFormSubmitting] = useState(false);
    const [uploadProgress, setUploadProgress] = useState<number>(0);
    const [quantity, setQuantity] = useState(1);
    const [addressObjects, setAddressObjects] = useState<any[]>([]);
    const [showAllocateModal, setShowAllocateModal] = useState(false);
    const [removalOrder, setRemovalOrder] = useState<any>(null);
    const [quoteModeEnabled, setQuoteModeEnabled] = useState(false);
    const [orderSummarySending, setOrderSummarySending] = useState(false);
    const [emailRecipientsModal, setEmailRecipientsModal] = useState<null | "quote" | "invoice">(null);

    // ✅ Track initial form values to detect changes
    const initialFormValuesRef = React.useRef<any>(null);
    const [installer, setInstaller] = useState<any[]>([]);
    const [selectedInstaller, setSelectedInstaller] = useState<Selected | null>(null);
    const [scheduledJobsCount, setScheduledJobsCount] = useState<number | null>(null);
    const [updatedProducts, setUpdatedProducts] = useState<any[]>([]);
    const [teamMembers, setTeamMembers] = useState<any[]>([]);

    // State for AsyncPaginate selected options
    const [selectedSignageTeamMemberOption, setSelectedSignageTeamMemberOption] = useState<SignageTeamMemberOption | null>(null);
    const [selectedBrokerageTeamMemberOption, setSelectedBrokerageTeamMemberOption] = useState<BrokerageTeamMemberOption | null>(null);
    const [selectedBrokerageFirm, setSelectedBrokerageFirm] = useState<Selected | null>(null);
    const [selectedTeamMember, setSelectedTeamMember] = useState<Selected | null>(null);
    const [selectedOffice, setSelectedOffice] = useState<Selected | null>(null);
    const [selectedOfficeAgent, setSelectedOfficeAgent] = useState<Selected | null>(null);
    const [selectedAgent, setSelectedAgent] = useState<Selected | null>(null);
    const [productNotes, setProductNotes] = useState<Record<string, { bin_location: string; bin_location_display: string; note: string }>>({});
    const [updatedRotationalUnits, setUpdatedRotationalUnits] = useState<Record<string, {
        current_status: string;
        restock_quantity: number;
        writeoff_quantity: number;
        damaged_quantity: number;
        reason_to_writeoff: string;
        repair: number;
        bin_location: string;
        bin_location_display: string;
        note: string;
    }>>({});
    const [paymentMethod, setPaymentMethod] = useState<PaymentMethod[]>([]);
    const [payment, setPayment] = useState(false);
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<{ name: string; _id: string } | null>(null);
    const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
    const [zones, setZones] = useState<Zone[]>([]);
    const [racks, setRacks] = useState<Rack[]>([]);
    const [bins, setBins] = useState<Bin[]>([]);
    const [clientSecret, setClientSecret] = useState<string | "">();
    const [previewFiles, setPreviewFiles] = useState<{ file: File; url: string }[]>([]);
    const [uploadedFileMeta, setUploadedFileMeta] = useState<any[]>([]);
    const proofUploadHandlerRef = useRef<(() => Promise<void>) | null>(null);
    const [removalQuantities, setRemovalQuantities] = useState<Record<string, number>>({});
    const [instructions, setInstructions] = useState<string>("");
    const [finalSubmittedItems, setFinalSubmittedItems] = useState<any>(null);
    const installationTaxRef = React.useRef<InstallationTaxEngineHandle | null>(null);

    const [selectedLocation, setSelectedLocation] = useState<{
        lat: number;
        lng: number;
    } | null>(null);

    const [discountUserTouched, setDiscountUserTouched] = useState(false);

    // ✅ Discount and Special Fee States
    const [selectedSpecialFees, setSelectedSpecialFees] = useState<any[]>([]);
    const [allAvailableSpecialFees, setAllAvailableSpecialFees] = useState<any[]>([]);
    const [isRecomputing, setIsRecomputing] = useState(false);
    const [removalCost, setRemovalCost] = useState<number>(0);
    const [dynamicCostId, setDynamicCostId] = useState<string>("");
    const [dynamicTaxable, setDynamicTaxable] = useState<boolean>(false);
    const [zeroOrNegativeMsg, setZeroOrNegativeMsg] = useState("");
    const [showTaxDetails, setShowTaxDetails] = useState(false);
    // Tax type + live tax workflow state
    const [taxType, setTaxType] = useState<"manual" | "taxcloud" | "unknown">("unknown");
    const [liveTaxLoading, setLiveTaxLoading] = useState(false);
    const [liveTaxReady, setLiveTaxReady] = useState(false);
    const [taxCartId, setTaxCartId] = useState<string | undefined>(undefined);
    const [ordernumber, setOrderNumber] = useState<string | null>(null);
    const skipLiveTaxNavigateRef = useRef(false);
    const completeAfterTaxReportRef = useRef(false);
    const lastResolvedRemovalRef = useRef<ResolvedRemovalOrder | null>(null);
    const latestDiscountCodeRef = useRef("");
    const formikValuesRef = useRef<any>(null);
    const isUpdatingFromRecomputeRef = useRef(false);
    const pendingRecomputeRef = useRef(false);
    const lastRecomputeParamsRef = useRef("");
    const recomputeRequestIdRef = useRef(0);
    const didInitialEditResolveRef = useRef(false);
    const prevFeesRef = useRef("");
    const [couponInput, setCouponInput] = React.useState("");

    const handlePaymentMethodSelect = React.useCallback(
        (method: { name: string; _id: string }) => {
            setSelectedPaymentMethod(method);
        },
        []
    );

    // ✅ Ref to track if special fees have been seeded from backend
    const seededSpecialFeesRef = useRef(false);

    const { isLoaded } = useLoadScript({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY as string, // Replace with your API key
    });

    const navigate = useNavigate();
    const safeNavigate = createSafeNavigate(navigate);

    const getRemovalSendLogOrderId = (order: any) =>
        String(order?.id || order?._id || order?.order_id || "").trim();

    useEffect(() => {
        if (isSignageTeammember && !parentSignageId) {
            return;
        }
        const ownerId = String(signageId || "").trim();
        if (!ownerId) {
            setQuoteModeEnabled(false);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const res = await fetchPublicOrderQuoteModeSetting(ownerId);
                if (!cancelled) setQuoteModeEnabled(Boolean(res.enabled));
            } catch {
                if (!cancelled) setQuoteModeEnabled(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [signageId, isSignageTeammember, parentSignageId]);

    const handleRemovalSendQuoteClick = React.useCallback(async (additionalEmails: string[] = []) => {
        if (!removalOrder || orderSummarySending) return;
        const cust = String(removalOrder?.customer_email || "").trim();
        const userEm = String(removalOrder?.user_email || "").trim();
        const teamMemberEmail = resolveInstallationBrokerageTeamMemberEmail(removalOrder);
        const primaryEmail =
            teamMemberEmail && cust && userEm && cust.toLowerCase() !== userEm.toLowerCase()
                ? cust
                : String(userEm || cust || "").trim();
        if (!primaryEmail) {
            showError("No customer email on this order; cannot send the order summary email.");
            return;
        }
        if (!token) {
            showError("Session expired. Please sign in again.");
            return;
        }
        setOrderSummarySending(true);
        try {
            await new Promise<void>((r) => requestAnimationFrame(() => requestAnimationFrame(() => r())));
            const removalOrderId = String(removalOrder?._id || removalOrder?.id || "").trim();
            if (!removalOrderId) {
                showError("Missing removal order id. Email was not sent.");
                return;
            }
            const safeNum = String(
                removalOrder._id || removalOrder.id || "order"
            ).replace(/[^\w.-]+/g, "_");
            const pdfAttachment = await buildRemovalOrderPdfAttachment({
                removalOrderId,
                token,
                fileLabel: safeNum,
            });
            if (!pdfAttachment) {
                showError("Could not fetch removal order PDF. Email was not sent.");
                return;
            }
            const p = await buildRemovalYourOrderReadyEmailParams(removalOrder, token);
            const licenseTypes: string[] = ["AGENT"];
            const recipients: string[] = [primaryEmail];
            if (
                teamMemberEmail &&
                teamMemberEmail.toLowerCase() !== primaryEmail.toLowerCase()
            ) {
                licenseTypes.push("BROKERAGE_TEAM_MEMBER");
                recipients.push(teamMemberEmail);
            }
            const merged = appendAdditionalRecipientEmails(
                recipients,
                licenseTypes,
                additionalEmails
            );
            const sendQuoteTrigger = await resolveSendQuoteEmailTrigger(
                "removal",
                removalOrder
            );
            await sendTriggerEmails(
                sendQuoteTrigger,
                merged.licenseTypes,
                {
                    customerName: p.customerName,
                    orderId: String(
                        removalOrder?._id || removalOrder.id || "order"
                    ),
                    quantity: p.orderReadyQty,
                    totalDue: p.orderReadyTotal,
                    alreadyPaid: p.alreadyPaid,
                    remainingBalance: p.remainingBalance,
                    viewPayUrl: p.publicViewPayUrl,
                    addressFieldRow: p.addressFieldRow,
                    email_signature_display_name: p.email_signature_display_name,
                },
                merged.emails,
                process.env.REACT_APP_FROM_EMAIL2 || "",
                token,
                pdfAttachment
            );
            const sendLogOrderId = getRemovalSendLogOrderId(removalOrder);
            if (sendLogOrderId) {
                try {
                    await createSendLogEntry({
                        orderType: "removal_orders",
                        orderId: sendLogOrderId,
                        type: "quote",
                        token,
                    });
                } catch (e) {
                    safeConsole.warn("[SignageRemovalWorkOrder] send-log quote failed:", e);
                }
            }
            showSuccess("Quote sent successfully.");
        } catch (e) {
            showError(e instanceof Error ? e.message : "Failed to send order summary email.");
        } finally {
            setOrderSummarySending(false);
        }
    }, [removalOrder, orderSummarySending, token]);

    const handleRemovalOrderSummaryPreviewClick = React.useCallback(() => {
        if (!removalOrder) return;
        const br = baseRoute || "/signage";
        const orderForPreview = {
            ...removalOrder,
            order_type: "removal",
            signage_id:
                removalOrder?.signage_id ??
                removalOrder?.seller_owner_node_id ??
                getAuthCookie("userId") ??
                "",
        };
        try {
            sessionStorage.setItem(
                "orderSummaryPrivatePreview",
                JSON.stringify({
                    privateOrderSummaryPreview: true,
                    order: orderForPreview,
                    ts: Date.now(),
                })
            );
        } catch {
            /* ignore */
        }
        safeNavigate(`${br}/RemovalOrderSummary`, {
            state: {
                privateOrderSummaryPreview: true,
                order: orderForPreview,
            },
        });
    }, [removalOrder, baseRoute, safeNavigate]);

    const canEditProof = [400, 500, 600].includes(removalOrder?.status);

    // Fetch final submitted items
    const fetchFinalSubmittedItems = async (removalOrderId: string) => {
        try {
            if (!removalOrderId) {
                // console.log("No removal order ID available yet");
                return;
            }

            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/removal-orders/${removalOrderId}/final-submitted-items`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            const data = await response.json();

            if (!response.ok) {
                safeConsole.error("Failed to fetch final submitted items:", data.error);
                return;
            }

            setFinalSubmittedItems(data);
            // console.log("Fetched final submitted items:", data);
        } catch (err) {
            safeConsole.error("Error fetching final submitted items:", err);
        }
    };

    useEffect(() => {
        return () => {
            if (preview) URL.revokeObjectURL(preview);
        };
    }, [preview]);

    // Fetch final submitted items when removalOrder status is 600
    useEffect(() => {
        if (removalOrder?.status === 600 && removalOrder?._id) {
            fetchFinalSubmittedItems(removalOrder._id);
        }
    }, [removalOrder?.status, removalOrder?._id]);

    // Populate UI fields with final submitted items data
    useEffect(() => {
        if (finalSubmittedItems && updatedProducts && updatedProducts.length > 0) {

            populateFieldsFromFinalSubmittedItems();
        }
    }, [finalSubmittedItems]);

    // Function to populate UI fields with final submitted items data
    const populateFieldsFromFinalSubmittedItems = () => {
        if (!finalSubmittedItems || !updatedProducts) return;

        // Group final submitted items by product_id
        const itemsByProduct = finalSubmittedItems.reduce((acc: any, item: any) => {
            if (!acc[item.product_id]) {
                acc[item.product_id] = [];
            }
            acc[item.product_id].push(item);
            return acc;
        }, {});

        // Update products with final submitted data
        const updatedProductsWithFinalData = updatedProducts.map((product: any) => {
            const finalItems = itemsByProduct[product.product_id] || [];

            if (product.inventory_source === "rotational") {
                // For rotational products, aggregate the data
                const aggregatedData = finalItems.reduce((acc: any, item: any) => {
                    acc.restocked += item.restocked || 0;
                    acc.damaged += item.damaged || 0;
                    acc.lost += item.lost || 0;
                    acc.repair += item.repair || 0;
                    return acc;
                }, { restocked: 0, damaged: 0, lost: 0, repair: 0 });

                return {
                    ...product,
                    restock_quantity: aggregatedData.restocked,
                    damaged_quantity: aggregatedData.damaged,
                    lost_quantity: aggregatedData.lost,
                    repair_quantity: aggregatedData.repair,
                };
            } else {
                // For normal products, aggregate the data
                const aggregatedData = finalItems.reduce((acc: any, item: any) => {
                    acc.restocked += item.restocked || 0;
                    acc.damaged += item.damaged || 0;
                    acc.lost += item.lost || 0;
                    return acc;
                }, { restocked: 0, damaged: 0, lost: 0 });

                // Determine current_status from final items
                // For normal products, prioritize restocked item's current_status
                let determinedStatus = product.current_status; // Keep original if no items
                if (finalItems.length > 0) {
                    const restockedItem = finalItems.find((item: any) => item.restocked > 0);
                    const damagedItem = finalItems.find((item: any) => item.damaged > 0);
                    const lostItem = finalItems.find((item: any) => item.lost > 0);

                    if (restockedItem && restockedItem.current_status) {
                        determinedStatus = restockedItem.current_status;
                        // console.log(`Product ${product.product_id}: Using restocked status = ${determinedStatus}`);
                    } else if (damagedItem && damagedItem.condition_status) {
                        determinedStatus = damagedItem.condition_status;
                        // console.log(`Product ${product.product_id}: Using damaged condition_status = ${determinedStatus}`);
                    } else if (lostItem && lostItem.condition_status) {
                        determinedStatus = lostItem.condition_status;
                        // console.log(`Product ${product.product_id}: Using lost condition_status = ${determinedStatus}`);
                    }
                }

                return {
                    ...product,
                    restock_quantity: aggregatedData.restocked,
                    damaged_quantity: aggregatedData.damaged,
                    writeoff_quantity: aggregatedData.lost, // Lost items map to writeoff_quantity for normal products
                    current_status: determinedStatus,
                };
            }
        });

        setUpdatedProducts(updatedProductsWithFinalData);

        // Update rotational units with final submitted data
        const updatedRotationalUnitsWithFinalData = { ...updatedRotationalUnits };
        const updatedProductNotesWithFinalData = { ...productNotes };
        console.log("finalSubmittedItems", finalSubmittedItems);

        finalSubmittedItems.forEach((item: any) => {
            if (item.inventory_source === "rotational" && item.serial_id) {
                // Create bin location string from location data
                const binLocationString = item.location ?
                    `${item.location.warehouse_name} > ${item.location.zone_name} > ${item.location.rack_name} > ${item.location.bin_name}` :
                    "";

                updatedRotationalUnitsWithFinalData[item.serial_id] = {
                    ...updatedRotationalUnitsWithFinalData[item.serial_id],
                    restock_quantity: item.restocked || 0,
                    damaged_quantity: item.damaged || 0,
                    writeoff_quantity: item.lost || 0, // Using writeoff_quantity for lost items
                    repair: item.repair || 0,
                    current_status: item.condition_status || "Good",
                    bin_location: item.location?.bin_id || "",
                    bin_location_display: binLocationString,
                    note: item.submitted_notes || "",
                };
            } else if (item.inventory_source === "agent" || item.inventory_source === "office") {
                // Handle normal products - update product notes with bin location
                const binLocationString = item.location ?
                    `${item.location.warehouse_name} > ${item.location.zone_name} > ${item.location.rack_name} > ${item.location.bin_name}` :
                    "";

                updatedProductNotesWithFinalData[item.product_id] = {
                    ...updatedProductNotesWithFinalData[item.product_id],
                    bin_location: item.location?.bin_id || "",
                    bin_location_display: binLocationString,
                    note: item.submitted_notes || "",
                };
            }
        });

        setUpdatedRotationalUnits(updatedRotationalUnitsWithFinalData);
        console.log("updatedProductNotesWithFinalData", updatedProductNotesWithFinalData);
        setProductNotes(updatedProductNotesWithFinalData);
    };

    // Auto-set default bin locations when entering restocking state
    useEffect(() => {
        if (removalOrder?.status === 500 && updatedProducts && updatedProducts.length > 0) {
            // Small delay to ensure all data is loaded
            const timer = setTimeout(() => {
                setDefaultBinLocations();
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [removalOrder?.status, updatedProducts]);

    const addressOptions =
        addressObjects !== null
            ? addressObjects?.map((address) => ({
                ...address,
                value: address._id,
                label: `${address.label}: ${address.address_details.addressLine1}, ${address.address_details.city}, ${address.address_details.state}, ${address.address_details.zip ? address.address_details.zip : ''}`,
            }))
            : [];

    const priorityOptions = [
        { value: 1, label: "Critical" },
        { value: 2, label: "High Priority" },
        { value: 3, label: "Normal" },
    ];

    const [selectedBillingAddress, setSelectedBillingAddress] =
        useState<Addresses | null>(null);
    const [selectedOfficeBillingAddress, setSelectedOfficeBillingAddress] =
        useState<Addresses | null>(null);

    const [selectedInstallationAddress, setSelectedInstallationAddress] =
        useState<Addresses | null>(null);
    const teamMemberOptions = teamMembers?.map((member) => ({
        value: member.id,
        label: `${member.firstName} ${member.lastName}`,
        email: member.email,
        phone: member.phone1,
        offices: member.offices,
    }));

    // Fetch parent hierarchy for signageteammember login
    const fetchParentHierarchy = async () => {
        if (!isSignageTeammember || !token) return;

        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/parent-hierarchy`,
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (response.ok) {
                const data = await response.json();

                // Find the parent with license_type "SIGNAGE_FIRM"
                const signageFirm = data.parent_chain?.find(
                    (parent: any) => parent.license_type === "SIGNAGE_FIRM"
                );

                if (signageFirm) {
                    setParentSignageId(signageFirm.id || signageFirm._id);
                    setParentSignageName(signageFirm.name);
                }
            }
        } catch (error) {
            safeConsole.error("Error fetching parent hierarchy:", error);
            // Send error email notification
            await handleApiError(error, {
                module: "removal",
                action: "Signage_Removal_Work_Order_fetch_parent_hierarchy",
                userId: userId || undefined,
                licenseType: licenseType || undefined,
                userName: userName || undefined,
                url: `${process.env.REACT_APP_API_URL}/parent-hierarchy`,
                method: "GET"
            }, token);
        }
    };

    // ✅ Fetch removal cost when order loads (using removal address from removal_details)
    useEffect(() => {
        const fetchRemovalCost = async () => {
            const addr = removalOrder?.removal_details?.removal_address?.address_details;

            if (
                !addr?.latitude ||
                !addr?.longitude ||
                addr?.latitude === 0 ||
                addr?.longitude === 0
            ) {
                // console.log("⚠️ Removal address is missing latitude/longitude - skipping dynamic cost fetch");
                return;
            }
            if (isSignageTeammember && !parentSignageId) {
                return;
            }

            try {
                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/removal_orders/dynamic_cost/${(isSignageTeammember && parentSignageId ? parentSignageId : userId) || ""}`,
                    {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`,
                        },
                        body: JSON.stringify({
                            removal_address: {
                                addressLine1: addr?.addressLine1,
                                addressLine2: addr?.addressLine2,
                                city: addr?.city,
                                state: addr?.state,
                                country: addr?.country,
                                zip: addr?.zip,
                                cityCode: addr?.cityCode,
                                stateCode: addr?.stateCode,
                                countryCode: addr?.countryCode,
                                latitude: addr?.latitude,
                                longitude: addr?.longitude,
                            }
                        }),
                    }
                );

                const data = await response.json();
                // console.log("✅ Removal cost fetched:", data);
                if (!response.ok) throw new Error(data?.error || "Failed to fetch removal cost");
                setDynamicCostId(data.id);
                setDynamicTaxable(data.is_taxable || data.taxable);
                // setRemovalCost(data.cost || 0);
            } catch (error: any) {
                setRemovalCost(0);
                const message = error instanceof Error ? error.message : "An unknown error occurred";
                safeConsole.error("❌ Removal cost fetch error:", message);
                // Send error email notification
                const removalCostPayload = {
                    removal_address: {
                        addressLine1: addr?.addressLine1,
                        addressLine2: addr?.addressLine2,
                        city: addr?.city,
                        state: addr?.state,
                        country: addr?.country,
                        zip: addr?.zip,
                        cityCode: addr?.cityCode,
                        stateCode: addr?.stateCode,
                        countryCode: addr?.countryCode,
                        latitude: addr?.latitude,
                        longitude: addr?.longitude,
                    }
                };
                await handleApiError(error, {
                    module: "removal",
                    action: "Signage_Removal_Work_Order_fetch_removal_cost",
                    userId: userId || undefined,
                    licenseType: licenseType || undefined,
                    userName: userName || undefined,
                    url: `${process.env.REACT_APP_API_URL}/removal_orders/dynamic_cost/${(isSignageTeammember && parentSignageId ? parentSignageId : userId) || ""}`,
                    method: "POST",
                    requestBody: removalCostPayload,
                    additionalInfo: {
                        isSignageTeammember,
                        parentSignageId: parentSignageId || null
                    }
                }, token);
            }
        };

        if (removalOrder?.removal_details?.removal_address?.address_details) {
            fetchRemovalCost();
        }
    }, [removalOrder?.removal_details?.removal_address?.address_details, userId, isSignageTeammember, parentSignageId, token]);

    // Extract fetchRemovalOrder as a separate function so it can be called from other functions
    const fetchRemovalOrder = useCallback(async () => {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/removal_orders/${id}`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            const data = await response.json();

            if (!response.ok) {
                showError(data.error || "Failed to fetch removal order");
                return;
            }

            const normalizedLineItems = (data.line_items || []).map(normalizeRemovalLineItemFromApi);

            setRemovalOrder({ ...data, line_items: normalizedLineItems });
            setUpdatedProducts(normalizedLineItems);

            // ✅ Store initial form values for change detection
            initialFormValuesRef.current = {
                scheduledDate: data?.removal_details?.scheduled_date,
                installerId: data?.removal_details?.installer_id,
                removalAddress: JSON.stringify(data?.removal_details?.removal_address),
                notes: data?.notes,
                lineItems: JSON.stringify(normalizedLineItems),
                removalCost: data?.removal_cost,
                specialFees: JSON.stringify(data?.order_charges?.[0]?.specialfee || []),
            };
            // console.log("Fetched removal order:", data);

            // ✅ Initialize removal cost and tax data from backend
            // if (data?.removal_cost) {
            //     setRemovalCost(Number(data.removal_cost));
            // }
            // if (data?.dynamic_cost_id) {
            //     setDynamicCostId(data.dynamic_cost_id);
            // }
            // if (data?.removal_taxable !== undefined) {
            //     setDynamicTaxable(data.removal_taxable);
            // } else if (data?.installation_taxable !== undefined) {
            //     setDynamicTaxable(data.installation_taxable);
            // }

            // Initialize installer from removal order data
            if (data?.removal_details?.installer_id && data?.removal_details?.installer_name) {
                const installerName = data.removal_details.installer_name;
                const installerOption = {
                    value: data.removal_details.installer_id,
                    label: installerName,
                    firstName: installerName.split(' ')[0] || "",
                    lastName: installerName.split(' ').slice(1).join(' ') || "",
                    email: "",
                    phone: data.removal_details.contact_number || ""
                };
                setSelectedSignageTeamMemberOption(installerOption);
                setSelectedInstaller(installerOption); // For compatibility with existing code
            }
            const initialQuantities: Record<string, number> = {};
            data.line_items?.forEach((item: any) => {
                if (item.inventory_source !== "rotational") {
                    initialQuantities[item.product_id] = item.quantity_to_be_removed; // default quantity
                }
            });
            setRemovalQuantities(initialQuantities);
        } catch (err) {
            showError("Something went wrong while fetching removal order.");
            safeConsole.error(err);
            // Send error email notification
            await handleApiError(err, {
                module: "removal",
                action: "Signage_Removal_Work_Order_fetch_removal_order",
                userId: userId || undefined,
                licenseType: licenseType || undefined,
                userName: userName || undefined,
                url: `${process.env.REACT_APP_API_URL}/removal_orders/${id}`,
                method: "GET",
                additionalInfo: {
                    removalOrderId: id || null
                }
            }, token);
        }
    }, [id, token]);

    useEffect(() => {
        if (id) {
            fetchRemovalOrder();
        }
    }, [id, fetchRemovalOrder]);

    const runPostPaymentOrderRefresh = React.useCallback(async () => {
        await fetchRemovalOrder();
    }, [fetchRemovalOrder]);

    // Fetch parent hierarchy for signageteammember login
    useEffect(() => {
        if (isSignageTeammember && token) {
            fetchParentHierarchy();
        }
    }, [isSignageTeammember, token]);

    // Initialize updatedRotationalUnits with bin location from serial_allocations
    useEffect(() => {
        if (!updatedProducts || updatedProducts.length === 0) return;

        setUpdatedRotationalUnits((prevUnits) => {
            const initializedUnits: Record<string, any> = { ...prevUnits };
            let hasChanges = false;

            updatedProducts
                .filter((item: any) => item.inventory_source === "rotational" && Array.isArray(item.serial_allocations))
                .forEach((item: any) => {
                    item.serial_allocations?.forEach((serial: any) => {
                        if (serial.serial_id && !prevUnits[serial.serial_id]) {
                            // Create bin location display string from serial allocation data
                            const binLocationDisplay = [
                                serial.warehouse_name,
                                serial.zone_name,
                                serial.rack_name,
                                serial.bin_name
                            ].filter(Boolean).join(' > ');

                            initializedUnits[serial.serial_id] = {
                                current_status: item.current_status || "",
                                restock_quantity: 0,
                                writeoff_quantity: 0,
                                damaged_quantity: 0,
                                reason_to_writeoff: "",
                                repair: 0,
                                bin_location: serial.bin_id || "",
                                bin_location_display: binLocationDisplay,
                                note: "",
                            };
                            hasChanges = true;
                        }
                    });
                });

            // Only return new state if there are changes
            return hasChanges ? initializedUnits : prevUnits;
        });
    }, [updatedProducts]);

    const fetchAddresses = async (id: any) => {
        // console.log("fetchAddresses IN")
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/user-addresses/${id}`,
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );
            const data = await response.json();
            // console.log("fetchAddresses :", data);
            // setAddresses(data.addresses);
            setAddressObjects(data);
        } catch (err) {
            setError(
                err instanceof Error ? err.message : "An unknown error occurred"
            );
        } finally {
            setLoading(false);
        }
    };

    // Fetch addresses, set selected user, and populate states based on the order's user type when order is loaded
    useEffect(() => {
        if (!removalOrder) return;

        // Set map location if available
        if (removalOrder?.removal_details?.removal_address?.address_details?.latitude &&
            removalOrder?.removal_details?.removal_address?.address_details?.longitude) {
            setSelectedLocation({
                lat: removalOrder.removal_details.removal_address.address_details.latitude,
                lng: removalOrder.removal_details.removal_address.address_details.longitude,
            });
        }

        // Set billing address based on inventory source
        if (removalOrder?.billing_address) {
            if (removalOrder.inventory_source === "office") {
                setSelectedOfficeBillingAddress(removalOrder.billing_address);
            } else {
                setSelectedBillingAddress(removalOrder.billing_address);
            }
        }

        // Determine which user to set and fetch addresses for
        // Priority: office agent > agent > team member
        let userIdForAddresses = null;

        // Check if this is an office agent order (has both agent_id and office)
        if (removalOrder.agent_id && removalOrder.office?._id) {
            userIdForAddresses = removalOrder.agent_id;

            // Set brokerage firm if available
            if (removalOrder.brokerage_firm_id && removalOrder.brokerage_firm_name) {
                const brokerageFirmOption = {
                    value: removalOrder.brokerage_firm_id,
                    label: removalOrder.brokerage_firm_name,
                    firstName: removalOrder.brokerage_firm_admin?.split(' ')[0] || "",
                    lastName: removalOrder.brokerage_firm_admin?.split(' ').slice(1).join(' ') || "",
                    email: "",
                    phone: "",
                };
                setSelectedBrokerageFirm(brokerageFirmOption);
            }

            // Set team member if available
            if (removalOrder.team_member_id && removalOrder.team_member_name) {
                const teamMemberOption = {
                    value: removalOrder.team_member_id,
                    label: removalOrder.team_member_name,
                    firstName: removalOrder.team_member_name.split(' ')[0] || "",
                    lastName: removalOrder.team_member_name.split(' ').slice(1).join(' ') || "",
                    email: removalOrder.user_email || "",
                    phone: removalOrder.user_phone || "",
                };
                setSelectedTeamMember(teamMemberOption);
            }

            // Set office agent option
            const agentOption = {
                value: removalOrder.agent_id,
                label: removalOrder.agent_name || "",
                firstName: removalOrder.agent_name?.split(' ')[0] || "",
                lastName: removalOrder.agent_name?.split(' ').slice(1).join(' ') || "",
                email: removalOrder.user_email || "",
                phone: removalOrder.user_phone || "",
            };
            setSelectedOfficeAgent(agentOption);

            // Also set the office
            const officeOption = {
                value: removalOrder.office._id,
                label: removalOrder.office.officeName || "",
                firstName: removalOrder.office.officeName || "",
                lastName: "",
                email: "",
                phone: "",
            };
            setSelectedOffice(officeOption);
        }
        // Check if this is a standalone agent order (has agent_id but no office)
        else if (removalOrder.agent_id && !removalOrder.office?._id) {
            userIdForAddresses = removalOrder.agent_id;

            // Set agent option
            const agentOption = {
                value: removalOrder.agent_id,
                label: removalOrder.agent_name || "",
                firstName: removalOrder.agent_name?.split(' ')[0] || "",
                lastName: removalOrder.agent_name?.split(' ').slice(1).join(' ') || "",
                email: removalOrder.user_email || "",
                phone: removalOrder.user_phone || "",
            };
            setSelectedAgent(agentOption);
        }
        // Check if this is a team member/office order (has team_member_id)
        else if (removalOrder.team_member_id) {
            // If order has an office but no agent, it's an office order
            if (removalOrder.office?._id && !removalOrder.agent_id) {
                userIdForAddresses = removalOrder.office._id;
            } else {
                // Otherwise use team member ID for address fetching
                userIdForAddresses = removalOrder.team_member_id;
            }

            // Set brokerage firm if available
            if (removalOrder.brokerage_firm_id && removalOrder.brokerage_firm_name) {
                const brokerageFirmOption = {
                    value: removalOrder.brokerage_firm_id,
                    label: removalOrder.brokerage_firm_name,
                    firstName: removalOrder.brokerage_firm_admin?.split(' ')[0] || "",
                    lastName: removalOrder.brokerage_firm_admin?.split(' ').slice(1).join(' ') || "",
                    email: "",
                    phone: "",
                };
                setSelectedBrokerageFirm(brokerageFirmOption);
            }

            // Set team member option
            const teamMemberOption = {
                value: removalOrder.team_member_id,
                label: removalOrder.team_member_name || "",
                firstName: removalOrder.team_member_name?.split(' ')[0] || "",
                lastName: removalOrder.team_member_name?.split(' ').slice(1).join(' ') || "",
                email: removalOrder.user_email || "",
                phone: removalOrder.user_phone || "",
            };
            setSelectedTeamMember(teamMemberOption);

            // If order has office, set it
            if (removalOrder.office?._id) {
                const officeOption = {
                    value: removalOrder.office._id,
                    label: removalOrder.office.officeName || "",
                    firstName: removalOrder.office.officeName || "",
                    lastName: "",
                    email: "",
                    phone: "",
                };
                setSelectedOffice(officeOption);
            }
        }

        // Fetch addresses if we have a user ID and addresses aren't already loaded
        if (userIdForAddresses && addressObjects.length === 0) {
            (async () => {
                try {
                    const response = await fetch(
                        `${process.env.REACT_APP_API_URL}/user-addresses/${userIdForAddresses}`,
                        {
                            method: "GET",
                            headers: {
                                Authorization: `Bearer ${token}`,
                                "Content-Type": "application/json",
                            },
                        }
                    );
                    const data = await response.json();
                    // console.log("Loaded addresses for order user:", data);
                    setAddressObjects(data || []);
                } catch (err) {
                    safeConsole.error("Failed to fetch addresses for order user:", err);
                }
            })();
        }
    }, [removalOrder, addressObjects.length, token]);

    const fetchAllZones = async () => {
        try {
            const res = await fetch(
                `${process.env.REACT_APP_API_URL}/zones`,
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            const data = await res.json();
            if (!res.ok) {
                showError(data.message || "Failed to fetch zones");
                return [];
            }
            return data; // Return the zones array
        } catch (err) {
            safeConsole.error("Zones fetch error", err);
            showError("Error getting zones");
            return [];
        }
    };

    const fetchAllRacks = async () => {
        try {
            const res = await fetch(
                `${process.env.REACT_APP_API_URL}/racks`,
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            const data = await res.json();
            if (!res.ok) {
                showError(data.message || "Failed to fetch racks");
                return [];
            }
            return data;
        } catch (err) {
            safeConsole.error("Racks fetch error", err);
            showError("Error getting racks");
            return [];
        }
    };

    const fetchAllBins = async () => {
        try {
            const res = await fetch(
                `${process.env.REACT_APP_API_URL}/bins`,
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            const data = await res.json();
            if (!res.ok) {
                showError(data.message || "Failed to fetch bins");
                return [];
            }
            return data;
        } catch (err) {
            safeConsole.error("Bins fetch error", err);
            showError("Error getting bins");
            return [];
        }
    };

    // Warehouses/zones/racks/bins only needed for restock submit (binOptions lookup) at status 500/600
    const warehouseHierarchyFetchedRef = useRef(false);
    useEffect(() => {
        const status = removalOrder?.status;
        if ((status !== 500 && status !== 600) || !token) return;
        if (warehouseHierarchyFetchedRef.current) return;

        let cancelled = false;
        const fetchWarehouseHierarchy = async () => {
            try {
                const [warehouseRes, zonesData, racksData, binsData] = await Promise.all([
                    fetch(`${process.env.REACT_APP_API_URL}/warehouses`, {
                        method: "GET",
                        headers: {
                            Authorization: `Bearer ${token}`,
                            "Content-Type": "application/json",
                        },
                    }),
                    fetchAllZones(),
                    fetchAllRacks(),
                    fetchAllBins(),
                ]);

                if (cancelled) return;

                if (warehouseRes.ok) {
                    const warehouseData = await warehouseRes.json();
                    setWarehouses(warehouseData);
                } else {
                    const warehouseError = await warehouseRes.json().catch(() => ({}));
                    showError(warehouseError.message || "Failed to fetch warehouses");
                }

                setZones(zonesData);
                setRacks(racksData);
                setBins(binsData);
                warehouseHierarchyFetchedRef.current = true;
            } catch (err) {
                if (cancelled) return;
                setError(
                    err instanceof Error ? err.message : "An unknown error occurred"
                );
                await handleApiError(err, {
                    module: "removal",
                    action: "Signage_Removal_Work_Order_fetch_warehouse_hierarchy",
                    userId: userId || undefined,
                    licenseType: licenseType || undefined,
                    userName: userName || undefined,
                    url: `${process.env.REACT_APP_API_URL}/warehouses`,
                    method: "GET",
                }, token);
            }
        };

        fetchWarehouseHierarchy();
        return () => {
            cancelled = true;
        };
    }, [removalOrder?.status, token]);

    const combinations: Combo[] = useMemo(() => {
        if (!warehouses?.length || !zones?.length || !racks?.length || !bins?.length) return [];

        // For memory & speed, create lookup tables first:
        const warehouseMap = Object.fromEntries(warehouses?.map(w => [w._id, w]));
        const zoneMap = Object.fromEntries(zones?.map(z => [z._id, z]));
        const rackMap = Object.fromEntries(racks?.map(r => [r._id, r]));

        // Only output bins that have matching rack, zone, warehouse (valid hierarchy)
        return bins
            ?.map(bin => {
                const rack = rackMap[bin.rack_id];
                const zone = zoneMap[bin.zone_id];
                const warehouse = warehouseMap[bin.warehouse_id];
                if (!rack || !zone || !warehouse) return null; // skip if missing hierarchy

                return {
                    warehouse_id: warehouse._id,
                    warehouse_name: warehouse.label,
                    zone_id: zone._id,
                    zone_name: zone.label,
                    rack_id: rack._id,
                    rack_name: rack.label,
                    bin_id: bin._id,
                    bin_name: bin.label,
                };
            })
            .filter(Boolean) as Combo[];
    }, [warehouses, zones, racks, bins]);

    // // 2. Filter for currently selected warehouseId
    // const filteredCombinations = useMemo(() => {
    //     if (!warehouses) return [];
    //     return combinations.filter(c => c.warehouse_id === formik.values.warehouseId);
    // }, [combinations, warehouses]);

    // useEffect(() => {
    //     console.log("combinations :", combinations)
    // }, [combinations])

    const binOptions = combinations?.map((c: Combo) => ({
        value: c.bin_id,
        label: `${c.warehouse_name} > ${c.zone_name} > ${c.rack_name} > ${c.bin_name}`,
        bin: c
    }));

    // console.log("binOptions :", binOptions)

    useEffect(() => {
        const fetchPaymentMethods = async () => {
            const id = removalOrder?.inventory_source === "office" ? removalOrder?.team_member_id : removalOrder?.agent_id
            const supplierId = isSignageTeammember && parentSignageId ? parentSignageId : userId;
            try {
                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/payment-methods/by-supplier/${supplierId}/by-user/${id}`,
                    {
                        method: "GET",
                        headers: {
                            Authorization: `Bearer ${token}`,
                            "Content-Type": "application/json",
                        },
                    }
                );
                const data = await response.json();
                // console.log("PaymentMethod :", data);
                setPaymentMethod(Array.isArray(data) ? data : []);
            } catch (err) {
                safeConsole.error("Error fetching payment methods:", err);
            } finally {
                setLoading(false);
            }
        };

        if (userId && removalOrder?.inventory_source) {
            fetchPaymentMethods();
        }
    }, [token, userId, removalOrder?.inventory_source, isSignageTeammember, parentSignageId]);

    // ✅ Helper Functions for Tax Calculation
    type Addresstax = {
        addressLine1: string;
        city: string;
        state: string;
        zip: string;
    };

    const coalesceDestination = (): Addresstax => {
        const a = removalOrder?.removal_details?.removal_address?.address_details as Addresstax | undefined;
        return {
            addressLine1: a?.addressLine1 || "",
            city: a?.city || "",
            state: a?.state || "",
            zip: a?.zip || "",
        };
    };

    const getSelectedFees = () => {
        return (selectedSpecialFees || [])
            .map((f) => normalizeInstallationSelectedFee(f, allAvailableSpecialFees))
            .filter((f) => f.id !== "");
    };

    // ✅ Map backend special fees to UI format (similar to installation work order)
    const preselectedSpecialFees = React.useMemo(() => {
        // Priority 1: Root-level specialfee (newer format)
        let list = Array.isArray(removalOrder?.specialfee)
            ? removalOrder.specialfee
            : [];

        return list.map((f: any) => mapSavedInstallationSpecialFeeToUi(f));
    }, [removalOrder]);

    // ✅ Initial totals for Cost Breakdown from backend payload
    const initialTotals = React.useMemo(() => {
        const list = Array.isArray(removalOrder?.specialfee) ? removalOrder.specialfee : [];
        const feeSub = list.reduce((s: number, f: any) => s + Number(f?.specialfee_amount ?? f?.amount ?? 0), 0);
        const feeTax = list.reduce((s: number, f: any) => s + Number(f?.specialfee_tax_amount ?? f?.tax_amount ?? 0), 0);
        const removalSub = Number(removalOrder?.removal_cost || 0);
        const removalTax = Number(removalOrder?.removal_tax_amount || 0);
        const grand = removalSub + feeSub + removalTax + feeTax;
        return {
            removal_subtotal: removalSub,
            removal_tax_amount: removalTax,
            specialfee_subtotal: feeSub,
            specialfee_tax_amount: feeTax,
            over_all_tax_amount: removalTax + feeTax,
            grand_total: grand,
            subtotal_amount: Number(removalOrder?.subtotal_amount || 0),
            cartId: String(removalOrder?.cart_id || ""),
        };
    }, [removalOrder]);

    // ✅ Seed special fees and totals from backend when order loads
    useEffect(() => {
        if (!removalOrder) return;

        // ✅ In view mode, always update from backend to reflect latest tax data
        // In edit mode, only initialize once to prevent overwriting user changes
        const shouldUpdate = !isEdit || !seededSpecialFeesRef.current;

        if (shouldUpdate) {
            // console.log("🔄 Initializing special fees and totals from backend:", {
            //     fees: preselectedSpecialFees,
            //     totals: initialTotals
            // });

            // ✅ Update special fees
            if (preselectedSpecialFees.length > 0) {
                formik.setFieldValue("specialfee", preselectedSpecialFees, false);
                setSelectedSpecialFees(preselectedSpecialFees);
            }

            // ✅ Always update totals from backend (includes tax data)
            formik.setFieldValue("totals", initialTotals, false);

            // ✅ Mark as seeded only in edit mode to allow view mode updates
            if (isEdit) {
                seededSpecialFeesRef.current = true;
            }
        }
    }, [removalOrder, preselectedSpecialFees, initialTotals, isEdit]);

    const buildProductsMerged = (fees?: any[]): TaxProduct[] => {
        const items: TaxProduct[] = [];

        // ✅ Calculate total quantity from line items
        const totalQty = updatedProducts?.reduce((sum, item) => {
            if (item.inventory_source === "rotational") {
                return sum + (item.serial_allocations?.length || 0);
            } else {
                return sum + (removalQuantities[item.product_id] || item.quantity_to_be_removed || 0);
            }
        }, 0) || 1;

        // ✅ Only include removal cost if it's taxable
        if (dynamicTaxable) {
            const removalPrice = Number(removalCost ?? 0);
            const discount = Number(formik.values.discount_amount || 0);
            const discountedRemovalPrice = Math.max(0, removalPrice - discount);
            if (discountedRemovalPrice > 0) {
                items.push({ sku: "RemovalCost", price: discountedRemovalPrice, quantity: 1, itemType: "service" });
            }
        }

        // ✅ Only include explicitly taxable special fees
        const feesToUse = fees ?? selectedSpecialFees;
        for (const fee of feesToUse) {
            const feeAmount = Number(fee?.amount || 0);
            if (feeAmount > 0 && fee?.taxable === true) {
                items.push({ sku: fee?.name || "Special Fee", price: feeAmount, quantity: 1, itemType: "special_fee" });
            }
        }

        return items;
    };

    const currentOwner =
        removalOrder?.agent_id
            ? { id: removalOrder.agent_id, name: removalOrder.agent_name, type: "agent" as const }
            : removalOrder?.office?._id
                ? { id: removalOrder.office._id, name: removalOrder.office.officeName, type: "office" as const }
                : null;

    // ✅ Separate logic for CouponDiscount customerId (different from currentOwner)
    const effectiveCouponCreatedById = React.useMemo(() => {
        let id = "";
        let source = "";

        // Priority: Agent > Team Member (for office path) > Fallback to userId
        if (removalOrder?.agent_id) {
            // Agent path: use agent ID
            id = removalOrder.agent_id;
            source = "Agent";
        } else if (removalOrder?.office?._id && removalOrder?.team_member_id) {
            // Office path with team member: use team member ID (NOT office ID)
            id = removalOrder.team_member_id;
            source = "Team Member (via Office)";
        } else {
            id = "";
            source = "User (Fallback)";
        }

        // console.log(`🎟️ [RemovalWorkOrder] Coupon customerId: ${id} (from ${source})`);
        return id;
    }, [removalOrder?.agent_id, removalOrder?.office?._id, removalOrder?.team_member_id, userId]);

    // ✅ Fetch active tax type on mount
    useEffect(() => {
        (async () => {
            try {
                if (!token || !userId) return;
                const supplierId = isSignageTeammember && parentSignageId ? parentSignageId : userId;
                const t = await getActiveTaxType(token, supplierId);
                setTaxType(t === "taxcloud" ? "taxcloud" : t === "manual" ? "manual" : "unknown");
                // console.log("[TAX] Active tax type for removal:", t);
            } catch (e) {
                safeConsole.warn("[TAX] getActiveTaxType failed:", e);
                setTaxType("unknown");
            }
        })();
    }, [token, userId, isSignageTeammember, parentSignageId]);

    const isRecomputingRef = useRef(false);

    const toScheduledDateInput = (raw?: string) => {
        const date = String(raw || "").split("T")[0];
        if (!date || date.startsWith("0001-01-01")) return "";
        return date;
    };

    const toScheduleIso = (raw?: string, fallback?: string) => {
        // TODO: restore scheduled-date validation later (block empty / 0001-01-01).
        // if (!raw || String(raw).startsWith("0001-01-01")) return null;
        const source = String(raw || fallback || "").trim();
        const parsed = source ? new Date(source) : null;
        if (parsed && Number.isFinite(parsed.getTime())) return parsed.toISOString();
        return fallback || "0001-01-01T00:00:00.000Z";
    };

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            installation_order_number: removalOrder?.installation_order_number || "",
            brokerage_firm_admin: removalOrder?.brokerage_firm_admin || "",
            officeName: removalOrder?.office?.officeName || "",
            agent_name: removalOrder?.agent_name || "",
            order_number: removalOrder?.order_number || "",
            userType: removalOrder?.user_type,
            teamMemberId: removalOrder?.team_member_id,
            userFirstName: "",
            userLastName: "",
            userEmail: removalOrder?.user_email,
            userPhone: removalOrder?.user_phone,
            priority: removalOrder?.priority || 0,
            scheduled_date: toScheduledDateInput(removalOrder?.removal_details?.scheduled_date),
            scheduled_jobs: 0,
            restock_warehouse: "",
            installeId: removalOrder?.removal_details?.installer_id,
            billing_address: removalOrder?.billing_address,
            // removal_address: removalOrder?.installation_details.removal_address,
            isSameAsBilling: !!removalOrder?.both_address_same,
            agentId: removalOrder?.agent_id,
            officeId: removalOrder?.office?._id,
            billingAddressId: removalOrder?.billing_address?._id,
            removalAddressId: removalOrder?.installation_details?.removal_address?._id ?? removalOrder?.removal_details?.removal_address?._id,
            location: "",
            products: "",
            instructions: "",
            payment_method: "",
            localLawsChecked: false,
            image: null,
            installation_charge: removalOrder?.removal_cost,
            discount: removalOrder?.discount_amount || 0,
            subtotal: removalOrder?.subtotal_amount || removalOrder?.total_amount || 0,
            total: removalOrder?.total_amount,
            tax: 0,
            enable_partial_payment: false,
            partial_amount: 0,
            transactionId: "",
            paymentMethod: "",
            gatecode: removalOrder?.gate_access_code,
            gateCodeEnabled: false,
            // ✅ Discount and Special Fee fields
            discount_amount: removalOrder?.discount_amount || 0,
            discount_code: removalOrder?.discount_code || "",
            specialfee: removalOrder?.specialfee?.map((fee: any) => ({
                id: fee.specialfee_id,
                name: fee.specialfee_name,
                amount: fee.specialfee_amount,
                taxable: fee.specialfee_taxable,
                tax_percentage: fee.specialfee_tax_percentage || 0,
                tax_amount: fee.specialfee_tax_amount || 0,
            })) || [],
            totals: {
                removal_subtotal: removalOrder?.removal_cost || 0,
                removal_tax_percentage: removalOrder?.removal_tax_percentage || 0,
                removal_tax_amount: removalOrder?.removal_tax_amount || 0,
                specialfee_subtotal: removalOrder?.specialfee?.reduce((sum: number, fee: any) => sum + Number(fee.specialfee_amount || 0), 0) || 0,
                specialfee_tax_amount: removalOrder?.specialfee?.reduce((sum: number, fee: any) => sum + Number(fee.specialfee_tax_amount || 0), 0) || 0,
                over_all_tax_percentage: removalOrder?.over_all_tax_percentage || 0,
                over_all_tax_amount: (() => {
                    // ✅ Calculate overall tax if not provided by backend
                    if (removalOrder?.over_all_tax_amount) {
                        return Number(removalOrder.over_all_tax_amount);
                    }
                    const removalTax = Number(removalOrder?.removal_tax_amount || 0);
                    const feeTax = removalOrder?.specialfee?.reduce((sum: number, fee: any) => sum + Number(fee.specialfee_tax_amount || 0), 0) || 0;
                    return removalTax + feeTax;
                })(),
                grand_total: removalOrder?.total_amount || 0,
                subtotal_amount: removalOrder?.subtotal_amount || 0,
            },
            // ✅ Tax percentage strings (for backend)
            calc_removal_rate_str: removalOrder?.removal_tax_percentage || "",
            calc_overall_rate_str: removalOrder?.over_all_tax_percentage || "",
            calc_fee_rate_str: "",
        },
        validationSchema: Yup.object({
            userEmail: Yup.string()
                .email("Enter a valid email")
                .required("Email is required"),

            userPhone: Yup.string()
                .required("Phone number is required"),

            priority: Yup.string()
                .required("Removal priority is required"),

            // ✅ Removal Date (optional)
            scheduled_date: Yup.string().notRequired(),

            // ✅ Assigned To (Installer Required)
            installeId: Yup.string()
                .required("Installer is required"),

            // ✅ Billing Address (Required)
            billingAddressId: Yup.string()
                .required("Billing address is required"),
            // installeId: Yup.string().required("Installer is required"),
            // installation_date: Yup.string().required("Installation date is required"),
            // billing_address: Yup.string().required("Billing address is required"),
            // removal_address: Yup.string().required(
            //     "Removal address is required"
            // ),
            // image: Yup.mixed().required("Image is required"),
            // removal_date: Yup.string().required("Scheduled date is required"),
            // priority: Yup.number().required("Priority is required"),
        }),
        onSubmit: async (values) => {
            // console.log("values In")
            setFormSubmitting(true);

            // ✅ Validate required special fees
            if (isEdit) {
                const requiredFees = allAvailableSpecialFees.filter((fee: any) => fee.required === true);
                const selectedFeeIds = selectedSpecialFees.map((fee: any) => fee.id);
                const missingRequiredFees = requiredFees.filter((rf: any) => !selectedFeeIds.includes(rf.id));

                if (missingRequiredFees.length > 0) {
                    const missingNames = missingRequiredFees.map((f: any) => f.name).join(", ");
                    showError(`Please select the following required special fees: ${missingNames}`);
                    setFormSubmitting(false);
                    return;
                }
            }
            let refundSuccess = false;
            const isTaxComplete = removalOrder?.tax_status === "complete";
            const isTaxDraft = removalOrder?.tax_status === "draft";
            // console.log("removalOrder.status :", removalOrder.status,!(removalOrder.status >= 500))
            if (!(removalOrder.status >= 500)) {
                if (isTaxComplete) {
                    try {
                        refundSuccess = await (installationTaxRef.current?.recalculate?.("taxcloud", { allowRefund: true }) ?? false);
                        // console.log("[INSTALL:ORDER_SUBMIT] tax recalc result:", { refundSuccess });
                    } catch (err) {
                        console.error("[INSTALL:ORDER_SUBMIT] tax recalc failed", err);
                    }

                    // if (removalOrder?.tax_status === "complete" && refundSuccess) {
                    //     if (!token) {
                    //         showError("Missing session token while attempting TaxCloud refund.");
                    //         return;
                    //     }

                    //     const order_type = removalOrder?.order_type || "removal";
                    //     const res = await PostVersionOrdernumber(token, removalOrder?.order_number || "", order_type);
                    //     console.log("[INSTALL:ORDER_SUBMIT] TaxCloud refund result:", { res });
                    //     if (!res) throw new Error("TaxCloud refund failed");
                    //     nextTaxStatus = "";
                    // }
                }
            }


            setDefaultBinLocations();
            try {
                // Ensure default bin locations are set before form submission

                // Small delay to ensure state updates are processed
                // await new Promise(resolve => setTimeout(resolve, 50));

                // Validate form before proceeding
                // const validationErrors = validateForm();
                // if (validationErrors.length > 0) {
                //     setFormSubmitting(false);
                //     focusFirstError(validationErrors);
                //     return;
                // }
                // Example file upload handling
                const imageUrl = values.image ? await uploadFile(values.image) : null;
                // const cleanedLineItems = [
                //     // Normal (non-rotational) items
                //     ...updatedProducts
                //         .filter(item => item.inventory_source !== "rotational")
                //         ?.map(item => ({
                //             product_id: item.product_id,
                //             warehouse_id: item.warehouse_id,
                //             zone_id: item.zone_id,
                //             rack_id: item.rack_id,
                //             bin_id: productNotes[item.product_id]?.bin_location || item.bin_id,
                //             serial_ids: [],
                //             serial_codes: [],
                //             product_name: item.product_name,
                //             inventory_source: item.inventory_source,
                //             rotational_allocated: item.rotational_allocated,
                //             source_product_id: item.source_product_id,
                //             sku: item.sku,
                //             available_quantity: item.available_quantity,
                //             quantity_to_be_removed: item.quantity_to_be_removed,
                //             restock_quantity: item.restock_quantity,
                //             writeoff_quantity: item.writeoff_quantity,
                //             reason_to_writeoff: item.reason_to_writeoff,
                //             current_status: item.current_status,
                //             unit_price: item.unit_price,
                //             total_price: item.total_price,
                //             is_rotational: item.is_rotational,
                //             notes: productNotes[item.product_id]?.note || "",
                //         })),

                //     // Rotational (serial-tracked) items
                //     ...updatedProducts
                //         .filter(item => item.inventory_source === "rotational" && Array.isArray(item.serial_ids))
                //         .flatMap(item =>
                //             item.serial_ids?.map((serial: string, i: number) => {
                //                 const serialData = updatedRotationalUnits[serial] || {};
                //                 return {
                //                     product_id: item.product_id,
                //                     warehouse_id: item.warehouse_id,
                //                     zone_id: item.zone_id,
                //                     rack_id: item.rack_id,
                //                     bin_id: serialData.bin_location || item.bin_id,
                //                     serial_ids: [serial],
                //                     serial_codes: [item.serial_codes?.[i] || ""],
                //                     product_name: item.product_name,
                //                     inventory_source: item.inventory_source,
                //                     rotational_allocated: item.rotational_allocated,
                //                     source_product_id: item.source_product_id,
                //                     sku: item.sku,
                //                     available_quantity: item.available_quantity,
                //                     quantity_to_be_removed: 1,
                //                     restock_quantity: serialData.restock_quantity || 0,
                //                     writeoff_quantity: serialData.writeoff_quantity || 0,
                //                     reason_to_writeoff: serialData.reason_to_writeoff || "",
                //                     current_status: serialData.current_status || "",
                //                     unit_price: item.unit_price,
                //                     total_price: item.total_price,
                //                     is_rotational: item.is_rotational,
                //                     notes: serialData.note || "",
                //                 };
                //             })
                //         )
                // ];

                const now = new Date().toISOString();
                // console.log("UpdatedProducts : ", updatedProducts)
                const normalProductLineItems = updatedProducts?.filter(item => item.inventory_source !== "rotational")
                    ?.flatMap(item => {
                        const notes = productNotes[item.product_id]?.note || "";
                        const reasonToWriteoff = item.reason_to_writeoff || "";
                        const binLocation = binOptions.find(opt => opt.value === productNotes[item.product_id]?.bin_location);
                        // console.log("Items :", item)
                        // console.log("binLocation :",binLocation)

                        // Fallback: If binLocation is undefined, try to get it from storage location (only when location[0] exists; custom products may have no location)
                        const firstLocation = item.storage_locations?.[0]?.location?.[0];
                        const fallbackBinLocation = !binLocation && firstLocation ? {
                            bin: {
                                warehouse_id: firstLocation.warehouse_id,
                                warehouse_name: firstLocation.warehouse_name,
                                zone_id: firstLocation.zone_id,
                                zone_name: firstLocation.zone_name,
                                rack_id: firstLocation.rack_id,
                                rack_name: firstLocation.rack_name,
                                bin_id: firstLocation.bin_id,
                                bin_name: firstLocation.bin_name
                            }
                        } : null;

                        // Use binLocation if available, otherwise use fallback
                        const finalBinLocation = binLocation || fallbackBinLocation;
                        // console.log("finalBinLocation :", finalBinLocation)

                        const restock = item.restock_quantity > 0 ? [{
                            product_id: item.product_id,
                            variant_id: item.variant_id,
                            product_name: item.product_name,
                            sku: item.sku,
                            inventory_source: item.inventory_source,
                            source_product_id: item.source_product_id,
                            is_rotational: item.is_rotational,
                            restocked_quantity: item.restock_quantity,
                            restock_locations: [{
                                quantity: item.restock_quantity,
                                current_status: "with_supplier",
                                product_location_status: item.storage_locations?.[0]?.product_location_status ?? "",
                                location: [{
                                    quantity: item.restock_quantity,
                                    warehouse_id: finalBinLocation?.bin.warehouse_id,
                                    warehouse_name: finalBinLocation?.bin.warehouse_name,
                                    zone_id: finalBinLocation?.bin.zone_id,
                                    zone_name: finalBinLocation?.bin.zone_name,
                                    rack_id: finalBinLocation?.bin.rack_id,
                                    rack_name: finalBinLocation?.bin.rack_name,
                                    bin_id: finalBinLocation?.bin.bin_id,
                                    bin_name: finalBinLocation?.bin.bin_name
                                }]
                            }],
                            submitted_notes: reasonToWriteoff || notes,
                            submitted_at: now
                        }] : [];

                        const damaged = (item.damaged_quantity > 0) ? [{
                            product_id: item.product_id,
                            variant_id: item.variant_id,
                            product_name: item.product_name,
                            sku: item.sku,
                            inventory_source: item.inventory_source,
                            source_product_id: item.source_product_id,
                            current_status: item.current_status,

                            is_rotational: item.is_rotational,
                            lost_quantity: 0,     // Lost
                            damaged_quantity: item.damaged_quantity || 0,       // Damaged
                            condition_status: item.reason_to_writeoff || "Damaged",
                            // writeoff_reason: notes,
                            writeoff_location: {
                                warehouse_id: finalBinLocation?.bin.warehouse_id,
                                warehouse: finalBinLocation?.bin.warehouse_name,
                                zone_id: finalBinLocation?.bin.zone_id,
                                zone: finalBinLocation?.bin.zone_name,
                                rack_id: finalBinLocation?.bin.rack_id,
                                rack: finalBinLocation?.bin.rack_name,
                                bin_id: finalBinLocation?.bin.bin_id,
                                bin: finalBinLocation?.bin.bin_name
                            },
                            submitted_notes: reasonToWriteoff || notes,
                            submitted_at: now
                        }] : [];
                        const lost = (item.writeoff_quantity > 0) ? [{
                            product_id: item.product_id,
                            variant_id: item.variant_id,
                            product_name: item.product_name,
                            sku: item.sku,
                            inventory_source: item.inventory_source,
                            source_product_id: item.source_product_id,
                            current_status: item.current_status,
                            is_rotational: item.is_rotational,
                            lost_quantity: item.writeoff_quantity || 0,     // Lost
                            damaged_quantity: 0,       // Damaged
                            condition_status: item.reason_to_writeoff || "Lost",
                            writeoff_reason: reasonToWriteoff || notes,
                            writeoff_location: {
                                warehouse_id: finalBinLocation?.bin.warehouse_id,
                                warehouse: finalBinLocation?.bin.warehouse_name,
                                zone_id: finalBinLocation?.bin.zone_id,
                                zone: finalBinLocation?.bin.zone_name,
                                rack_id: finalBinLocation?.bin.rack_id,
                                rack: finalBinLocation?.bin.rack_name,
                                bin_id: finalBinLocation?.bin.bin_id,
                                bin: finalBinLocation?.bin.bin_name
                            },
                            submitted_notes: reasonToWriteoff || notes,
                            submitted_at: now
                        }] : [];

                        return [...restock, ...damaged, ...lost];
                    });

                const rotationalLineItems = updatedProducts
                    ?.filter(item => item.inventory_source === "rotational" && Array.isArray(item.serial_allocations))
                    ?.flatMap(item =>
                        item.serial_allocations?.map((serial: any) => {
                            const unit = updatedRotationalUnits[serial.serial_id] || {};
                            // binOption for this serial, if you need bin metadata for backend
                            let restocked_quantity = 0, damaged_quantity = 0, writeoff_quantity = 0;
                            // console.log("serial :", serial)
                            // console.log("unit :", unit)
                            // Only one of these should be 1, others must be 0
                            if (unit.restock_quantity === 1) {
                                restocked_quantity = 1;
                            } else if (unit.damaged_quantity === 1) {
                                damaged_quantity = 1;
                            } else if (unit.writeoff_quantity === 1) {
                                writeoff_quantity = 1;
                            }

                            // If all are zero, skip this serial
                            if (restocked_quantity === 0 && damaged_quantity === 0 && writeoff_quantity === 0 && unit.repair !== 1) return null;

                            // ✅ Check if user updated bin location
                            let binData = {
                                warehouse_id: serial.warehouse_id,
                                warehouse_name: serial.warehouse_name,
                                zone_id: serial.zone_id,
                                zone_name: serial.zone_name,
                                rack_id: serial.rack_id,
                                rack_name: serial.rack_name,
                                bin_id: serial.bin_id,
                                bin_name: serial.bin_name,
                            };

                            // If user selected a new bin location, use that instead
                            if (unit.bin_location) {
                                const selectedBin = binOptions.find(opt => opt.value === unit.bin_location);
                                if (selectedBin?.bin) {
                                    binData = {
                                        warehouse_id: selectedBin.bin.warehouse_id,
                                        warehouse_name: selectedBin.bin.warehouse_name,
                                        zone_id: selectedBin.bin.zone_id,
                                        zone_name: selectedBin.bin.zone_name,
                                        rack_id: selectedBin.bin.rack_id,
                                        rack_name: selectedBin.bin.rack_name,
                                        bin_id: selectedBin.bin.bin_id,
                                        bin_name: selectedBin.bin.bin_name,
                                    };
                                }
                            }

                            // Writeoff case
                            // if (unit.restock_quantity > 0) {
                            const lineItem: any = {
                                product_id: item.product_id,
                                variant_id: item.variant_id,
                                product_name: item.product_name,
                                sku: item.sku,
                                inventory_source: item.inventory_source,
                                source_product_id: item.source_product_id,
                                is_rotational: true,
                                restocked_quantity: unit.restock_quantity === 0 ? unit.repair : unit.restock_quantity,
                                lost_quantity: unit.writeoff_quantity,
                                damaged_quantity: unit.damaged_quantity,
                                writeoff_reason: unit.reason_to_writeoff,
                                serial_allocations: [{
                                    serial_id: serial.serial_id,
                                    serial_code: serial.serial_code,
                                    warehouse_id: binData.warehouse_id,
                                    warehouse_name: binData.warehouse_name,
                                    zone_id: binData.zone_id,
                                    zone_name: binData.zone_name,
                                    rack_id: binData.rack_id,
                                    rack_name: binData.rack_name,
                                    bin_id: binData.bin_id,
                                    bin_name: binData.bin_name,
                                    status: serial.status,
                                }],
                                submitted_notes: unit.note,
                                submitted_at: now
                            };

                            // Add condition field only if repair value is 1
                            if (unit.repair === 1) {
                                lineItem.condition_status = "Repair";
                            }

                            return lineItem;
                            // }
                            // // Lost case
                            // if (unit.writeoff_quantity > 0) {
                            //     return {
                            //         product_id: item.product_id,
                            //         variant_id: item.variant_id,
                            //         product_name: item.product_name,
                            //         sku: item.sku,
                            //         inventory_source: item.inventory_source,
                            //         source_product_id: item.source_product_id,
                            //         is_rotational: true,
                            //         lost_quantity: unit.writeoff_quantity,
                            //         lost_reason: unit.note,
                            //         serial_allocations: [{
                            //             serial_id: serial.serial_id,
                            //             serial_code: serial.serial_code
                            //         }],
                            //         submitted_notes: notes,
                            //         submitted_at: now
                            //     };
                            // }
                            // Restock or other types could be handled here...
                            return null; // or skip
                        }).filter(Boolean)
                    );

                const line_items = [...normalProductLineItems, ...rotationalLineItems];

                // console.log("line_items :", line_items)

                if (isEdit) {

                    await waitForRecomputeIdle();
                    await recomputeRef.current?.();

                    // Build updated line_items from current UI edits
                    const sourceLineItems =
                        updatedProducts.length > 0 ? updatedProducts : removalOrder?.line_items ?? [];
                    const updatedLineItems = sourceLineItems.map((item: any) => {
                        // leave rotational untouched; custom: always send with quantity_to_be_removed 0
                        if (item.inventory_source === "rotational") {
                            return withRemovalLineItemIdForPayload(item);
                        }
                        if (item.inventory_source === "custom") {
                            return withRemovalLineItemIdForPayload({
                                ...item,
                                quantity_to_be_removed: 0,
                            });
                        }

                        const originalToRemove = Number(item.quantity_to_be_removed) || 0;
                        const baseAvailable = Number(item.available_quantity) || 0;
                        const pool = baseAvailable + originalToRemove; // max user can set

                        // only update if the user edited this product’s qty
                        const plannedRaw = removalQuantities[item.product_id];
                        if (plannedRaw == null) {
                            return withRemovalLineItemIdForPayload(item);
                        }

                        const nextQty = Math.max(0, Math.min(Number(plannedRaw) || 0, pool));
                        const nextAvail = Math.max(0, pool - nextQty);

                        return withRemovalLineItemIdForPayload({
                            ...item,
                            quantity_to_be_removed: nextQty, // ✅ updated
                            available_quantity: nextAvail,   // ✅ updated
                        });
                    });

                    const submitDiscountAmount = preferFormikDiscount
                        ? Number(formik.values?.discount_amount || 0)
                        : Number(costBreakdownDiscount.amount || 0);
                    const submitTaxTotal =
                        Number(formik.values.totals?.over_all_tax_amount || 0) > 0
                            ? Number(formik.values.totals.over_all_tax_amount)
                            : Number(formik.values.totals?.removal_tax_amount || 0) +
                              Number(formik.values.totals?.specialfee_tax_amount || 0);
                    const currentGrandTotalValue =
                        Number(formik.values.totals?.removal_subtotal || removalCost || 0) +
                        Number(formik.values.totals?.specialfee_subtotal || 0) +
                        submitTaxTotal -
                        submitDiscountAmount;

                    const roundToCentsSubmit = (n: number) => Math.round(Number(n) * 100) / 100;
                    const currentGrandTotalForSubmit = roundToCentsSubmit(currentGrandTotalValue);
                    const zeroOutTaxFields = refundSuccess && isTaxComplete;
                    const removalTax = zeroOutTaxFields
                        ? 0
                        : Number(
                              formik.values.totals?.removal_tax_amount ??
                                  lastResolvedRemovalRef.current?.removal_tax_amount ??
                                  0
                          );
                    const feeTax = zeroOutTaxFields
                        ? 0
                        : Number(formik.values.totals?.specialfee_tax_amount || 0);
                    const overallTax = zeroOutTaxFields
                        ? 0
                        : Number(
                              formik.values.totals?.over_all_tax_amount ??
                                  lastResolvedRemovalRef.current?.over_all_tax_amount ??
                                  removalTax + feeTax
                          );

                    let editPatchBody: Record<string, unknown> = {
                            line_items: updatedLineItems, // ⬅️ use the recomputed list
                            removal_details: {
                                ...(values.scheduled_date
                                    ? { scheduled_date: new Date(values.scheduled_date).toISOString() }
                                    : {}),
                                removal_address: removalOrder?.removal_details?.removal_address,
                                installer_id: selectedInstaller?.value || "",
                                installer_name: selectedInstaller?.label || "",
                                contact_number: selectedInstaller?.phone || "",
                                estimated_duration: null,
                                actual_installation_date: null,
                                removal_notes: [
                                    {
                                        note: values.instructions || "",
                                        added_by: {
                                            id: userId,
                                            name: userName,
                                        },
                                        added_at: new Date().toISOString(),
                                    },
                                ],
                                assignee_id: selectedInstaller?.value,
                                tax_details: null,
                            },
                            billing_address: removalOrder?.agent_id ? selectedBillingAddress : selectedOfficeBillingAddress,
                            both_address_same: values.isSameAsBilling,
                            user_email: values.userEmail,
                            user_phone: values.userPhone,
                            gate_access_code: values.gatecode || "",
                            priority: Number(values.priority),
                            // ✅ Updated financial fields with special fees and discount
                            subtotal_amount: Number(removalCost),
                            removal_cost: parseFloat(String(removalCost || formik.values.installation_charge || 0)),
                            removal_tax_percentage: zeroOutTaxFields
                                ? "0"
                                : String(
                                      formik.values.calc_removal_rate_str ||
                                          lastResolvedRemovalRef.current?.removal_tax_percentage ||
                                          ""
                                  ),
                            removal_tax_amount: removalTax,
                            over_all_tax_percentage: zeroOutTaxFields
                                ? "0"
                                : String(
                                      formik.values.calc_overall_rate_str ||
                                          lastResolvedRemovalRef.current?.over_all_tax_percentage ||
                                          ""
                                  ),
                            over_all_tax_amount: overallTax,
                            total_amount: currentGrandTotalForSubmit,
                            ...(() => {
                                const submitDiscount = getSubmitRemovalDiscount(formik.values);
                                return {
                                    discount_code: submitDiscount.code,
                                    discount_amount: submitDiscount.code ? submitDiscount.amount : 0,
                                };
                            })(),
                            // ✅ Special fees: both taxable and non-taxable with correct flags in payload
                            specialfee: selectedSpecialFees.map((fee: any) =>
                                mapInstallationSpecialFeeForSubmit(fee, {
                                    zeroOutTaxFields,
                                    catalog: allAvailableSpecialFees,
                                })
                            ),
                            additional_instructions: [
                                {
                                    note: values.instructions || "",
                                    added_by: {
                                        id: userId,
                                        name: userName,
                                    },
                                    added_at: new Date().toISOString(),
                                },
                            ],
                        };

                    if (lastResolvedRemovalRef.current) {
                        editPatchBody = overlayRemovalSubmitFromResolved(
                            editPatchBody,
                            lastResolvedRemovalRef.current
                        );
                    }

                    if (isTaxDraft) {
                        const submitDiscount = getSubmitRemovalDiscount(formik.values);
                        const disc = submitDiscount.code ? submitDiscount.amount : 0;
                        const removalSub = Number(editPatchBody.removal_cost ?? removalCost ?? 0);
                        const feeSub = Array.isArray(editPatchBody.specialfee)
                            ? (editPatchBody.specialfee as Array<{ specialfee_amount?: number }>).reduce(
                                  (s, f) => s + Number(f.specialfee_amount || 0),
                                  0
                              )
                            : Number(formik.values.totals?.specialfee_subtotal || 0);
                        editPatchBody.total_amount = roundToCentsSubmit(removalSub + feeSub - disc);
                    }

                    const response = await axios.patch(
                        `${process.env.REACT_APP_API_URL}/removal_orders/${id}`,
                        editPatchBody,
                        {
                            headers: {
                                "Content-Type": "application/json",
                                Authorization: `Bearer ${token}`,
                            },
                        }
                    );

                    const data = response.data;

                    if (response.status !== 200) {
                        showError(data.error || "Failed to update removal order");
                        throw new Error(data.error || "Failed to update removal order");
                    }

                    // ✅ Trigger: Removal Order Edit - Send notifications
                    try {
                        const { createNotification } = await import("../../services/notificationApi");
                        const { getRemovalOrderEditContent } = await import("../../services/notificationContent");
                        const { fetchUserData } = await import("../../utils/userDataFetcher");

                        const orderId = `#${removalOrder?.order_number || removalOrder?._id || "REM-XXXX"}`;
                        const signageCompanyName = removalOrder?.signage_company_name || userName || "Signage Company";

                        // Resolve single end-user for edit notifications based on effectiveCouponCreatedById
                        if (effectiveCouponCreatedById && token) {
                            try {
                                const endUserData = await fetchUserData(effectiveCouponCreatedById, token);
                                if (endUserData?.id) {
                                    const recipientType =
                                        endUserData.licenseType === "AGENT" || endUserData.licenseType === "BROKERAGE_AGENT"
                                            ? "agent"
                                            : "brokerage_team";

                                    const endUserContent = getRemovalOrderEditContent({
                                        orderId: orderId,
                                        signageCompanyName: signageCompanyName,
                                        recipientType,
                                    });
                                    await createNotification(endUserData.id, endUserContent, token || "", recipientType);
                                }
                            } catch (err) {
                                safeConsole.warn("Failed to send removal order edit notification to end user:", err);
                            }
                        }
                    } catch (notifError) {
                        safeConsole.warn("Failed to send removal order edit notifications:", notifError);
                    }

                    if (response.status === 200 && refundSuccess) {
                        try {
                            if (!token) {
                                showError("Missing session token while attempting TaxCloud refund.");
                                return;
                            }

                            const order_type = removalOrder?.order_type || "removal";
                            const res = await PostVersionOrdernumber(token, removalOrder?.order_number || "", order_type);
                            safeConsole.log("[INSTALL:ORDER_SUBMIT] TaxCloud refund result:", { res });
                            if (!res) {
                                throw new Error("TaxCloud refund failed");
                            }
                        } catch (err) {
                            safeConsole.error("[INSTALL:ORDER_SUBMIT] PostVersionOrdernumber failed", err);
                            showError("TaxCloud refund call failed. Please try again.");
                            return;
                        }
                    }

                    // ✅ Check if form was modified
                    const initialValues = initialFormValuesRef.current;
                    const hasChanges = initialValues && (
                        values.scheduled_date !== initialValues.scheduledDate ||
                        selectedInstaller?.value !== initialValues.installerId ||
                        // JSON.stringify(removalAddress) !== initialValues.removalAddress ||
                        // values.notes !== initialValues.notes ||
                        JSON.stringify(updatedProducts) !== initialValues.lineItems ||
                        removalCost !== initialValues.removalCost ||
                        JSON.stringify(selectedSpecialFees) !== initialValues.specialFees
                    );

                    // ✅ Send email notifications if changes were made
                    if (hasChanges) {
                        try {
                            const from = process.env.REACT_APP_FROM_EMAIL2;
                            if (from && token) {
                                const orderNumber = removalOrder?.order_number || `#${removalOrder?._id || id || "REM-XXXX"}`;
                                const signageCompanyName = (isSignageTeammember && parentSignageName) ? parentSignageName : userName || "Signage Company";
                                const signageTeamMemberName = userName || "Signage Team Member";
                                const updatedOn = new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });

                                // Build changes description
                                const changesList: string[] = [];
                                if (values.scheduled_date !== initialValues.scheduledDate) {
                                    changesList.push("Date changed");
                                }
                                if (selectedInstaller?.value !== initialValues.installerId) {
                                    changesList.push("Installer changed");
                                }
                                // if (JSON.stringify(removalAddress) !== initialValues.removalAddress) {
                                //     changesList.push("Location updated");
                                // }
                                // if (values.notes !== initialValues.notes) {
                                //     changesList.push("Note added");
                                // }
                                if (JSON.stringify(updatedProducts) !== initialValues.lineItems) {
                                    changesList.push("Products updated");
                                }
                                const changesMade = changesList.length > 0 ? changesList.join(", ") : "Removal order details updated";

                                // Get agent name if applicable
                                const agentNameForEmail = removalOrder?.agent_name || (selectedOfficeAgent?.label) || (selectedAgent?.label) || "N/A";

                                // Property line for AGENT / BROKERAGE_AGENT templates ({{Property Address}} in emailConfig)
                                const mergedRemovalDetails = {
                                    ...(removalOrder?.removal_details || {}),
                                    ...(data?.removal_details || {}),
                                };
                                const removalAddrForEmail = mergedRemovalDetails?.removal_address;
                                const ad = removalAddrForEmail?.address_details;
                                const propertyAddr = ad
                                    ? `${ad.addressLine1 || ""}, ${ad.city || ""}, ${ad.state || ""} ${ad.zip || ""}`.trim().replace(/^,\s*|,\s*$/g, "")
                                    : "N/A";

                                // Installer notes / instructions ({{Optional field}} in emailConfig)
                                const additionalInstructionsSource =
                                    Array.isArray(data?.additional_instructions) && data.additional_instructions.length > 0
                                        ? data.additional_instructions
                                        : removalOrder?.additional_instructions || [];
                                const lastAdditional =
                                    additionalInstructionsSource.length > 0
                                        ? additionalInstructionsSource[additionalInstructionsSource.length - 1]
                                        : null;
                                const lastInstr = lastAdditional as { note?: string; value?: string } | null | undefined;
                                const lastNoteFromAdditional = String(lastInstr?.note ?? lastInstr?.value ?? "").trim();
                                const instructionsPart = String(values.instructions || "").trim();
                                let optionalFieldDisplay = [instructionsPart, lastNoteFromAdditional].filter(Boolean).join("\n");
                                if (!optionalFieldDisplay.trim()) {
                                    optionalFieldDisplay = String(data?.notes ?? removalOrder?.notes ?? "").trim();
                                }
                                if (!optionalFieldDisplay) {
                                    optionalFieldDisplay = "—";
                                }

                                // View order URL
                                const viewOrderUrl = `${window.location.origin}${baseRoute}/login`;

                                // Get scheduled date
                                const scheduledDate = values.scheduled_date ? new Date(values.scheduled_date).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }) : "N/A";

                                // await sendRemovalOrderTriggerEmail({
                                //     triggerName: "Signage Removal order edit",
                                //     removalOrderId: String(data?._id || id || ""),
                                //     token: token || "",
                                //     fromEmail: from,
                                //     extraParams: {
                                //         "Order ID": String(orderNumber),
                                //         "Order Number": String(orderNumber),
                                //         "Signage Company Name": String(signageCompanyName),
                                //         "Signage Team Member Name": String(signageTeamMemberName),
                                //         "Updated On": String(updatedOn),
                                //         "Updated Date": String(scheduledDate),
                                //         "Property Address": String(propertyAddr),
                                //         "Optional field": String(optionalFieldDisplay),
                                //         "Changes Made": String(changesMade),
                                //         "Agent Name": String(agentNameForEmail),
                                //         "View Updated Order": String(viewOrderUrl),
                                //         "Track My Orders": String(`${window.location.origin}/login`),
                                //     },
                                // });
                            }
                        } catch (emailError) {
                            safeConsole.warn("Failed to send removal order edit emails:", emailError);
                            // Don't fail the order update if email fails
                        }
                    }

                    showSuccess(data.message || "Removal order updated successfully!");
                    safeNavigate(`${baseRoute}/jobs/removalWorkorder/${data._id}`);
                    window.location.reload();
                }
                else {
                    // Close mode - Close removal order
                    // Step 1: Run Schedule or Reschedule First
                    let shouldProceed = true;
                    if (removalOrder?.status === 200) {
                        shouldProceed = await handleRemovalStatus(300); // returns true/false
                    }

                    if (!shouldProceed) {
                        return; // ❌ Stop here
                    }

                    const response = await fetch(
                        `${process.env.REACT_APP_API_URL}/removal_orders/${id}/closed`,
                        {
                            method: "POST",
                            headers: {
                                "Content-Type": "application/json",
                                Authorization: `Bearer ${token}`,
                            },
                            body: JSON.stringify({
                                final_line_items: line_items,
                                removal_details: {
                                    gate_access_code: values.gatecode || "",
                                    ...(values.scheduled_date
                                        ? { scheduled_date: new Date(values.scheduled_date).toISOString() }
                                        : {}),
                                    removal_address: removalOrder?.removal_details?.removal_address,
                                    installer_id: selectedInstaller?.value || "",
                                    installer_name: selectedInstaller?.label || "",
                                    contact_number: selectedInstaller?.phone || "",
                                    estimated_duration: null,
                                    actual_removal_date: removalOrder?.actual_removal_date || null,
                                    removal_notes: [
                                        {
                                            note: values.instructions || "",
                                            added_by: {
                                                id: userId,
                                                name: userName,
                                            },
                                            added_at: new Date().toISOString(),
                                        },
                                    ],
                                    assignee_id: selectedInstaller?.value,
                                    tax_details: null,
                                },
                                priority: Number(values.priority),
                                subtotal_amount: removalOrder?.subtotal_amount || 0,
                                tax_amount: 0,
                                total_amount: removalOrder?.total_amount || 0,
                                additional_instructions: [
                                    {
                                        note: values.instructions || "",
                                        added_by: {
                                            id: userId,
                                            name: userName,
                                        },
                                        added_at: new Date().toISOString(),
                                    },
                                ],
                            }),
                        }
                    );

                    const data = await response.json();

                    if (!response.ok) {
                        throw new Error(data.error || "Failed to update removal order");
                    }

                    showSuccess(data.message || "Removal order updated successfully!");

                    // Navigate to view mode with a small delay to ensure the success message is shown
                    setTimeout(() => {
                        safeNavigate(`${baseRoute}/jobs/removalWorkOrder/${id}`, { state: { type: "View" }, replace: true });
                        // Reload the page to ensure fresh data is loaded and avoid version mismatch errors
                        window.location.reload();
                    }, 500);
                }
            } catch (error: any) {
                safeConsole.error(error);
                showError(`Error: ${error.message}`);
            } finally {
                setFormSubmitting(false);
                setUploadProgress(0);
            }
        },
    });

    // ✅ Initialize removal cost and tax metadata from backend when order loads
    useEffect(() => {
        if (!removalOrder) return;

        // ✅ Initialize removal cost from backend (will be overridden by dynamic cost API if it runs)
        if (removalOrder.removal_cost !== undefined && removalCost === 0) {
            // console.log("💰 Initializing removal cost from backend:", removalOrder.removal_cost);
            setRemovalCost(Number(removalOrder.removal_cost));
        }

        // ✅ Initialize dynamic cost metadata (needed for tax calculations)
        if (removalOrder.dynamic_cost_id && !dynamicCostId) {
            // console.log("🔑 Initializing dynamic_cost_id from backend:", removalOrder.dynamic_cost_id);
            setDynamicCostId(removalOrder.dynamic_cost_id);
        }

        // ✅ Initialize taxable flag
        if (removalOrder.removal_taxable !== undefined && !dynamicTaxable) {
            setDynamicTaxable(removalOrder.removal_taxable);
        } else if (removalOrder.installation_taxable !== undefined && !dynamicTaxable) {
            setDynamicTaxable(removalOrder.installation_taxable);
        }
    }, [removalOrder]);

    // Removed featchBrokerageTeammembers useEffect - now using AsyncPaginate

    // Removed featchSignageTeammembers useEffect - now using AsyncPaginate

    // to get the scheduled job count based on the selected date
    useEffect(() => {
        const fetchScheduledJobs = async () => {
            if (!formik.values.scheduled_date || formik.values.scheduled_date.startsWith("0001-01-01") || !userId) return;

            const date = new Date(formik.values.scheduled_date).toISOString().split("T")[0]; // format as YYYY-MM-DD

            try {
                const res = await fetch(
                    `${process.env.REACT_APP_API_URL}/installation_orders/${formik.values.installeId}/${date}/scheduled_jobs`,
                    {
                        method: "GET",
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );

                const data = await res.json();
                if (!res.ok) throw new Error(data.message || "Failed to fetch job count");
                // console.log("Scheduled Jobs:", data);

                // setScheduledJobsCount(data.scheduled_jobs); // assuming response shape: { count: number }
                formik.setFieldValue("scheduled_jobs", data.scheduled_jobs);
            } catch (err: any) {
                safeConsole.error("Scheduled Jobs Error:", err.message);
                // setScheduledJobsCount(null);
            }
        };

        fetchScheduledJobs();
    }, [formik.values.installeId, userId, token]);

    const uploadFile = async (file: File): Promise<string> => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("uploadDir", "removal_requests");

        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open("POST", `${process.env.REACT_APP_API_URL}/images/upload`, true);
            xhr.setRequestHeader("Authorization", `Bearer ${token}`);

            xhr.upload.onprogress = (event) => {
                if (event.lengthComputable) {
                    setUploadProgress((event.loaded / event.total) * 100);
                }
            };

            xhr.onload = () => {
                if (xhr.status === 200 || xhr.status === 201) {
                    const response = JSON.parse(xhr.responseText);
                    resolve(response.path);
                } else {
                    reject(new Error("Failed to upload file"));
                }
            };

            xhr.onerror = () => reject(new Error("Network error during file upload"));
            xhr.send(formData);
        });
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.currentTarget.files?.[0];
        if (file) {
            formik.setFieldValue("image", file);
            setPreview(URL.createObjectURL(file));
        }
    };

    const handleLocationChange = (
        selectedOption: SingleValue<{ value: string; label: string }>
    ) => {
        formik.setFieldValue("location", selectedOption?.value || "");
    };

    const handleMapClick = useCallback((event: google.maps.MapMouseEvent) => {
        if (event.latLng) {
            const lat = event.latLng.lat();
            const lng = event.latLng.lng();
            setSelectedLocation({ lat, lng });
            formik.setFieldValue("location", { lat, lng });
        }
    }, []);

    // Helper functions for quantity validation algorithm (Tightened Algorithm)
    const getRowType = (item: any) => {
        return item.inventory_source === "rotational" ? "rotational" : "normal";
    };

    const getMaxQty = (item: any) => {
        return getRowType(item) === "rotational" ? 1 : item.quantity_to_be_removed;
    };

    const getSum = (item: any) => {
        if (getRowType(item) === "rotational") {
            return (item.restock_quantity || 0) + (item.writeoff_quantity || 0) + (item.damaged_quantity || 0) + (item.repair || 0);
        } else {
            return (item.restock_quantity || 0) + (item.writeoff_quantity || 0) + (item.damaged_quantity || 0);
        }
    };

    const getRemaining = (item: any) => {
        return getMaxQty(item) - getSum(item);
    };

    const shouldDisableField = (item: any, field: string) => {
        // Disable all fields when status is 600 (final submitted state)
        if (removalOrder?.status === 600) {
            return true;
        }

        const rowType = getRowType(item);
        const sum = getSum(item);
        const maxQty = getMaxQty(item);

        if (rowType === "normal") {
            // Single-bucket 100% allocation
            if (item.restock_quantity === maxQty) {
                return field === "writeoff_quantity" || field === "damaged_quantity";
            }
            if (item.writeoff_quantity === maxQty) {
                return field === "restock_quantity" || field === "damaged_quantity";
            }
            if (item.damaged_quantity === maxQty) {
                return field === "restock_quantity" || field === "writeoff_quantity";
            }

            // Split fully allocated case (remaining == 0 and more than one field > 0)
            if (getRemaining(item) === 0 && sum > 0) {
                const nonZeroFields = [];
                if ((item.restock_quantity || 0) > 0) nonZeroFields.push("restock_quantity");
                if ((item.writeoff_quantity || 0) > 0) nonZeroFields.push("writeoff_quantity");
                if ((item.damaged_quantity || 0) > 0) nonZeroFields.push("damaged_quantity");

                // If more than one field has value > 0, disable fields that are currently 0
                if (nonZeroFields.length > 1) {
                    return !nonZeroFields.includes(field);
                }
            }
        } else if (rowType === "rotational") {
            // Exactly one field should be 1, others should be 0 and disabled
            if (item.restock_quantity === 1) {
                return field === "writeoff_quantity" || field === "damaged_quantity" || field === "repair";
            }
            if (item.writeoff_quantity === 1) {
                return field === "restock_quantity" || field === "damaged_quantity" || field === "repair";
            }
            if (item.damaged_quantity === 1) {
                return field === "restock_quantity" || field === "writeoff_quantity" || field === "repair";
            }
            if (item.repair === 1) {
                return field === "restock_quantity" || field === "writeoff_quantity" || field === "damaged_quantity";
            }
        }
        return false;
    };

    const shouldEnableBinLocation = (item: any) => {
        // Disable bin location selection when status is 600 (final submitted state)
        if (removalOrder?.status === 600) {
            return false;
        }

        const rowType = getRowType(item);
        if (rowType === "normal") {
            return (item.restock_quantity || 0) > 0;
        } else if (rowType === "rotational") {
            // Treat Repair like Restock for bin location requirements
            return (item.restock_quantity || 0) === 1 || (item.repair || 0) === 1;
        }
        return false;
    };

    // Helper functions for rotational units
    const getRotationalSum = (serialId: string) => {
        const unit = updatedRotationalUnits[serialId];
        if (!unit) return 0;
        return (unit.restock_quantity || 0) + (unit.writeoff_quantity || 0) + (unit.damaged_quantity || 0) + (unit.repair || 0);
    };

    const shouldDisableRotationalField = (serialId: string, field: string) => {
        // Disable all fields when status is 600 (final submitted state)
        if (removalOrder?.status === 600) {
            return true;
        }

        const unit = updatedRotationalUnits[serialId];
        if (!unit) return false;

        // Exactly one field should be 1, others should be 0 and disabled
        if (unit.restock_quantity === 1) {
            return field === "writeoff_quantity" || field === "damaged_quantity" || field === "repair";
        }
        if (unit.writeoff_quantity === 1) {
            return field === "restock_quantity" || field === "damaged_quantity" || field === "repair";
        }
        if (unit.damaged_quantity === 1) {
            return field === "restock_quantity" || field === "writeoff_quantity" || field === "repair";
        }
        if (unit.repair === 1) {
            return field === "restock_quantity" || field === "writeoff_quantity" || field === "damaged_quantity";
        }
        return false;
    };

    const shouldEnableRotationalBinLocation = (serialId: string) => {
        // Disable bin location selection when status is 600 (final submitted state)
        if (removalOrder?.status === 600) {
            return false;
        }

        const unit = updatedRotationalUnits[serialId];
        // Treat Repair like Restock for bin location requirements
        return unit && (unit.restock_quantity === 1 || unit.repair === 1);
    };

    const updateProductField = (index: number, field: string, value: any) => {
        setUpdatedProducts((prev) => {
            const updated = [...prev];
            const currentItem = updated[index];
            const rowType = getRowType(currentItem);
            const maxQty = getMaxQty(currentItem);

            // Clamp value to valid range
            value = Math.max(0, Math.min(value, maxQty));

            if (rowType === "normal") {
                // Normal row logic
                const currentRestock = field === "restock_quantity" ? value : (currentItem.restock_quantity || 0);
                const currentWriteoff = field === "writeoff_quantity" ? value : (currentItem.writeoff_quantity || 0);
                const currentDamaged = field === "damaged_quantity" ? value : (currentItem.damaged_quantity || 0);

                const totalQuantity = currentRestock + currentWriteoff + currentDamaged;

                // If total exceeds max, adjust the current field value
                if (totalQuantity > maxQty) {
                    const otherFieldsTotal = totalQuantity - value;
                    value = Math.max(0, maxQty - otherFieldsTotal);
                }
            } else if (rowType === "rotational") {
                // Rotational row logic - exactly one field should be 1
                if (value > 0) {
                    // Set all other fields to 0
                    updated[index] = {
                        ...currentItem,
                        restock_quantity: field === "restock_quantity" ? value : 0,
                        writeoff_quantity: field === "writeoff_quantity" ? value : 0,
                        damaged_quantity: field === "damaged_quantity" ? value : 0,
                        repair: field === "repair" ? value : 0,
                    };
                    return updated;
                }
            }

            updated[index] = {
                ...currentItem,
                [field]: value,
            };

            return updated;
        });
    };

    // useEffect(() => {
    //     console.log("Updated Products:", updatedProducts);
    //     console.log("Product Notes:", productNotes);
    //     console.log("Rotational Units:", updatedRotationalUnits);
    // }, [updatedProducts, productNotes, updatedRotationalUnits]);

    const updateNoteField = (
        productId: string,
        field: "bin_location" | "note",
        value: string,
        label?: string
    ) => {
        setProductNotes((prev) => {
            // Special handling for bin_location: also update bin_location_display
            if (field === "bin_location") {
                return {
                    ...prev,
                    [productId]: {
                        ...prev[productId],
                        bin_location: value,
                        bin_location_display: label || "",
                    },
                };
            }

            return {
                ...prev,
                [productId]: {
                    ...prev[productId],
                    [field]: value,
                },
            };
        });
    };

    // Function to automatically set default bin locations for all products
    const setDefaultBinLocations = () => {
        const newProductNotes = { ...productNotes };
        let hasChanges = false;

        updatedProducts?.forEach((item: any) => {
            if (item.inventory_source !== "rotational") {
                // Check if this product already has a bin_location set
                if (!newProductNotes[item.product_id]?.bin_location) {
                    // Get the storage location for this product
                    const productStorage = item.storage_locations?.[0];
                    const hasValidStorageBin = productStorage &&
                        productStorage.warehouse_id &&
                        productStorage.warehouse_name &&
                        productStorage.zone_id &&
                        productStorage.zone_name &&
                        productStorage.rack_id &&
                        productStorage.rack_name &&
                        productStorage.bin_id &&
                        productStorage.bin_name;

                    if (hasValidStorageBin) {
                        // Set the default bin location
                        newProductNotes[item.product_id] = {
                            ...newProductNotes[item.product_id],
                            bin_location: productStorage.bin_id,
                        };
                        hasChanges = true;
                        // console.log(`Set default bin location for product ${item.product_id}: ${productStorage.bin_id}`);
                    }
                }
            }
        });

        // Also handle rotational units
        updatedProducts?.forEach((item: any) => {
            if (item.inventory_source === "rotational" && Array.isArray(item.serial_allocations)) {
                item.serial_allocations?.forEach((serial: any) => {
                    if (!updatedRotationalUnits[serial.serial_id]?.bin_location) {
                        // Get the storage location for this serial
                        const serialStorage = serial.storage_locations?.[0];
                        const hasValidStorageBin = serialStorage &&
                            serialStorage.warehouse_id &&
                            serialStorage.warehouse_name &&
                            serialStorage.zone_id &&
                            serialStorage.zone_name &&
                            serialStorage.rack_id &&
                            serialStorage.rack_name &&
                            serialStorage.bin_id &&
                            serialStorage.bin_name;

                        if (hasValidStorageBin) {
                            // Set the default bin location for rotational units
                            setUpdatedRotationalUnits((prev) => ({
                                ...prev,
                                [serial.serial_id]: {
                                    ...prev[serial.serial_id],
                                    bin_location: serialStorage.bin_id,
                                },
                            }));
                            // console.log(`Set default bin location for serial ${serial.serial_id}: ${serialStorage.bin_id}`);
                        }
                    }
                });
            }
        });

        if (hasChanges) {
            setProductNotes(newProductNotes);
            // console.log("Updated product notes with default bin locations");
        }
    };

    // Validation functions for submit-time validation with first-error focus
    // const validateForm = () => {
    //     const errors: Array<{ type: string; message: string; dataPath: string; element?: HTMLElement }> = [];

    //     // Process normal products first
    //     updatedProducts?.forEach((item: any, index: number) => {
    //         if (getRowType(item) === "normal") {
    //             const qtyToRemove = item.quantity_to_be_removed;
    //             const sum = getSum(item);

    //             // Check if qtyToRemove is valid
    //             if (!qtyToRemove || qtyToRemove < 1) {
    //                 errors.push({
    //                     type: "normal",
    //                     message: "Quantity to remove is required and must be at least 1",
    //                     dataPath: `normal[${index}].qtyToRemove`
    //                 });
    //                 return;
    //             }
    //             if (sum === 0) {
    //                 errors.push({
    //                     type: "normal",
    //                     message: `Allocate all ${qtyToRemove} units`,
    //                     dataPath: `normal[${index}].restock`
    //                 });
    //                 return;
    //             }

    //             if (sum < qtyToRemove) {
    //                 errors.push({
    //                     type: "normal",
    //                     message: `Allocate exactly ${qtyToRemove} (remaining ${qtyToRemove - sum})`,
    //                     dataPath: `normal[${index}].restock`
    //                 });
    //                 return;
    //             }

    //             if (sum > qtyToRemove) {
    //                 errors.push({
    //                     type: "normal",
    //                     message: `Total allocation exceeds quantity to remove`,
    //                     dataPath: `normal[${index}].restock`
    //                 });
    //                 return;
    //             }

    //             // Check if restock > 0 and binLocation is required
    //             if ((item.restock_quantity || 0) > 0) {
    //                 const binLocation = productNotes[item.product_id]?.bin_location;
    //                 if (!binLocation) {
    //                     errors.push({
    //                         type: "normal",
    //                         message: "Bin location is required for restock",
    //                         dataPath: `normal[${index}].binLocation`
    //                     });
    //                     return;
    //                 }
    //             }
    //         }
    //     });

    //     // Process rotational products
    //     updatedProducts?.forEach((item: any, index: number) => {
    //         if (getRowType(item) === "rotational" && Array.isArray(item.serial_allocations)) {
    //             item.serial_allocations?.forEach((serial: any, serialIndex: number) => {
    //                 const unit = updatedRotationalUnits[serial.serial_id];
    //                 if (!unit) return;

    //                 const sum = (unit.restock_quantity || 0) + (unit.writeoff_quantity || 0) + 
    //                            (unit.damaged_quantity || 0) + (unit.repair || 0);

    //                 // Check if exactly one choice is selected
    //                 if (sum === 0) {
    //                     errors.push({
    //                         type: "rotational",
    //                         message: "Pick exactly one: Restock / Lost / Damaged / Repair",
    //                         dataPath: `rotational[${index}][${serialIndex}].restock`
    //                     });
    //                     return;
    //                 }

    //                 if (sum > 1) {
    //                     errors.push({
    //                         type: "rotational",
    //                         message: "Only one status can be selected per asset",
    //                         dataPath: `rotational[${index}][${serialIndex}].restock`
    //                     });
    //                     return;
    //                 }

    //                 // Check if restock or repair is selected and binLocation is required
    //                 if ((unit.restock_quantity || 0) === 1 || (unit.repair || 0) === 1) {
    //                     if (!unit.bin_location) {
    //                         errors.push({
    //                             type: "rotational",
    //                             message: "Bin location is required for restock/repair",
    //                             dataPath: `rotational[${index}][${serialIndex}].binLocation`
    //                         });
    //                         return;
    //                     }
    //                 }
    //             });
    //         }
    //     });

    //     return errors;
    // };

    // const focusFirstError = (errors: Array<{ type: string; message: string; dataPath: string; element?: HTMLElement }>) => {
    //     if (errors.length === 0) return;

    //     const firstError = errors[0];
    //     let targetElement: HTMLElement | null = null;

    //     // Try to find the element by data-path attribute
    //     targetElement = document.querySelector(`[data-path="${firstError.dataPath}"]`) as HTMLElement;

    //     if (targetElement) {
    //         // Scroll the element into view and focus it
    //         targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    //         setTimeout(() => {
    //             targetElement?.focus();
    //         }, 100);
    //     }

    //     // Show error message
    //     showError(firstError.message);
    // };

    const updateRotationalUnit = (
        serialId: string,
        field: keyof (typeof updatedRotationalUnits[string]),
        value: any,
        _availableQty: number = 1, // no need for auto-distribution now
        label?: string
    ) => {
        setUpdatedRotationalUnits((prev) => {
            const current = prev[serialId] || {
                current_status: "",
                restock_quantity: 0,
                writeoff_quantity: 0,
                damaged_quantity: 0,
                reason_to_writeoff: "",
                repair: 0,
                bin_location: "",
                bin_location_display: "",
                note: "",
            };

            // For rotational units, exactly one field should be 1, others should be 0
            if (field === "restock_quantity" || field === "writeoff_quantity" || field === "damaged_quantity" || field === "repair") {
                // Clamp value to 0 or 1
                value = value > 0 ? 1 : 0;

                if (value === 1) {
                    // Set all other quantity fields to 0
                    return {
                        ...prev,
                        [serialId]: {
                            ...current,
                            restock_quantity: field === "restock_quantity" ? 1 : 0,
                            writeoff_quantity: field === "writeoff_quantity" ? 1 : 0,
                            damaged_quantity: field === "damaged_quantity" ? 1 : 0,
                            repair: field === "repair" ? 1 : 0,
                            [field]: 1,
                        },
                    };
                } else {
                    // If clearing a field, just set it to 0 (allow user to choose again)
                    return {
                        ...prev,
                        [serialId]: {
                            ...current,
                            [field]: 0,
                        },
                    };
                }
            }

            // Special handling for bin_location: also update bin_location_display
            if (field === "bin_location") {
                return {
                    ...prev,
                    [serialId]: {
                        ...current,
                        bin_location: value,
                        bin_location_display: label || "",
                    },
                };
            }

            return {
                ...prev,
                [serialId]: {
                    ...current,
                    [field]: value,
                },
            };
        });
    };

    // useEffect(() => {
    //     console.log("Updated Rotational Units:", updatedRotationalUnits);
    //     console.log("Product Notes:", productNotes);
    // }, [updatedRotationalUnits, productNotes]);


    const handleRemovalStatus = async (nextStatus: number): Promise<boolean> => {
        if (!id || !token) return false;
        setLoading(true);
        // console.log("installer_id :", selectedInstaller?.value)
        // console.log("installer_name :", selectedInstaller?.label)
        let url = "";
        let body: any = undefined;
        try {

            // const scheduledDate = removalOrder?.removal_details?.scheduled_date;
            const newDate = formik.values.scheduled_date;
            // const isDateChanged = newDate && newDate !== scheduledDate?.split("T")[0];

            switch (nextStatus) {
                case 200: // Finalize
                    url = `${process.env.REACT_APP_API_URL}/removal_orders/${id}/finalize`;
                    break;

                case 300: // Schedule or Reschedule
                    // if (isDateChanged) {
                    //     url = `${process.env.REACT_APP_API_URL}/removal_orders/${id}/reschedule`;
                    //     body = {
                    //         new_date: new Date(newDate).toISOString(),
                    //     };
                    // } else {

                    url = `${process.env.REACT_APP_API_URL}/removal_orders/${id}/schedule`;
                    body = {
                        // TODO: restore `new Date(newDate).toISOString()` once scheduled-date is required again.
                        scheduled_date: toScheduleIso(newDate, removalOrder?.removal_details?.scheduled_date),
                        installer_id: selectedInstaller?.value,
                        installer_name: selectedInstaller?.label,
                        contact_number: removalOrder?.user_phone,
                        estimated_duration: null,
                        // };
                    }
                    break;

                case 400:
                    url = `${process.env.REACT_APP_API_URL}/removal_orders/${id}/start`;
                    break;

                case 500:
                    // Reuse existing TaxCloud cart eligibility logic used by "Get Live Tax".
                    const selectedFeesForTax = getSelectedFees();
                    const selectedFeesForCartTax = getSelectedFeesForCart();
                    const mergedFeesForTax =
                        selectedFeesForTax.length > 0 ? selectedFeesForTax : selectedFeesForCartTax;
                    const taxableCartProducts = buildProductsMerged(mergedFeesForTax);
                    const hasAnyTaxableCartLine = taxableCartProducts.length > 0;
                    if (
                        taxType === "taxcloud" &&
                        hasAnyTaxableCartLine &&
                        removalOrder?.tax_status !== "complete"
                    ) {
                        completeAfterTaxReportRef.current = true;
                        const reported = await handleLiveTaxNext();
                        completeAfterTaxReportRef.current = false;
                        if (!reported) {
                            setLoading(false);
                            return false;
                        }
                    }
                    // Upload selected proof images when user clicks Complete.
                    if (proofUploadHandlerRef.current) {
                        await proofUploadHandlerRef.current();
                    }
                    url = `${process.env.REACT_APP_API_URL}/removal_orders/${id}/complete`;
                    break;

                case 700: // Cancel
                    let refundSuccess = false;
                    let nextTaxStatus = removalOrder?.tax_status || "draft";
                    if (removalOrder?.tax_status === "complete") {
                        try {
                            const forceType =
                                removalOrder?.tax_status === "complete" ? "taxcloud" : undefined;

                            refundSuccess = await (
                                installationTaxRef.current?.recalculate?.(forceType, {
                                    allowRefund: true,
                                }) ?? false
                            );

                            safeConsole.log("[INSTALL:ORDER_SUBMIT] tax recalc result:", { refundSuccess });

                            // 👉 RUN THIS ONLY IF refund is successful
                            if (refundSuccess) {
                                const order_type = removalOrder?.order_type || "removal";

                                const res = await PostVersionOrdernumber(
                                    token,
                                    removalOrder?.order_number || "",
                                    order_type
                                );

                                safeConsole.log("[INSTALL:ORDER_SUBMIT] TaxCloud refund result:", { res });

                                if (!res) throw new Error("TaxCloud refund failed");

                                nextTaxStatus = "";
                            } else {
                                safeConsole.log("[INSTALL:ORDER_SUBMIT] Refund NOT successful → skipping PostVersionOrdernumber");
                            }

                        } catch (err) {
                            safeConsole.error("[INSTALL:ORDER_SUBMIT] tax recalc failed", err);
                        }
                    }

                    url = `${process.env.REACT_APP_API_URL}/removal_orders/${id}/cancel`;
                    body = {
                        reason: formik.values.instructions || "Cancelled by user",
                    };
                    break;

                case 800: // Void
                    url = `${process.env.REACT_APP_API_URL}/removal_orders/${id}/void`;
                    body = {
                        lost_items: updatedProducts
                            // .filter((item) => item.writeoff_quantity > 0)
                            ?.map((item) => ({
                                product_id: item.product_id,
                                product_name: item.product_name,
                                warehouse_id: item.warehouse_id,
                                source_product_id: item.source_product_id,
                            })),
                    };
                    break;

                default:
                    showError("Invalid status selected");
                    setLoading(false);
                    return false;
            }

            const response = await fetch(url, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                ...(body ? { body: JSON.stringify(body) } : {}),
            });

            const result = await response.json();
            if (!response.ok) throw new Error(result?.error || "Status update failed");

            const updatedOrder = { ...removalOrder, status: nextStatus };

            // Send cancellation emails if order was canceled
            if (nextStatus === 700) {
                try {
                    const from = process.env.REACT_APP_FROM_EMAIL2;
                    if (from) {
                        const orderId = `#${updatedOrder.order_number || updatedOrder._id || "REM-XXXX"}`;
                        const canceledOn = new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
                        const currentUserName = getAuthCookie("userName") || "Signage Team Member";
                        const canceledBy = currentUserName;

                        // Format property address
                        const propertyAddr = updatedOrder?.removal_details?.removal_address?.address_details
                            ? `${updatedOrder.removal_details.removal_address.address_details.addressLine1 || ""}, ${updatedOrder.removal_details.removal_address.address_details.city || ""}, ${updatedOrder.removal_details.removal_address.address_details.state || ""} ${updatedOrder.removal_details.removal_address.address_details.zip || ""}`.trim().replace(/^,\s*|,\s*$/g, "")
                            : "N/A";

                        const reason = formik.values.instructions || "N/A";
                        const agentName = updatedOrder?.agent_name || "N/A";
                        const signageCompanyName = updatedOrder?.signage_company_name || userName || "Signage Company";

                        // Build base URL for order links
                        const baseUrl = (process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "");
                        const viewOrderUrl = `${baseUrl}${baseRoute}/jobs/removalWorkorder/${id}`;
                        const createOrderUrl = `${baseUrl}${baseRoute}/jobs/addRemovalRequest`;

                        // Import user data fetcher
                        const { fetchUserData } = await import("utils/userDataFetcher");

                        const cancelParams = {
                            orderId: orderId,
                            canceledOn: canceledOn,
                            canceledBy: canceledBy,
                            propertyAddress: propertyAddr,
                            reason: reason,
                            agentName: agentName,
                            signageCompanyName: signageCompanyName,
                            viewOrderUrl: viewOrderUrl,
                            createOrderUrl: createOrderUrl,
                        };

                        await sendRemovalOrderTriggerEmail({
                            triggerName: "Signage Removal order Canceled",
                            removalOrderId: String(updatedOrder?._id || id || ""),
                            token: token || "",
                            fromEmail: from,
                            extraParams: cancelParams,
                        });
                    }
                } catch (emailError) {
                    safeConsole.warn("Failed to send cancellation emails:", emailError);
                    // Don't block status update if email fails
                }

                // ✅ Trigger: Removal Order Canceled - Send notifications
                try {
                    const { createNotification } = await import("../../services/notificationApi");
                    const { getRemovalOrderCanceledContent } = await import("../../services/notificationContent");
                    const { fetchUserData } = await import("../../utils/userDataFetcher");

                    const orderId = `#${updatedOrder.order_number || updatedOrder._id || "REM-XXXX"}`;
                    const signageCompanyName = updatedOrder?.signage_company_name || userName || "Signage Company";
                    const currentUserName = getAuthCookie("userName") || "Agent/Brokerage Admin / Team member";
                    const canceledBy = currentUserName;

                    // Send notification to Signage Admin/Team Member (internal)
                    if (updatedOrder.signage_id && token) {
                        try {
                            const signageData = await fetchUserData(updatedOrder.signage_id, token);
                            if (signageData?.id) {
                                const signageContent = getRemovalOrderCanceledContent({
                                    orderId: orderId,
                                    canceledBy: canceledBy,
                                    recipientType: signageData.licenseType === "SIGNAGE_TEAM_MEMBER" ? "team_member" : "signage_admin",
                                });
                                await createNotification(updatedOrder.signage_id, signageContent, token || "", signageData.licenseType === "SIGNAGE_TEAM_MEMBER" ? "team_member" : "signage_admin");
                            }
                        } catch (err) {
                            safeConsole.warn("Failed to send cancellation notification to signage admin:", err);
                        }
                    }

                    // Send notification to single end user (effectiveCouponCreatedById) only
                    if (effectiveCouponCreatedById && token) {
                        try {
                            const endUserData = await fetchUserData(effectiveCouponCreatedById, token);
                            if (endUserData?.id) {
                                // Determine recipientType based on licenseType
                                let recipientType: "agent" | "brokerage_team" = "agent";
                                if (endUserData.licenseType === "BROKERAGE_FIRM" || endUserData.licenseType === "BROKERAGE_TEAM_MEMBER") {
                                    recipientType = "brokerage_team";
                                } else if (endUserData.licenseType === "AGENT" || endUserData.licenseType === "BROKERAGE_AGENT") {
                                    recipientType = "agent";
                                }
                                
                                const endUserContent = getRemovalOrderCanceledContent({
                                    orderId: orderId,
                                    signageCompanyName: signageCompanyName,
                                    recipientType: recipientType,
                                });
                                await createNotification(effectiveCouponCreatedById, endUserContent, token || "", recipientType);
                            }
                        } catch (err) {
                            safeConsole.warn("Failed to send cancellation notification to end user:", err);
                        }
                    }
                } catch (notifError) {
                    safeConsole.warn("Failed to send removal order cancellation notifications:", notifError);
                }
            }

            // Send voided emails if order was voided
            if (nextStatus === 800) {
                try {
                    const from = process.env.REACT_APP_FROM_EMAIL2;
                    if (from) {
                        const orderId = `#${updatedOrder.order_number || updatedOrder._id || "REM-XXXX"}`;
                        const voidedOn = new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
                        const currentUserName = getAuthCookie("userName") || "Signage Team Member";
                        const voidedBy = currentUserName;

                        // Format property address
                        const propertyAddr = updatedOrder?.removal_details?.removal_address?.address_details
                            ? `${updatedOrder.removal_details.removal_address.address_details.addressLine1 || ""}, ${updatedOrder.removal_details.removal_address.address_details.city || ""}, ${updatedOrder.removal_details.removal_address.address_details.state || ""} ${updatedOrder.removal_details.removal_address.address_details.zip || ""}`.trim().replace(/^,\s*|,\s*$/g, "")
                            : "N/A";

                        const reason = formik.values.instructions || "N/A";
                        const agentName = updatedOrder?.agent_name || "N/A";
                        const signageCompanyName = updatedOrder?.signage_company_name || userName || "Signage Company";

                        // Build base URL for order links
                        const baseUrl = (process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "");
                        const createOrderUrl = `${baseUrl}${baseRoute}/jobs/addRemovalRequest`;

                        // Import user data fetcher
                        const { fetchUserData } = await import("utils/userDataFetcher");

                        const voidParams = {
                            orderId: orderId,
                            voidedOn: voidedOn,
                            voidedBy: voidedBy,
                            propertyAddress: propertyAddr,
                            reason: reason,
                            agentName: agentName,
                            signageCompanyName: signageCompanyName,
                            createOrderUrl: createOrderUrl,
                        };

                        await sendRemovalOrderTriggerEmail({
                            triggerName: "Signage Removal order Voided",
                            removalOrderId: String(updatedOrder?._id || id || ""),
                            token: token || "",
                            fromEmail: from,
                            extraParams: voidParams,
                        });
                    }
                } catch (emailError) {
                    safeConsole.warn("Failed to send voided emails:", emailError);
                    // Don't block status update if email fails
                }

                // ✅ Trigger: Removal Order Void - Send notifications
                try {
                    const { createNotification } = await import("../../services/notificationApi");
                    const { getRemovalOrderVoidContent } = await import("../../services/notificationContent");
                    const { fetchUserData } = await import("../../utils/userDataFetcher");

                    const orderId = `#${updatedOrder.order_number || updatedOrder._id || "REM-XXXX"}`;
                    const signageCompanyName = updatedOrder?.signage_company_name || userName || "Signage Company";

                    // Send notification to single end user (effectiveCouponCreatedById) only
                    if (effectiveCouponCreatedById && token) {
                        try {
                            const endUserData = await fetchUserData(effectiveCouponCreatedById, token);
                            if (endUserData?.id) {
                                // Determine recipientType based on licenseType
                                let recipientType: "agent" | "brokerage_team" = "agent";
                                if (endUserData.licenseType === "BROKERAGE_FIRM" || endUserData.licenseType === "BROKERAGE_TEAM_MEMBER") {
                                    recipientType = "brokerage_team";
                                } else if (endUserData.licenseType === "AGENT" || endUserData.licenseType === "BROKERAGE_AGENT") {
                                    recipientType = "agent";
                                }

                                const endUserContent = getRemovalOrderVoidContent({
                                    orderId: orderId,
                                    ...(recipientType === "brokerage_team" ? { signageCompanyName: signageCompanyName } : {}),
                                    recipientType: recipientType,
                                });
                                await createNotification(effectiveCouponCreatedById, endUserContent, token || "", recipientType);
                            }
                        } catch (err) {
                            safeConsole.warn("Failed to send void notification to end user:", err);
                        }
                    }
                } catch (notifError) {
                    safeConsole.warn("Failed to send removal order void notifications:", notifError);
                }
            }

            // Schedule (300): "Finalized & Scheduled". Complete (500): "Completed". No email on Confirm 200 / In Progress 400.
            if (nextStatus === 300 || nextStatus === 500) {
                try {
                    const from = process.env.REACT_APP_FROM_EMAIL2;
                    if (from) {
                        const orderId = `#${updatedOrder.order_number || updatedOrder._id || "REM-XXXX"}`;
                        const newStatus = nextStatus === 300 ? "Finalized & Scheduled" : "Completed";
                        const updateTime = new Date().toLocaleString('en-US', {
                            month: '2-digit',
                            day: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: true
                        });
                        const currentUserName = getAuthCookie("userName") || "Signage Team Member";
                        const updatedBy = currentUserName;

                        // Format property address
                        const propertyAddr = updatedOrder?.removal_details?.removal_address?.address_details
                            ? `${updatedOrder.removal_details.removal_address.address_details.addressLine1 || ""}, ${updatedOrder.removal_details.removal_address.address_details.city || ""}, ${updatedOrder.removal_details.removal_address.address_details.state || ""} ${updatedOrder.removal_details.removal_address.address_details.zip || ""}`.trim().replace(/^,\s*|,\s*$/g, "")
                            : "N/A";

                        const agentName = updatedOrder?.agent_name || "N/A";
                        const brokerageFirm = updatedOrder?.brokerage_firm_name || "N/A";

                        // Build base URL for order links
                        const baseUrl = (process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "");
                        const viewOrderUrl = `${baseUrl}${baseRoute}/jobs/removalWorkorder/${id}`;

                        // Import user data fetcher
                        const { fetchUserData } = await import("utils/userDataFetcher");

                        const statusParams = {
                            orderId: orderId,
                            newStatus: newStatus,
                            agentName: agentName,
                            brokerageFirm: brokerageFirm,
                            propertyAddress: propertyAddr,
                            updatedBy: updatedBy,
                            updateTime: updateTime,
                            viewOrderUrl: viewOrderUrl,
                        };

                        await sendRemovalOrderTriggerEmail({
                            triggerName: "Signage Removal Status change",
                            removalOrderId: String(updatedOrder?._id || id || ""),
                            token: token || "",
                            fromEmail: from,
                            extraParams: statusParams,
                        });
                    }
                } catch (emailError) {
                    safeConsole.warn("Failed to send status change emails:", emailError);
                    // Don't block status update if email fails
                }

                // ✅ Trigger: Removal Status Update - Send notifications
                try {
                    const { createNotification } = await import("../../services/notificationApi");
                    const { getRemovalStatusUpdateContent } = await import("../../services/notificationContent");
                    const { fetchUserData } = await import("../../utils/userDataFetcher");

                    const orderId = `#${updatedOrder.order_number || updatedOrder._id || "REM-XXXX"}`;
                    const newStatus = nextStatus === 300 ? "Finalized & Scheduled" : "Completed";
                    const currentUserId = getAuthCookie("userId");
                    const isSignageUser = licenseType === "SIGNAGE_FIRM" || licenseType === "SIGNAGE_TEAM_MEMBER";

                    // Send notification to Signage Admin/Team Member (if they updated the status)
                    if (isSignageUser && updatedOrder.signage_id && currentUserId && token) {
                        try {
                            const signageData = await fetchUserData(updatedOrder.signage_id, token);
                            if (signageData?.id) {
                                const signageContent = getRemovalStatusUpdateContent({
                                    orderId: orderId,
                                    newStatus: newStatus,
                                    recipientType: signageData.licenseType === "SIGNAGE_TEAM_MEMBER" ? "team_member" : "signage_admin",
                                });
                                await createNotification(updatedOrder.signage_id, signageContent, token || "", signageData.licenseType === "SIGNAGE_TEAM_MEMBER" ? "team_member" : "signage_admin");
                            }
                        } catch (err) {
                            safeConsole.warn("Failed to send status update notification to signage admin:", err);
                        }
                    }

                    // Send notification to single end user (effectiveCouponCreatedById) only
                    if (effectiveCouponCreatedById && token) {
                        try {
                            const endUserData = await fetchUserData(effectiveCouponCreatedById, token);
                            if (endUserData?.id) {
                                // Determine recipientType based on licenseType
                                let recipientType: "agent" | "brokerage_team" = "agent";
                                if (endUserData.licenseType === "BROKERAGE_FIRM" || endUserData.licenseType === "BROKERAGE_TEAM_MEMBER") {
                                    recipientType = "brokerage_team";
                                } else if (endUserData.licenseType === "AGENT" || endUserData.licenseType === "BROKERAGE_AGENT") {
                                    recipientType = "agent";
                                }

                                const endUserContent = getRemovalStatusUpdateContent({
                                    orderId: orderId,
                                    newStatus: newStatus,
                                    recipientType: recipientType,
                                });
                                await createNotification(effectiveCouponCreatedById, endUserContent, token || "", recipientType);
                            }
                        } catch (err) {
                            safeConsole.warn("Failed to send status update notification to end user:", err);
                        }
                    }
                } catch (notifError) {
                    safeConsole.warn("Failed to send removal status update notifications:", notifError);
                }
            }

            showSuccess(result.message || "Removal status updated");
            setRemovalOrder(updatedOrder);
            safeNavigate(0); // Refresh the page to reflect changes
            return true;
        } catch (err: any) {
            safeConsole.error("Status update error:", err.message);
            showError(err.message || "Something went wrong");
            // Send error email notification
            let statusUpdatePayload: any = undefined;
            if (body) {
                statusUpdatePayload = body;
            }
            await handleApiError(err, {
                module: "removal",
                action: "Signage_Removal_Work_Order_handle_removal_status",
                userId: userId || undefined,
                licenseType: licenseType || undefined,
                userName: userName || undefined,
                url: url || "",
                method: "POST",
                requestBody: statusUpdatePayload,
                additionalInfo: {
                    removalOrderId: id || null,
                    nextStatus: nextStatus || null
                }
            }, token);
            return false;
        } finally {
            setLoading(false);
        }
    };



    // show actions ONLY in view mode
    // view mode only
    const isViewMode = !isEdit;



    type ViewAction =
        | "confirm"
        | "finalize"
        | "schedule"
        | "start"
        | "complete"
        | "startRestocking"
        | "confirmRestocking"
        | "restocking";

    // 100 → run 200 then 300
    // const handleConfirmFlow = async () => {
    //     const ok200 = await handleRemovalStatus(200);   // finalize
    //     if (!ok200) return;
    //     await handleRemovalStatus(300);                 // schedule (uses formik values)
    // };

    const handleConfirmFlow = async (): Promise<boolean> => {
        if (status === 200) {
            return await handleRemovalStatus(300);
        }
        const ok200 = await handleRemovalStatus(200);
        if (!ok200) return false;
        return await handleRemovalStatus(300);
    };

    // soft refresh wrapper like Installation
    const withRefresh = (fn: () => Promise<boolean>) => async () => {
        const ok = await fn();
        if (ok) safeNavigate(0);
    };

    const renderViewModeActions = () => {
        if (!isViewMode) return null;

        const status = removalOrder?.status ?? 0;
        const baseDisabled =
            formSubmitting ||
            formik.isSubmitting

        const hasPaymentInfo = (removalOrder?.payment_info?.length ?? 0) > 0;
        const actions: Array<{
            key: ViewAction;
            label: string;
            show: boolean;
            onClick: () => void;
            extraDisabled?: boolean;
            type?: "button" | "submit";
        }> = [
                {
                    key: "finalize",
                    label: "Finalize",
                    show: status === 100,
                    onClick: withRefresh(() => handleRemovalStatus(200)),
                },
                // {
                //     key: "confirm",
                //     label: "Confirm",
                //     show: status === 100,
                //     onClick: handleConfirmFlow,
                //     extraDisabled: status === 100 && !hasPaymentInfo,        // ← runs 200 then 300
                // },
                // {
                //     key: "confirm",
                //     label: "Confirm",
                //     show: status === 100,
                //     onClick: withRefresh(() => handleRemovalStatus(200)),
                // },
                {
                    key: "schedule",
                    label: "Schedule",
                    show: status === 200,
                    onClick: withRefresh(() => handleRemovalStatus(300)),
                },
                {
                    key: "start",
                    label: "Start",
                    show: status === 300,
                    onClick: withRefresh(() => handleRemovalStatus(400)),
                },
                {
                    key: "complete",
                    label: "Complete",
                    show: status === 400,
                    onClick: withRefresh(() => handleRemovalStatus(500)),
                },
                // {
                //     key: "startRestocking",
                //     label: "Confirm Restocking",
                //     show: status === 500,
                //     onClick: () => handleRemovalStatus(600),
                // },
                {
                    key: "confirmRestocking",
                    label: "Confirm Restocking",
                    show: status === 500,
                    onClick: () => formik.submitForm(),          // ← submit the form, NOT handleRemovalStatus
                    type: "submit",
                },
                // {
                //     key: "restocking",
                //     label: "Restocking",
                //     show: status === 700,
                //     onClick: () => handleRemovalStatus(800),
                // },
            ];

        return actions
            .filter(a => a.show)
            .map(a => (
                <React.Fragment key={a.key}>
                    {/* desktop buttons */}
                    <button
                        type={a.type ?? "button"}
                        onClick={a.onClick}
                        className="btn btn-primary fs-16 fw-bold save-btn surface-btn px-4 me-2 mb-2 actionbuttons d-none d-lg-block"
                        disabled={baseDisabled || !!a.extraDisabled}
                    >
                        {a.label}
                    </button>
                    {/* mobile buttons */}
                    <button
                        type={a.type ?? "button"}
                        onClick={a.onClick}
                        className="btn btn-primary fs-16 fw-bold save-btn surface-btn px-4 me-2 mb-2 actionbuttons d-lg-none w-100"
                        disabled={baseDisabled || !!a.extraDisabled}
                    >
                        {a.label}
                    </button>
                </React.Fragment>
            ));
    };




    // type ViewAction =
    //     | "confirm"
    //     | "schedule"
    //     | "start"
    //     | "complete"
    //     | "startRestocking"
    //     | "confirmRestocking"
    //     | "restocking";

    // // 100 → run 200 then 300
    // const handleConfirmFlow = async () => {
    //     const ok200 = await handleRemovalStatus(200);   // finalize
    //     if (!ok200) return;
    //     await handleRemovalStatus(300);                 // schedule (uses formik values)
    // };

    // const renderViewModeActions = () => {
    //     if (!isViewMode) return null;

    //     const status = removalOrder?.status ?? 0;
    //     const disabled = formSubmitting || !formik.values.localLawsChecked;

    //     const actions: Array<{
    //         key: ViewAction;
    //         label: string;
    //         show: boolean;
    //         onClick: () => void;
    //         type?: "button" | "submit";
    //     }> = [
    //             {
    //                 key: "confirm",
    //                 label: "Confirm",
    //                 show: status === 100,
    //                 onClick: handleConfirmFlow,                  // ← runs 200 then 300
    //             },
    //             // {
    //             //     key: "schedule",
    //             //     label: "Schedule",
    //             //     show: status === 200 || status === 300,
    //             //     onClick: () => handleRemovalStatus(300),
    //             // },
    //             {
    //                 key: "start",
    //                 label: "Start",
    //                 show: status === 300,
    //                 onClick: () => handleRemovalStatus(400),
    //             },
    //             {
    //                 key: "complete",
    //                 label: "Complete",
    //                 show: status === 400,
    //                 onClick: () => handleRemovalStatus(500),
    //             },
    //             // {
    //             //     key: "startRestocking",
    //             //     label: "Confirm Restocking",
    //             //     show: status === 500,
    //             //     onClick: () => handleRemovalStatus(600),
    //             // },
    //             {
    //                 key: "confirmRestocking",
    //                 label: "Confirm Restocking",
    //                 show: status === 500,
    //                 onClick: () => formik.submitForm(),          // ← submit the form, NOT handleRemovalStatus
    //                 type: "submit",
    //             },
    //             // {
    //             //     key: "restocking",
    //             //     label: "Restocking",
    //             //     show: status === 700,
    //             //     onClick: () => handleRemovalStatus(800),
    //             // },
    //         ];

    //     return actions
    //         .filter(a => a.show)
    //         .map(a => (
    //             <button
    //                 key={a.key}
    //                 type={a.type ?? "button"}
    //                 onClick={a.onClick}
    //                 className="btn btn-primary px-4 table-cells me-2 mb-2 actionbuttons"
    //                 disabled={disabled}
    //             >
    //                 {a.label}
    //             </button>
    //         ));
    // };



    // useEffect(() => {
    //     console.log("Updated Products:", updatedProducts);
    //     console.log("Product Notes:", productNotes);
    //     console.log("Rotational Units:", updatedRotationalUnits);

    //     // updatedProducts
    //     //     .filter(item => item.inventory_source !== "rotational")
    //     //     .flatMap(item => {
    //     //         const notes = productNotes[item.product_id]?.note || "";
    //     //         const binLocation = binOptions.find(opt => opt.value === productNotes[item.product_id]?.bin_location);
    //     //         console.log("binLocation", binLocation);
    //     //     });
    // }, [updatedProducts, productNotes, updatedRotationalUnits]);

    const [integrations, setIntegrations] = useState<PaymentGateway[]>([]);

    // Helper function and constants for AsyncPaginate
    const normalizeId = (row: any) => String(row?.id ?? row?._id ?? "");
    const PAGE_SIZE = 10;

    // ===== Signage Team Member Loader (for Assigned To dropdown) =====
    const loadSignageTeamMemberOptions: LoadOptions<SignageTeamMemberOption, any, { page: number }> = useCallback(
        async (inputValue, _loadedOptions, additional) => {
            const page = additional?.page ?? 1;
            // console.log("SignageTeamMemberLoader firing with:", { page, inputValue });

            try {
                const q = (inputValue || "").trim();

                const url =
                    `${process.env.REACT_APP_API_URL}/users-search` +
                    `?licenseType=SIGNAGE_TEAM_MEMBER` +
                    `&signageFirmID=${encodeURIComponent((isSignageTeammember && parentSignageId ? parentSignageId : userId) || "")}` +
                    `&page=${page}&limit=${PAGE_SIZE}` +
                    (q ? `&search=${encodeURIComponent(q)}` : "");

                const response = await fetch(url, {
                    headers: {
                        Accept: "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                });

                if (!response.ok) {
                    safeConsole.error("Failed to fetch signage team members:", response.statusText);
                    return { options: [], hasMore: false, additional: { page } };
                }

                const json = await response.json();
                // console.log('Signage team member API response:', { page, q, json });

                const list = Array.isArray(json) ? json : json.data ?? json.results ?? [];

                const options: SignageTeamMemberOption[] = list.map((u: any) => ({
                    value: normalizeId(u),
                    label: `${u?.firstName || ""} ${u?.lastName || ""}`.trim() || "(no name)",
                    firstName: u?.firstName || "",
                    lastName: u?.lastName || "",
                    email: u?.email || "",
                    phone: u?.phone1 || "",
                }));

                const hasMore = Array.isArray(json)
                    ? options.length === PAGE_SIZE
                    : json?.pagination?.more ??
                    (json?.total ? page * (json?.limit ?? PAGE_SIZE) < json.total : options.length === PAGE_SIZE);

                // console.log('✅ Mapped signage team member options:', options);
                // console.log('📊 Returning:', { optionsCount: options.length, hasMore, nextPage: page + 1 });

                return {
                    options,
                    hasMore,
                    additional: { page: page + 1 },
                };
            } catch (err) {
                safeConsole.error("Failed to load signage team member options:", err);
                return { options: [], hasMore: false, additional: { page } };
            }
        },
        [userId, token, isSignageTeammember, parentSignageId]
    );

    // ===== Brokerage Team Member Loader =====
    const loadBrokerageTeamMemberOptions: LoadOptions<BrokerageTeamMemberOption, any, { page: number }> = useCallback(
        async (inputValue, _loadedOptions, additional) => {
            const page = additional?.page ?? 1;

            if (!removalOrder?.brokerage_firm_id) {
                return { options: [], hasMore: false, additional: { page } };
            }

            try {
                const params = new URLSearchParams({
                    licenseType: "BROKERAGE_TEAM_MEMBER",
                    brokerageFirmID: removalOrder.brokerage_firm_id,
                    page: String(page),
                    limit: String(PAGE_SIZE)
                });

                const q = (inputValue || "").trim();
                if (q) params.set("search", q);

                const response = await fetch(`${process.env.REACT_APP_API_URL}/users-search?${params.toString()}`, {
                    headers: {
                        Accept: "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                });

                if (!response.ok) {
                    return { options: [], hasMore: false, additional: { page } };
                }

                const data = await response.json();
                const list = Array.isArray(data) ? data : data.data ?? data.results ?? [];

                const options: BrokerageTeamMemberOption[] = list.map((u: any) => ({
                    value: normalizeId(u),
                    label: `${u?.firstName || ""} ${u?.lastName || ""}`.trim() || "(no name)",
                    firstName: u?.firstName || "",
                    lastName: u?.lastName || "",
                    email: u?.email || "",
                    phone: u?.phone1 || "",
                    offices: u?.offices || [],
                }));

                const hasMore = Array.isArray(data)
                    ? options.length === PAGE_SIZE
                    : data?.pagination?.more ??
                    (data?.total ? page * (data?.limit ?? PAGE_SIZE) < data.total : options.length === PAGE_SIZE);

                return {
                    options,
                    hasMore,
                    additional: { page: page + 1 },
                };
            } catch (err) {
                safeConsole.error("Failed to load brokerage team member options:", err);
                return { options: [], hasMore: false, additional: { page } };
            }
        },
        [removalOrder?.brokerage_firm_id, token]
    );

    // ===== Bin Location Loader (with qty-based filtering and caching) =====
    const binLocationCacheRef = useRef<Map<string, { data: any; timestamp: number }>>(new Map());
    const binLocationPendingRequestsRef = useRef<Map<string, Promise<any>>>(new Map());
    const CACHE_DURATION = 30000; // 30 seconds cache

    const loadBinLocationOptions = useCallback(
        (qty: number) => {
            const loader: LoadOptions<any, any, { page: number }> = async (inputValue, _loadedOptions, additional) => {
                const page = additional?.page ?? 1;
                const q = (inputValue || "").trim();
                const normalizedQty = Math.max(1, qty || 1);

                // Create a unique cache key based on qty, search query, and page
                const cacheKey = `${normalizedQty}-${q}-${page}`;

                // Check if there's a pending request for this key
                const pendingRequest = binLocationPendingRequestsRef.current.get(cacheKey);
                if (pendingRequest) {
                    return pendingRequest;
                }

                // Check cache first
                const cached = binLocationCacheRef.current.get(cacheKey);
                if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
                    return cached.data;
                }

                try {
                    const body: any = {
                        qty: normalizedQty,
                        page,
                        limit: PAGE_SIZE,
                    };

                    if (q) {
                        body.search = q;
                    }

                    // Create and store the pending request promise
                    const requestPromise = fetch(`${process.env.REACT_APP_API_URL}/bins/pick-options`, {
                        method: "POST",
                        headers: {
                            Accept: "application/json",
                            Authorization: `Bearer ${token}`,
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify(body),
                    }).then(async (response) => {
                        if (!response.ok) {
                            safeConsole.error("Failed to fetch bin options:", response.statusText);
                            return { options: [], hasMore: false, additional: { page } };
                        }

                        const json = await response.json();
                        const list = json.results || [];

                        const options = list.map((bin: any) => ({
                            value: bin.bin_id,
                            label: `${bin.warehouse_name} > ${bin.zone_name} > ${bin.rack_name} > ${bin.bin_name}`,
                            bin: {
                                warehouse_id: bin.warehouse_id,
                                warehouse_name: bin.warehouse_name,
                                zone_id: bin.zone_id,
                                zone_name: bin.zone_name,
                                rack_id: bin.rack_id,
                                rack_name: bin.rack_name,
                                bin_id: bin.bin_id,
                                bin_name: bin.bin_name,
                            },
                        }));

                        const hasMore = json.pagination?.more ?? false;

                        const result = {
                            options,
                            hasMore,
                            additional: { page: page + 1 },
                        };

                        // Cache the result
                        binLocationCacheRef.current.set(cacheKey, {
                            data: result,
                            timestamp: Date.now(),
                        });

                        // Remove from pending requests
                        binLocationPendingRequestsRef.current.delete(cacheKey);

                        return result;
                    }).catch((err) => {
                        safeConsole.error("Failed to load bin location options:", err);
                        binLocationPendingRequestsRef.current.delete(cacheKey);
                        return { options: [], hasMore: false, additional: { page } };
                    });

                    // Store the pending request
                    binLocationPendingRequestsRef.current.set(cacheKey, requestPromise);

                    return requestPromise;
                } catch (err) {
                    console.error("Failed to load bin location options:", err);
                    binLocationPendingRequestsRef.current.delete(cacheKey);
                    return { options: [], hasMore: false, additional: { page } };
                }
            };
            return loader;
        },
        [token]
    );

    useEffect(() => {
        const fetchIntegrations = async () => {
            try {
                const supplierId = isSignageTeammember && parentSignageId ? parentSignageId : userId;
                const response = await fetch(`${process.env.REACT_APP_API_URL}/payments/gateway/created-by/${supplierId}`, {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                });
                if (!response.ok) {
                    throw new Error("Failed to fetch integrations.");
                }
                const data = await response.json();
                // console.log('Methods ', data);
                setIntegrations(data);
            } catch (err) {
                // showError("Failed to load integrations.");
                setError(err instanceof Error ? err.message : "An unknown error occurred");
            } finally {
                setLoading(false);
            }
        };

        fetchIntegrations();
    }, [token, isSignageTeammember, parentSignageId, userId]);

    const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);


    useEffect(() => {
        const stripeInfo = integrations?.find((method) => method.gateway === "stripe");
        // console.log('Stripe info ', stripeInfo);
        if (stripeInfo && stripeInfo.publishable_key) {
            const promise = loadStripe(stripeInfo.publishable_key);
            setStripePromise(promise);
        }
    }, [integrations]);

    // Helper function to send payment applied emails for removal orders
    const normalizePaymentMethodName = (name: string): string =>
        String(name || "").trim().toLowerCase().replace(/\s+/g, "_");
    const shouldSkipPaymentAppliedEmail = (paymentMethodName: string, isDynamicMethod: boolean): boolean => {
        const methodKey = normalizePaymentMethodName(paymentMethodName);
        return methodKey === "store_paid" || isDynamicMethod;
    };

    const sendRemovalPaymentAppliedEmails = async (
        updatedOrder: any,
        paymentAmountValue: number,
        paymentMethodName: string,
        fromEmail: string,
        isDynamicMethod: boolean = false
    ) => {
        if (!fromEmail || !updatedOrder) {
            safeConsole.warn("sendRemovalPaymentAppliedEmails: Missing fromEmail or updatedOrder", { fromEmail: !!fromEmail, updatedOrder: !!updatedOrder });
            return;
        }
        if (shouldSkipPaymentAppliedEmail(paymentMethodName, isDynamicMethod)) {
            safeConsole.log("sendRemovalPaymentAppliedEmails: skipped for payment method", { paymentMethodName, isDynamicMethod });
            return;
        }
        
        try {
            safeConsole.log("sendRemovalPaymentAppliedEmails: Starting email send", {
                orderId: updatedOrder._id || updatedOrder.id,
                paymentAmountValue,
                paymentMethodName
            });
            
            // Import user data fetcher
            const { fetchUserData } = await import("utils/userDataFetcher");
            
            // Format order ID
            const orderId = `#${updatedOrder.order_number || updatedOrder._id || updatedOrder.id || "REM-XXXX"}`;
            
            // Get payment details from updated order
            const paymentInfoArray = Array.isArray(updatedOrder.payment_info) ? updatedOrder.payment_info : [];
            const latestPayment = paymentInfoArray.length > 0
                ? paymentInfoArray.reduce((latest: any, current: any) => {
                    const latestDate = new Date(latest?.payment_date || 0).getTime();
                    const currentDate = new Date(current?.payment_date || 0).getTime();
                    return currentDate > latestDate ? current : latest;
                }, paymentInfoArray[0])
                : null;
            
            // Format payment amount - use latest payment if available, otherwise use the passed value
            const paymentAmount = latestPayment?.amount_paid 
                ? `$${Number(latestPayment.amount_paid).toFixed(2)}`
                : `$${Number(paymentAmountValue).toFixed(2)}`;
            
            // Format payment date
            const paymentDate = latestPayment?.payment_date
                ? new Date(latestPayment.payment_date).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
                : new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
            
            // Format payment method
            const formattedPaymentMethod = paymentMethodName === "online"
                ? "Credit Card"
                : paymentMethodName === "bank_transfer"
                ? "Bank Transfer"
                : paymentMethodName === "cheque"
                ? "Cheque"
                : paymentMethodName === "store_paid"
                ? "Store Paid"
                : paymentMethodName;
            
            // Calculate remaining balance from updated order
            const totalPaidAfter = paymentInfoArray.reduce(
                (acc: number, p: any) => acc + Number(p?.amount_paid || 0),
                0
            );
            const remainingBalance = Math.max(0, Number(updatedOrder.total_amount || 0) - totalPaidAfter);
            const currentBalance = remainingBalance > 0 ? `$${remainingBalance.toFixed(2)}` : "$0.00";

            let paymentPdfAttachment: Record<string, string> | undefined;
            try {
                const { buildPaymentAppliedOrderPdfAttachment } = await import(
                  "utils/paymentAppliedOrderPdfAttachment"
                );
                paymentPdfAttachment = await buildPaymentAppliedOrderPdfAttachment({
                    triggerName: "Signage Payment applied for removal Job",
                    order: String(updatedOrder?._id || updatedOrder?.id || ""),
                    kind: "removal",
                    imageBearerToken: token ?? "",
                    alreadyPaid: totalPaidAfter,
                    remainingBalance,
                });
            } catch (pdfErr) {
                safeConsole.warn("Payment-applied removal order summary PDF failed:", pdfErr);
            }
            
            // Determine who applied the payment - get current user from userId
            const currentUserId = getAuthCookie("userId");
            let currentUserData = null;
            if (currentUserId) {
                currentUserData = await fetchUserData(currentUserId, token);
            }
            
            const currentUserLicenseType = currentUserData?.licenseType || getAuthCookie("licenseType") || "";
            const currentUserEmail = currentUserData?.email || "";
            const currentUserName = currentUserData?.firstName ? `${currentUserData.firstName} ${currentUserData.lastName || ""}`.trim() : userName || "";
            
            let userRoleName = "User";
            if (currentUserLicenseType === "SIGNAGE_FIRM") {
                userRoleName = "Signage Admin";
            } else if (currentUserLicenseType === "SIGNAGE_TEAM_MEMBER") {
                userRoleName = "Signage Team Member";
            } else if (currentUserLicenseType === "BROKERAGE_FIRM") {
                userRoleName = "Brokerage Firm Admin";
            } else if (currentUserLicenseType === "BROKERAGE_TEAM_MEMBER") {
                userRoleName = "Brokerage Team Member";
            } else if (currentUserLicenseType === "AGENT" || currentUserLicenseType === "BROKERAGE_AGENT") {
                userRoleName = "Agent";
            }
            
            const paidByUser = (latestPayment as any)?.paid_by?.name || currentUserName || "User";
            const paidByEmail = (latestPayment as any)?.paid_by?.email || currentUserEmail || "";
            // const paymentAppliedBy = `${userRoleName} (${paidByEmail})`;
            const paymentAppliedBy = `(${paidByEmail})`;
            
            // Get agent name and brokerage firm name
            const agentName = updatedOrder.agent_name || updatedOrder.user_name || "N/A";
            const brokerageFirm = updatedOrder.brokerage_firm_name || "N/A";
            
            // Format property address
            const propertyAddr = updatedOrder.removal_details?.removal_address?.address_details
                ? `${updatedOrder.removal_details.removal_address.address_details.addressLine1 || ""}, ${updatedOrder.removal_details.removal_address.address_details.city || ""}, ${updatedOrder.removal_details.removal_address.address_details.state || ""} ${updatedOrder.removal_details.removal_address.address_details.zip || ""}`.trim().replace(/^,\s*|,\s*$/g, "")
                : "N/A";
            
            // Build base URL for order links
            const baseUrl = (process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "");
            const viewOrderUrl = `${baseUrl}${baseRoute}/jobs/removalWorkOrder/${updatedOrder._id || updatedOrder.id}`;
            
            // Common email parameters
            const commonParams: Record<string, string> = {
                orderId: orderId,
                agentName: agentName,
                brokerageFirm: brokerageFirm,
                propertyAddress: propertyAddr,
                paymentAppliedBy: paymentAppliedBy,
                paymentAmount: paymentAmount,
                paymentDate: paymentDate,
                paymentMethod: formattedPaymentMethod,
                currentBalance: currentBalance,
                viewOrderUrl: viewOrderUrl,
            };
            
            const isSignageUser = currentUserLicenseType === "SIGNAGE_FIRM" || currentUserLicenseType === "SIGNAGE_TEAM_MEMBER";
            const isBrokerageUser = currentUserLicenseType === "BROKERAGE_FIRM" || currentUserLicenseType === "BROKERAGE_TEAM_MEMBER";
            const isAgentUser = currentUserLicenseType === "AGENT" || currentUserLicenseType === "BROKERAGE_AGENT";

            await sendRemovalOrderTriggerEmail({
                triggerName: "Signage Payment applied for removal Job",
                removalOrderId: String(updatedOrder?._id || updatedOrder?.id || ""),
                token: token || "",
                fromEmail,
                includeOrderPdfAttachment: !paymentPdfAttachment,
                extraParams: commonParams,
            });

            // ✅ Trigger: Removal Payment Applied - Send notifications
            try {
                const { createNotification } = await import("../../services/notificationApi");
                const { getRemovalPaymentAppliedContent } = await import("../../services/notificationContent");
                
                // If payment was applied by brokerage/agent, notify signage admin/team member (internal)
                if ((isBrokerageUser || isAgentUser) && updatedOrder.signage_id) {
                    try {
                        const signageData = await fetchUserData(updatedOrder.signage_id, token);
                        if (signageData?.id) {
                            const signageContent = getRemovalPaymentAppliedContent({
                                orderId: orderId,
                                amount: paymentAmount.replace("$", ""),
                                recipientType: signageData.licenseType === "SIGNAGE_TEAM_MEMBER" ? "team_member" : "signage_admin",
                            });
                            await createNotification(updatedOrder.signage_id, signageContent, token || "", signageData.licenseType === "SIGNAGE_TEAM_MEMBER" ? "team_member" : "signage_admin");
                        }
                    } catch (err) {
                        safeConsole.warn("Failed to send payment notification to signage admin:", err);
                    }
                }

                // Send notification to single end user (effectiveCouponCreatedById) only
                if (effectiveCouponCreatedById && token) {
                    try {
                        const { fetchUserData } = await import("../../utils/userDataFetcher");
                        const endUserData = await fetchUserData(effectiveCouponCreatedById, token);
                        if (endUserData?.id) {
                            // Determine recipientType based on licenseType
                            let recipientType: "agent" | "brokerage_team" = "agent";
                            if (endUserData.licenseType === "BROKERAGE_FIRM" || endUserData.licenseType === "BROKERAGE_TEAM_MEMBER") {
                                recipientType = "brokerage_team";
                            } else if (endUserData.licenseType === "AGENT" || endUserData.licenseType === "BROKERAGE_AGENT") {
                                recipientType = "agent";
                            }
                            
                            const endUserContent = getRemovalPaymentAppliedContent({
                                orderId: orderId,
                                amount: paymentAmount.replace("$", ""),
                                recipientType: recipientType,
                            });
                            await createNotification(effectiveCouponCreatedById, endUserContent, token || "", recipientType);
                        }
                    } catch (err) {
                        safeConsole.warn("Failed to send payment notification to end user:", err);
                    }
                }
            } catch (notifError) {
                safeConsole.warn("Failed to send removal payment applied notifications:", notifError);
            }
        } catch (emailError) {
            safeConsole.error("Failed to send removal payment applied emails:", emailError);
            // Don't block payment success if email fails, but log the error for debugging
        }
    };

    const completeOrder = async (method: { name: string; _id: string }) => {
        // console.log("method", method);
        setLoading(true);
        let paymentSucceeded = false;

        try {
            // Find the selected payment method to check is_credit
            const selectedPaymentMethod = paymentMethod.find(pm => pm._id === method._id);
            const isCreditPayment = selectedPaymentMethod?.is_credit || false;

            // console.log("Selected payment method:", selectedPaymentMethod);
            // console.log("Is credit payment:", isCreditPayment);

            // Credit Payment Logic:
            // - If is_credit: false → User pays immediately → amount_paid: total, amount_due: 0
            // - If is_credit: true → User pays later → amount_paid: 0, amount_due: total

            const offlineStaticMethods = ["cheque", "bank_transfer", "store_paid"];
            const isDynamicMethod = method._id !== method.name;
            let actualAmountPaid = 0;

            if (offlineStaticMethods.includes(method.name)) {
                // 🔁 Call Net30-style API
                if (!formik.values.transactionId || formik.values.transactionId.trim() === "") {
                    showError("Transaction ID is required");
                    setLoading(false);
                    return;
                }

                const latestPayment = removalOrder?.payment_info.length > 0
                    ? Number(remaingamount)
                    : undefined;
                const lastAmountDue = latestPayment ?? removalOrder?.total_amount;

                // store_paid is always full payment; cheque/bank_transfer can use partial
                const isStorePaidMethod = method.name === "store_paid";
                actualAmountPaid = isStorePaidMethod
                    ? Number(lastAmountDue)
                    : formik.values.partial_amount > 0
                    ? Number(formik.values.partial_amount)
                    : Number(lastAmountDue);

                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/removal_orders/${removalOrder?._id}/net30_payment`,
                    {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`, // use token from context
                        },
                        body: JSON.stringify({
                            payment_id: formik.values.transactionId || "",
                            method: method.name,
                            amount_paid: Number(actualAmountPaid.toFixed(2)),
                            total_amount: Number(lastAmountDue.toFixed(2)),
                            amount_due: Number(
                                Math.max(0, Number(lastAmountDue) - Number(actualAmountPaid)).toFixed(2)
                            ),

                            // amount_due: Math.max(0, Number(lastAmountDue) - Number(actualAmountPaid)),
                            payment_date: new Date().toISOString(),
                            paid_by: {
                                id: userId,
                                name: userName,
                            },
                            payment_status: removalOrder?.payment_status ?? "",
                        }),
                    }
                );
                const data = await response.json();
                if (!response.ok) {
                    throw new Error(data?.error || "Cheque/Bank payment failed.");
                }

            } else {
                // 🔁 Default online payment API
                // Calculate payment amounts based on credit status
                const amountPaid = isCreditPayment ? 0 : remaingamount;
                const amountDue = isCreditPayment ? remaingamount : 0;

                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/removal_orders/${removalOrder?._id}/payment`,
                    {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`,
                        },
                        body: JSON.stringify({
                            payment_id: "",
                            total_amount: formik.values.total,
                            amount_paid: Number(amountPaid.toFixed(2)),
                            amount_due: Number(amountDue.toFixed(2)),
                            payment_date: new Date().toISOString(),
                            method: method.name,
                            paid_by: {
                                id: userId,
                                name: userName,
                            },
                            payment_status: removalOrder?.payment_status ?? "",
                        }),
                    }
                );
                const data = await response.json();
                if (!response.ok) {
                    throw new Error(data?.error || "Online payment failed.");
                }
            }

            // Send "Payment applied for Removal Job" emails
            try {
                const from = process.env.REACT_APP_FROM_EMAIL2;
                if (from && removalOrder) {
                    // Wait a moment for backend to process payment before fetching updated order
                    await new Promise(resolve => setTimeout(resolve, 1000));
                    
                    // Fetch updated order to get latest payment info (with retry)
                    let updatedOrder = removalOrder;
                    let retries = 3;
                    while (retries > 0) {
                        try {
                            const orderRes = await fetch(`${process.env.REACT_APP_API_URL}/removal_orders/${removalOrder._id}`, {
                                headers: { Authorization: `Bearer ${token}` },
                            });
                            if (orderRes.ok) {
                                const fetchedOrder = await orderRes.json();
                                // Check if payment_info has been updated (has at least one payment)
                                if (Array.isArray(fetchedOrder.payment_info) && fetchedOrder.payment_info.length > 0) {
                                    updatedOrder = fetchedOrder;
                                    break;
                                } else if (retries === 1) {
                                    // Last retry, use fetched order even if payment_info is empty
                                    updatedOrder = fetchedOrder;
                                }
                            }
                        } catch (fetchErr) {
                            safeConsole.warn(`Failed to fetch updated order (retries left: ${retries - 1}):`, fetchErr);
                        }
                        if (retries > 1) {
                            await new Promise(resolve => setTimeout(resolve, 500));
                        }
                        retries--;
                    }
                    
                    // Determine payment amount based on payment method
                    const paymentAmount = offlineStaticMethods.includes(method.name)
                        ? actualAmountPaid
                        : (isCreditPayment ? 0 : remaingamount);
                    
                    await sendRemovalPaymentAppliedEmails(updatedOrder, paymentAmount, method.name, from, isDynamicMethod);
                }
            } catch (emailError) {
                safeConsole.warn("Failed to send payment applied emails:", emailError);
                // Don't block payment success if email fails
            }

            showSuccess("Payment submitted successfully!");
            localStorage.removeItem("cart_id");
            paymentSucceeded = true;
        } catch (error) {
            const errMessage =
                (error as Error).message || "An unknown error occurred";
            safeConsole.error("Order Failed:", errMessage);
            showError(`Error: ${errMessage}`);
        } finally {
            setLoading(false);
            if (paymentSucceeded) {
                safeNavigate(0);
            }
        }
    };

    // ✅ Calculate totalPaid from payment history
    const totalPaidFixed = useMemo(() => {
        return (removalOrder?.payment_info ?? []).reduce(
            (acc: number, payment: { amount_paid?: number | string }) =>
                acc + Number(payment.amount_paid || 0),
            0
        );
    }, [removalOrder?.payment_info]);

    const totalPaid = Number(totalPaidFixed.toFixed(2));
    // console.log("totalPaid", totalPaid);

    useEffect(() => {
        setDiscountUserTouched(false);
    }, [removalOrder?.order_number]);

    const preferFormikDiscount = isEdit && discountUserTouched;
    const markDiscountTouched = useCallback(() => setDiscountUserTouched(true), []);

    React.useEffect(() => {
        formikValuesRef.current = formik.values;
    }, [formik.values]);

    React.useEffect(() => {
        const code = String(removalOrder?.discount_code || "").trim();
        if (code && !discountUserTouched) {
            setCouponInput(code);
            latestDiscountCodeRef.current = code;
        }
    }, [removalOrder?.discount_code, removalOrder?.order_number, discountUserTouched]);

    const costBreakdownDiscount = useMemo(
        () => resolveRemovalOrderDiscount(removalOrder, formik.values, preferFormikDiscount),
        [
            removalOrder,
            preferFormikDiscount,
            removalOrder?.discount_amount,
            removalOrder?.discount_code,
            formik.values?.discount_amount,
            formik.values?.discount_code,
            formik.values?.discount,
        ]
    );
    const showCostBreakdownDiscount =
        Boolean(costBreakdownDiscount.code) || costBreakdownDiscount.amount > 0;

    const effectiveDiscountAmount = preferFormikDiscount
        ? Number(formik.values.discount_amount || 0)
        : costBreakdownDiscount.amount;

    const resolvedTaxTotal = useMemo(
        () =>
            Number(formik.values.totals?.over_all_tax_amount || 0) > 0
                ? Number(formik.values.totals.over_all_tax_amount)
                : Number(formik.values.tax || 0) > 0
                  ? Number(formik.values.tax)
                  : Number(formik.values.totals?.removal_tax_amount || 0) +
                    Number(formik.values.totals?.specialfee_tax_amount || 0),
        [
            formik.values.totals?.over_all_tax_amount,
            formik.values.tax,
            formik.values.totals?.removal_tax_amount,
            formik.values.totals?.specialfee_tax_amount,
        ]
    );

    // ✅ Round to 2 decimals (cents) to avoid floating-point mismatch between UI and Stripe
    const roundToCents = (n: number) => Math.round(Number(n) * 100) / 100;

    // Sum breakdown lines: removal + special fees + tax − discount (matches installation work order)
    const currentGrandTotalValue =
        Number(formik.values.totals?.removal_subtotal || removalCost || 0) +
        Number(formik.values.totals?.specialfee_subtotal || 0) +
        resolvedTaxTotal -
        effectiveDiscountAmount;

    const currentGrandTotal = (roundToCents(currentGrandTotalValue)).toFixed(2);
    const remaingamount = roundToCents(Number(currentGrandTotal || 0) - totalPaid);
    // console.log("remaingamount", remaingamount);

    const maxAllowed = (removalOrder?.payment_info ?? []).length > 0
        ? roundToCents(Number(currentGrandTotal || 0) - totalPaid)
        : Number(currentGrandTotal || 0);

    const statusHistory: any[] = Array.isArray(removalOrder?.status_history)
        ? (removalOrder?.status_history as any[])
        : [];
    // console.log("statusHistory", statusHistory);
    const paymentInfo: PaymentInfoItem[] = [...(removalOrder?.payment_info || [])];

    const resolveOrderStatus = (status: number | undefined): string => {
        switch (status) {
            case 100: return "Draft";
            case 200: return "Finalized";
            case 300: return "Scheduled";
            case 400: return "In Progress";
            case 500: return "Completed";
            case 600: return "Closed";
            case 700: return "Canceled";
            case 800: return "Lost";
            default: return status !== undefined ? `Status ${status}` : "Unknown";
        }
    };


    const parseDate = (input: any): string => {
        try {
            return new Date(input).toLocaleString();
        } catch {
            return "Invalid Date";
        }
    };

    useEffect(() => {
        const fetchPaymentIntent = async () => {
            // const amountToPay = (formik.values.partial_amount === 0
            //   ? (Number(order?.total_amount || 0) - totalPaid)
            //   : formik.values.partial_amount);

            safeConsole.log("amountToPay", (formik.values.partial_amount === 0
                ? (Number(removalOrder?.total_amount || 0) - totalPaid)
                : formik.values.partial_amount));

            try {
                const supplierId = isSignageTeammember && parentSignageId ? parentSignageId : userId;
                const res = await fetch(
                    `${process.env.REACT_APP_API_URL}/payments/stripe/payment-intent/${supplierId}`,
                    {
                        method: "POST",
                        headers: {
                            Authorization: `Bearer ${token}`,
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify({
                            // Use same rounded amount we show in Cost Breakdown so Stripe charge matches Total Amount
                            amount: Math.round(
                                Number(
                                    formik.values.partial_amount === 0
                                        ? remaingamount
                                        : formik.values.partial_amount
                                ) * 100
                            ),
                            currency: "usd",
                            order_id: String(removalOrder?._id || removalOrder?.id || ""),
                            order_number: String(removalOrder?.order_number || ""),
                            customer_name: String(removalOrder?.user_name || ""),
                            customer_email: String(removalOrder?.user_email || ""),
                            customer_phone: String(removalOrder?.user_phone || ""),
                            payment_method_types: ["card"],
                        }),
                    }
                );
                const data = await res.json();
                setClientSecret(data.clientSecret || data.client_secret);
            } catch (err) {
                safeConsole.error("Failed to create PaymentIntent:", err);
            }
        };

        if (stripePromise) fetchPaymentIntent();
    }, [
        stripePromise,
        formik.values.partial_amount,
        remaingamount,
        removalOrder?._id,
        removalOrder?.id,
        removalOrder?.order_number,
        removalOrder?.user_name,
        removalOrder?.user_email,
        removalOrder?.user_phone,
        isSignageTeammember,
        parentSignageId,
        userId,
    ]);

    const uploadProofOfRemovals = async (files: any[]) => {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/removal_orders/${id}/upload-proof`,
                {
                    method: "POST",
                    headers: {
                        "Authorization": `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(files), // send array of files
                }
            );

            const data = await response.json();
            if (!response.ok) throw new Error(data.message || "Failed to upload proof");
            showSuccess("Proof of installation uploaded successfully!");
        } catch (error: any) {
            showError(error.message || "Upload failed");
        }
    };

    const notifyCustomerProofAvailable = async () => {
        if (!id || !token) {
            throw new Error("Missing removal order or session token");
        }

        const response = await fetch(
            `${process.env.REACT_APP_API_URL}/removal_orders/${id}/proof-available-email`,
            {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            }
        );

        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
            throw new Error(data.message || data.error || "Failed to notify customer");
        }

        showSuccess("Customer notified successfully!");
    };

    useEffect(() => {
        if (removalOrder?.proof_of_removal) {
            setUploadedFileMeta(removalOrder.proof_of_removal);
            // console.log("Uploaded File Meta:", removalOrder.proof_of_removal);
        }
    }, [removalOrder]);
    const isFieldEditable = (status: number, field: string): boolean => {
        const editableMap: Record<number, string[]> = {
            100: [
                // Draft
                "BillingAddress",
                "ScheduleDate",
                "Installer",
                "Priority",
                "Notes",
                "Instructions",
                "removalQuantity",
                "userEmail",
                "userPhone",
            ],
            200: [
                // Payment Accepted (Finalized)
                "BillingAddress",
                "ScheduleDate",
                "Installer",
                "Priority",
                "Notes",
                "Instructions",
                "removalQuantity",
                "userEmail",
                "userPhone",
            ],
            300: [
                // Scheduled or Rescheduled
                "ScheduleDate",
                "Installer",
                "Priority",
                "Notes",
                "Instructions",
                "userEmail",
                "userPhone",
            ],
            // 400: [
            //     // In Progress
            //     "Instructions", // Only instructions editable
            // ],
            // 500 and 600: No edits allowed
        };

        const nonEditableStatuses = [400, 500, 600, 700, 800]; // Completed, Canceled

        if (nonEditableStatuses.includes(status)) return false;

        const editableFields = editableMap[status] || [];
        return editableFields.includes(field);
    };
    const status = removalOrder?.status ?? 0;

    // useEffect(() => {
    //     console.log("address", removalOrder?.removal_details?.removal_address?.address_details?.addressLine1, removalOrder?.removal_details?.removal_address?.address_details?.city, removalOrder?.removal_details?.removal_address?.address_details?.state);
    // }, [removalOrder]);

    const addr = removalOrder?.removal_details?.removal_address?.address_details;
    const fullAddress = [addr?.addressLine1, addr?.city, addr?.state]
        .filter(Boolean)
        .join(", ");


    const removalLogEvents = React.useMemo(
        () => buildRemovalLogEventsFromOrder(removalOrder),
        [removalOrder]
    );

    // Function to update gate access code only
    const handleSaveGateCode = async () => {
        try {
            const response = await axios.patch(
                `${process.env.REACT_APP_API_URL}/removal_orders/${id}`,
                {
                    version: removalOrder?.version,
                    gate_access_code: formik.values.gatecode,
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (response.status === 200) {
                showSuccess("Gate access code updated successfully!");
                // Refresh the removal order details instead of manually updating state
                await fetchRemovalOrder();
            } else {
                showError("Failed to update gate access code");
            }
        } catch (error: any) {
            showError(`Error updating gate access code: ${error.message}`);
        }
    };

    // Function to update instructions only
    const handleSaveInstructionsOnly = async (instructions: string) => {
        try {
            const response = await axios.patch(
                `${process.env.REACT_APP_API_URL}/removal_orders/${id}`,
                {
                    version: removalOrder?.version,
                    additional_instructions: [{ value: instructions }],
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (response.status === 200) {
                showSuccess("Instructions updated successfully!");
                // Update the local state to reflect the change
                setInstructions(instructions);
                // Also update formik value to keep them in sync
                formik.setFieldValue("instructions", instructions);
                // Refresh the removal order details instead of reloading the page
                await fetchRemovalOrder();
            } else {
                showError("Failed to update instructions");
            }
        } catch (error: any) {
            showError(`Error updating instructions: ${error.message}`);
        }
    };

    // Initialize instructions from installation order after formik is declared
    useEffect(() => {
        if (removalOrder) {
            if (removalOrder?.additional_instructions) {
                const latestInstruction = removalOrder?.additional_instructions[removalOrder?.additional_instructions.length - 1];
                if (latestInstruction?.value) {
                    setInstructions(latestInstruction.value);
                    formik.setFieldValue("instructions", latestInstruction.value);
                }
            }
        }
    }, [removalOrder]);


    const [sendInvoiceLoading, setSendInvoiceLoading] = useState(false);
    const fromInvoiceEmail =
        process.env.REACT_APP_FROM_EMAIL2?.trim() || "noreply@realesy.com";

    const handleSendInvoiceEmail = async (additionalEmails: string[] = []) => {
        if (!removalOrder || sendInvoiceLoading) return;
        if (!token) {
            showError("Session expired. Please sign in again.");
            return;
        }
        const recipientPlan = await buildInvoiceSendRecipientPlan(removalOrder, true, token);
        recipientPlan.customerRecipients = mergeAdditionalIntoInvoiceCustomerRecipients(
            recipientPlan.customerRecipients,
            additionalEmails
        );
        const hasRecipients =
            recipientPlan.customerRecipients.emails.length > 0 ||
            recipientPlan.accountEmails.length > 0;
        if (!hasRecipients) {
            showError("No brokerage, customer, or account email found for this job; cannot send the invoice.");
            return;
        }
        setSendInvoiceLoading(true);
        try {
            const removalOrderId = String(removalOrder?._id || removalOrder?.id || "").trim();
            if (!removalOrderId) {
                throw new Error("Missing removal order id.");
            }
            const safeNum = String(
                removalOrder?.order_number ??
                removalOrder?.job_number ??
                removalOrder?.id ??
                removalOrder?._id ??
                "order"
            ).replace(/[^\w.-]+/g, "_");
            const pdfAttachment = await buildRemovalOrderInvoicePdfAttachment({
                removalOrderId,
                token,
                fileLabel: safeNum,
                pdfFetch: {
                    maxAttempts: 8,
                    initialDelayMs: 400,
                    retryDelayMs: 600,
                    perFetchTimeoutMs: 20_000,
                },
            });
            if (!pdfAttachment) {
                throw new Error("Could not fetch invoice PDF from the server.");
            }
            const baseParams = await buildInvoiceEmailParamsForSend(removalOrder, token, "removal");
            await sendInvoiceTriggerEmails(
                baseParams,
                recipientPlan,
                fromInvoiceEmail,
                token,
                pdfAttachment
            );
            const sendLogOrderId = String(
                removalOrder?.id || removalOrder?._id || removalOrder?.order_id || ""
            ).trim();
            if (sendLogOrderId) {
                try {
                    await createSendLogEntry({
                        orderType: "removal_orders",
                        orderId: sendLogOrderId,
                        type: "invoice",
                        token,
                    });
                } catch (e) {
                    safeConsole.warn("[SignageRemovalWorkOrder] send-log invoice failed:", e);
                }
            }
            showSuccess("Invoice email sent.");
        } catch (e) {
            showError(e instanceof Error ? e.message : "Failed to send invoice email.");
        } finally {
            setSendInvoiceLoading(false);
        }
    };


    // ✅ Build TaxCloud destination from removal address
    const pick = (v?: string | null) => (v ?? "").trim() || undefined;
    const zip5 = (z?: string | null) => ((z ?? "").trim() || "").slice(0, 5) || undefined;

    const buildTaxCloudDestination = (): TaxCloudAddress => {
        const addr = removalOrder?.removal_details?.removal_address?.address_details;
        const destMaybe = toTaxCloudAddressFromObject({
            addressLine1: pick(addr?.addressLine1),
            city: pick(addr?.city),
            state: pick(addr?.stateCode) || "GA",
            zip: zip5(addr?.zip),
            country: pick(addr?.countryCode) || "US",
        });
        if (!destMaybe) throw new Error("Invalid destination address for TaxCloud");
        return destMaybe;
    };

    // ✅ Get selected fees for cart (simplified version)
    const getSelectedFeesForCart = () => {
        return selectedSpecialFees
            .map((f: any) => normalizeInstallationSelectedFee(f, allAvailableSpecialFees))
            .filter((f) => f.id !== "" && f.amount > 0);
    };

    // if orderId contains "-", split and take the number after "-"
    let finalCartId = ""
    if (ordernumber && removalOrder?.cart_id) {
        const suffix = ordernumber.includes("-")
            ? ordernumber.split("-")[1]   // get part after "-"
            : "";                     // no suffix

        // append suffix to cart_id
        finalCartId = suffix ? `${removalOrder?.cart_id}-${suffix}` : removalOrder?.cart_id;
    }

    type RecomputeOverride = { fees?: any[]; discountCode?: string; force?: boolean };

    const buildRemovalFeeSignature = (fees: any[]) =>
        fees
            .map((f) => `${f.id}:${Number(f.amount || 0)}`)
            .sort()
            .join(",");

    const callWorkOrderResolveDraft = React.useCallback(
        async (
            feesInput?: any[],
            discountCodeOverride?: string,
            action: OrderResolveAction = "draft"
        ) => {
            if (!dynamicCostId || !token) return null;

            const fees = (feesInput ?? selectedSpecialFees)
                .map((f: any) => normalizeInstallationSelectedFee(f, allAvailableSpecialFees))
                .filter((f) => f.id !== "");

            let cartId = String(removalOrder?.cart_id || "");
            if (ordernumber && removalOrder?.cart_id) {
                const suffix = ordernumber.includes("-") ? ordernumber.split("-")[1] : "";
                cartId = suffix ? `${removalOrder.cart_id}-${suffix}` : String(removalOrder.cart_id);
            }

            const formikVals = formikValuesRef.current || {};
            const discountCode =
                discountCodeOverride !== undefined
                    ? String(discountCodeOverride).trim()
                    : String(
                          formikVals.discount_code ||
                              latestDiscountCodeRef.current ||
                              removalOrder?.discount_code ||
                              ""
                      ).trim();

            const resolvePayload = buildRemovalResolvePayload({
                orderNumber: String(removalOrder?.order_number || "").trim(),
                cartId,
                dynamicCostId,
                signageId: String(removalOrder?.signage_id || signageId || ""),
                sourceId: String(
                    removalOrder?.source_id ||
                        removalOrder?.agent_id ||
                        removalOrder?.office?._id ||
                        ""
                ),
                customerId: String(effectiveCouponCreatedById || removalOrder?.source_id || ""),
                inventorySource: String(removalOrder?.inventory_source || "agent"),
                removalCost: Number(removalCost ?? formikVals.installation_charge ?? 0),
                removalTaxable: dynamicTaxable,
                discountCode,
                paymentMethod: formikVals.payment_method || "card",
                userType: formikVals.userType,
                userName: `${formikVals.userFirstName || ""} ${formikVals.userLastName || ""}`.trim(),
                userEmail: formikVals.userEmail,
                userPhone: formikVals.userPhone,
                brokerageFirmId: removalOrder?.brokerage_firm_id ?? null,
                brokerageFirmName: removalOrder?.brokerage_firm_name ?? null,
                brokerageFirmAdmin: removalOrder?.brokerage_firm_admin ?? "",
                teamMemberId: formikVals.teamMemberId || removalOrder?.team_member_id || null,
                teamMemberName: removalOrder?.team_member_name || "",
                office: removalOrder?.office,
                agentId: formikVals.agentId || removalOrder?.agent_id || null,
                agentName: removalOrder?.agent_name || null,
                billingAddress: (removalOrder?.agent_id
                    ? selectedBillingAddress
                    : selectedOfficeBillingAddress) as InstallationResolveAddress | null,
                removalAddress: removalOrder?.removal_details?.removal_address as InstallationResolveAddress | null,
                installerId: String(selectedInstaller?.value || removalOrder?.removal_details?.installer_id || ""),
                installerName: String(selectedInstaller?.label || removalOrder?.removal_details?.installer_name || ""),
                lineItems: [],
                specialFees: fees.map((f) => mapInstallationSpecialFeeForResolve(f, allAvailableSpecialFees)),
            });

            const resolved = await postRemovalOrderResolve(token, {
                order_type: "removal",
                action,
                payload: resolvePayload,
            });

            return resolved && typeof resolved === "object" ? resolved : null;
        },
        [
            token,
            dynamicCostId,
            signageId,
            ordernumber,
            removalCost,
            dynamicTaxable,
            removalOrder,
            selectedSpecialFees,
            selectedInstaller,
            selectedBillingAddress,
            selectedOfficeBillingAddress,
            effectiveCouponCreatedById,
        ]
    );

    const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
    const pendingOverrideRef = React.useRef<RecomputeOverride | null>(null);

    const recompute = React.useCallback(
        async (override?: RecomputeOverride) => {
            if (!dynamicCostId || !token) return false;

            if (isRecomputingRef.current && !override?.force) {
                pendingRecomputeRef.current = true;
                return false;
            }

            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
                debounceRef.current = null;
                pendingOverrideRef.current = null;
            }

            const fees = override?.fees ?? getSelectedFees();
            const allFeesSubtotal = fees.reduce((sum: number, f: any) => sum + Number(f.amount || 0), 0);
            const discountCode =
                override?.discountCode !== undefined
                    ? String(override.discountCode).trim()
                    : String(
                          formikValuesRef.current?.discount_code ||
                              latestDiscountCodeRef.current ||
                              removalOrder?.discount_code ||
                              ""
                      ).trim();

            const addr = removalOrder?.removal_details?.removal_address?.address_details;
            const requestSignature = JSON.stringify({
                dynamicCostId,
                removalCost: Number(removalCost) || 0,
                dynamicTaxable,
                discountCode,
                feeSig: buildRemovalFeeSignature(fees),
                installer: selectedInstaller?.value || removalOrder?.removal_details?.installer_id || "",
                addressSig: addr
                    ? `${addr.addressLine1}-${addr.city}-${addr.state}-${addr.zip}`
                    : "",
            });

            if (!override?.force && lastRecomputeParamsRef.current === requestSignature) {
                return true;
            }

            recomputeRequestIdRef.current += 1;
            const currentRequestId = recomputeRequestIdRef.current;
            lastRecomputeParamsRef.current = requestSignature;

            try {
                isRecomputingRef.current = true;
                setIsRecomputing(true);

                if (currentRequestId !== recomputeRequestIdRef.current) return false;

                const resolved = await callWorkOrderResolveDraft(fees, discountCode, "draft");
                if (currentRequestId !== recomputeRequestIdRef.current) return false;

                if (!resolved || typeof resolved !== "object") {
                    const allFeesSubtotal = fees.reduce((sum: number, f: any) => sum + Number(f.amount || 0), 0);
                    formik.setFieldValue("totals.removal_subtotal", Number(removalCost) || 0, false);
                    formik.setFieldValue("totals.removal_tax_amount", 0, false);
                    formik.setFieldValue("totals.specialfee_subtotal", allFeesSubtotal, false);
                    formik.setFieldValue("totals.specialfee_tax_amount", 0, false);
                    formik.setFieldValue(
                        "totals.grand_total",
                        (Number(removalCost) || 0) + allFeesSubtotal,
                        false
                    );
                    formik.setFieldValue("specialfee", fees, false);
                    return false;
                }

                lastResolvedRemovalRef.current = resolved;
                isUpdatingFromRecomputeRef.current = true;
                const applied = applyRemovalWorkOrderResolveResponse(
                    resolved,
                    formik,
                    Number(removalCost) || 0,
                    {
                        requestedDiscountCode: discountCode,
                        existingFeeRows: fees,
                        specialFeeCatalog: allAvailableSpecialFees,
                    }
                );

                const mergedSig = buildRemovalFeeSignature(applied.mergedFees);
                prevFeesRef.current = mergedSig;
                setSelectedSpecialFees(applied.mergedFees);

                return true;
            } catch (e) {
                safeConsole.error("[OrderResolve] removal work order failed:", e);
                return false;
            } finally {
                isRecomputingRef.current = false;
                setIsRecomputing(false);
                if (pendingRecomputeRef.current) {
                    pendingRecomputeRef.current = false;
                    setTimeout(() => {
                        void recomputeRef.current?.({ force: true });
                    }, 0);
                }
            }
        },
        [
            token,
            dynamicCostId,
            removalCost,
            dynamicTaxable,
            removalOrder,
            selectedInstaller?.value,
            callWorkOrderResolveDraft,
        ]
    );

    const recomputeRef = React.useRef(recompute);
    recomputeRef.current = recompute;

    const waitForRecomputeIdle = React.useCallback((maxMs = 10000) => {
        return new Promise<void>((resolve) => {
            const started = Date.now();
            const tick = () => {
                if (!isRecomputingRef.current || Date.now() - started > maxMs) {
                    resolve();
                    return;
                }
                window.setTimeout(tick, 50);
            };
            tick();
        });
    }, []);

    const applyDiscountCode = React.useCallback(
        async (rawCode: string) => {
            const code = String(rawCode || "").trim();
            if (!code) return;
            if (!dynamicCostId) {
                showError("Removal pricing is still loading. Please wait and try again.");
                return;
            }

            markDiscountTouched();
            setCouponInput(code);
            latestDiscountCodeRef.current = code;
            formik.setFieldValue("discount_code", code, false);

            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
                debounceRef.current = null;
            }

            await waitForRecomputeIdle();

            const ok = await recomputeRef.current?.({ discountCode: code });
            const display = getResolvedDiscountDisplay(
                lastResolvedRemovalRef.current as Parameters<typeof getResolvedDiscountDisplay>[0],
                code
            );

            if (display.code && display.amount > 0) {
                setCouponInput(display.code);
                latestDiscountCodeRef.current = display.code;
                formik.setFieldValue("discount_code", display.code, false);
                formik.setFieldValue("discount_amount", display.amount, false);
                setZeroOrNegativeMsg("");
                return;
            }

            if (!ok) {
                showError("Discount could not be applied. Please try again.");
            } else {
                showError("This discount code could not be applied.");
            }
        },
        [dynamicCostId, formik, waitForRecomputeIdle, markDiscountTouched]
    );

    const clearDiscountCode = React.useCallback(async () => {
        markDiscountTouched();
        setCouponInput("");
        latestDiscountCodeRef.current = "";
        formik.setFieldValue("discount_code", "", false);
        formik.setFieldValue("discount_amount", 0, false);
        setZeroOrNegativeMsg("");

        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
            debounceRef.current = null;
        }

        await waitForRecomputeIdle();
        await recomputeRef.current?.({ discountCode: "" });
    }, [formik, waitForRecomputeIdle, markDiscountTouched]);

    const discountDisplay = React.useMemo(() => {
        const formikCode = discountUserTouched
            ? String(formik.values.discount_code || "").trim()
            : String(formik.values.discount_code || costBreakdownDiscount.code || "").trim();
        const formikAmount = discountUserTouched
            ? Number(formik.values.discount_amount || 0)
            : Number(formik.values.discount_amount || costBreakdownDiscount.amount || 0);

        return getOrderDiscountFieldDisplay({
            formikCode,
            formikAmount,
            couponInput,
            resolved: lastResolvedRemovalRef.current as Parameters<
                typeof getOrderDiscountFieldDisplay
            >[0]["resolved"],
        });
    }, [
        discountUserTouched,
        formik.values.discount_code,
        formik.values.discount_amount,
        costBreakdownDiscount.code,
        costBreakdownDiscount.amount,
        couponInput,
    ]);

    const editResolveReady = React.useMemo(
        () =>
            Boolean(
                isEdit &&
                (removalOrder?.signage_id || signageId) &&
                removalOrder?.removal_details?.removal_address?.address_details &&
                Number.isFinite(Number(removalCost)) &&
                dynamicCostId
            ),
        [
            isEdit,
            removalOrder?.signage_id,
            signageId,
            removalOrder?.removal_details?.removal_address?.address_details,
            removalCost,
            dynamicCostId,
        ]
    );

    React.useEffect(() => {
        didInitialEditResolveRef.current = false;
        lastRecomputeParamsRef.current = "";
        prevFeesRef.current = "";
    }, [removalOrder?._id, removalOrder?.id]);

    React.useEffect(() => {
        if (!isEdit || !editResolveReady || didInitialEditResolveRef.current) return;
        didInitialEditResolveRef.current = true;
        prevFeesRef.current = buildRemovalFeeSignature(selectedSpecialFees);
        void recomputeRef.current?.({ force: true });
    }, [isEdit, editResolveReady]);

    React.useEffect(() => {
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, []);

    // Merge TaxCloud fee rows with current UI: reattach IDs/taxable flags; include BOTH taxable and non-taxable fees (ref: SignageInstallationWorkOrder)
    const mergeFeesWithUi = (tcFees: any[], uiFees: any[]) => {
        const byName = new Map(uiFees.map((u: any) => [String(u.name || "").toLowerCase(), u]));
        const mergedTaxable = tcFees.map((f: any) => {
            const key = String(f.name || "").toLowerCase();
            const match = byName.get(key);
            return {
                id: String(match?.id || match?.specialfee_id || ""),
                name: f.name,
                amount: Number(f.amount || 0),
                taxable: true,
                tax_percentage: Number.isFinite(f.tax_percentage) ? Number(f.tax_percentage) : null,
                tax_amount: Number(f.tax_amount || 0),
            };
        });
        const tcNames = new Set(mergedTaxable.map((f: any) => String(f.name || "").toLowerCase()));
        const nonTaxable = uiFees.filter((u: any) => (u?.taxable ?? u?.specialfee_taxable) === false);
        for (const u of nonTaxable) {
            const key = String(u.name || "").toLowerCase();
            if (!tcNames.has(key)) {
                mergedTaxable.push({
                    id: String(u?.id ?? u?.specialfee_id ?? ""),
                    name: String(u?.name ?? u?.specialfee_name ?? "Special Fee"),
                    amount: Number(u?.amount ?? u?.specialfee_amount ?? 0),
                    taxable: false,
                    tax_percentage: 0,
                    tax_amount: 0,
                });
                tcNames.add(key);
            }
        }
        return mergedTaxable;
    };

    // ✅ Handle Get Live Tax via /orders/resolve (TaxCloud draft)
    const handleGetLiveTax = async (): Promise<string | undefined> => {
        try {
            if (!token || !userId || !id) {
                showError("Missing token/user/order for live tax");
                return undefined;
            }
            setLiveTaxLoading(true);

            const selectedFees = getSelectedFees();
            const resolved = await callWorkOrderResolveDraft(selectedFees, undefined, "draft");
            if (!resolved) {
                showError("Pricing could not be calculated. Please try again.");
                setLiveTaxLoading(false);
                return undefined;
            }

            lastResolvedRemovalRef.current = resolved;
            const applied = applyRemovalWorkOrderResolveResponse(
                resolved,
                formik,
                Number(removalCost) || 0,
                {
                    requestedDiscountCode: String(
                        formik.values.discount_code || removalOrder?.discount_code || ""
                    ).trim(),
                    existingFeeRows: selectedFees,
                    specialFeeCatalog: allAvailableSpecialFees,
                }
            );
            setSelectedSpecialFees(applied.mergedFees);

            const patchBody = buildRemovalPatchFromResolved(resolved, {
                version: removalOrder?.version,
                removalCost: Number(removalCost) || 0,
                taxStatus: "draft",
                existingFeeRows: selectedFees,
                specialFeeCatalog: allAvailableSpecialFees,
            });

            const patchRes = await fetch(
                `${process.env.REACT_APP_API_URL}/removal_orders/${id}`,
                {
                    method: "PATCH",
                    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
                    body: JSON.stringify(patchBody),
                }
            );

            if (!patchRes.ok) {
                const msg = await patchRes.text().catch(() => "");
                throw new Error(msg || "Failed to update removal order with live tax");
            }

            setRemovalOrder((prev: any) => ({
                ...prev,
                cart_id: resolved.cart_id || prev?.cart_id,
                removal_cost: patchBody.removal_cost,
                removal_tax_amount: patchBody.removal_tax_amount,
                removal_tax_percentage: patchBody.removal_tax_percentage,
                over_all_tax_percentage: patchBody.over_all_tax_percentage,
                over_all_tax_amount: patchBody.over_all_tax_amount,
                subtotal_amount: patchBody.subtotal_amount,
                total_amount: patchBody.total_amount,
                specialfee: patchBody.specialfee,
                tax_status: "draft",
            }));

            setRemovalCost(Number(patchBody.removal_cost || removalCost || 0));
            setTaxCartId(resolved.cart_id || finalCartId);
            setLiveTaxReady(true);
            showSuccess("Live tax saved to order. Click Next to finalize.");
            if (!skipLiveTaxNavigateRef.current) {
                safeNavigate(0);
            }
            return resolved.cart_id || finalCartId;
        } catch (e: any) {
            safeConsole.error("[GetLiveTax] failed", e);
            const rawMsg = String(e?.message || "Get Live Tax failed");
            // const normalizedMsg = /cartitems\s*\(non-empty array\)\s*is required/i.test(rawMsg)
            //     ? "Selected product is not taxable: Review product configuration"
            //     : rawMsg;
            // showError(normalizedMsg);
            setLiveTaxReady(false);
            return undefined;
        } finally {
            setLiveTaxLoading(false);
        }
    };

    // ✅ Handle Live Tax Next (finalize via resolve complete)
    const handleLiveTaxNext = async (): Promise<boolean> => {
        try {
            if (!token || !userId || !id) {
                showError("Missing token/user/order for live tax");
                return false;
            }

            const selectedFees = getSelectedFees();
            const resolved = await callWorkOrderResolveDraft(selectedFees, undefined, "complete");
            if (!resolved) {
                showError("Pricing could not be calculated. Please try again.");
                return false;
            }

            lastResolvedRemovalRef.current = resolved;
            showSuccess("TaxCloud order finalized.");

            const patchBody = {
                version: removalOrder?.version,
                tax_status: "complete",
            };

            const patchRes = await fetch(
                `${process.env.REACT_APP_API_URL}/removal_orders/${id}`,
                {
                    method: "PATCH",
                    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
                    body: JSON.stringify(patchBody),
                }
            );

            if (!patchRes.ok) {
                const msg = await patchRes.text().catch(() => "");
                throw new Error(msg || "Failed to update removal order with live tax");
            }

            setRemovalOrder((prev: any) => ({
                ...prev,
                tax_status: "complete",
            }));
            if (!completeAfterTaxReportRef.current) {
                safeNavigate(0);
            }
            return true;
        } catch (e: any) {
            safeConsole.error("[LiveTax Next] failed", e);
            showError(e?.message || "Finalize failed");
            return false;
        } finally {
            void fetchRemovalOrder();
        }
    };

    const onPreviewInvoice = (t: InvoiceType) => {
        safeNavigate(`${baseRoute}/InvoiceDocument`, {
            state: {
                type: t,
                order: removalOrder, // pass the full order object used for the invoice
                imageUrlMap: undefined, // optional thumbs
                // logoUrl, serviceInfo, resolveImageSrc, ... if needed
                autoPrint: false, // set true if you want it to auto-open the print dialog
            },
        });
    };

    const recalcInstallationTaxes = React.useCallback(
        async (forceType?: "manual" | "taxcloud", options?: { allowRefund?: boolean }) => {
            try {
                await recompute();
            } catch (err) {
                safeConsole.error("[INSTALL:TAX] recompute before refund failed", err);
            }

            if (!options?.allowRefund) return false;
            if (!token || !removalOrder?.order_number) return false;
            if (removalOrder?.tax_status !== "complete") return false;
            if (forceType !== "taxcloud") return false;

            try {
                const orderdata = (await GetVersionOrdernumber(
                    token,
                    removalOrder.order_number,
                    removalOrder?.order_type || "removal"
                )) || null;
                const order_number = (orderdata as any)?.full_number || (orderdata as any)?.order_number || "";
                setOrderNumber(order_number);
                if (!order_number) throw new Error("Missing order number for refund");
                const success = await requestInstallationTaxRefund(token, ordernumber || "");
                if (!success) throw new Error("TaxCloud refund failed");
                try {
                    await recompute();
                } catch (recomputeErr) {
                    safeConsole.error("[INSTALL:TAX] recompute after refund failed", recomputeErr);
                }
                return true;
            } catch (err) {
                safeConsole.error("[INSTALL:TAX] refund failed:", err);
                showError("Failed to request TaxCloud refund.");
                return false;
            }
        },
        [removalOrder?.order_number, removalOrder?.tax_status, recompute, token, ordernumber,]
    );

    React.useEffect(() => {
        installationTaxRef.current = { recalculate: recalcInstallationTaxes };
    }, [recalcInstallationTaxes]);

    useEffect(() => {
        if (!removalOrder?.order_number || !token) return;
        (async () => {
            const orderdata = (await GetVersionOrdernumber(
                token || "",
                removalOrder.order_number || "",
                removalOrder?.order_type || "removal"
            )) || null;
            const order_number = (orderdata as any)?.full_number || (orderdata as any)?.order_number || "";
            setOrderNumber(order_number);
        })();
    }, [removalOrder?.order_number, token]);

    if (!isLoaded) return <div>Loading...</div>;


    return (
        <div className="content container">
            <SendOrderEmailRecipientsModal
                show={emailRecipientsModal != null}
                title={emailRecipientsModal === "invoice" ? "Send Invoice" : "Send Quote"}
                token={token || ""}
                primaryEmailHint={
                    String(
                        removalOrder?.customer_email || removalOrder?.user_email || ""
                    ).trim() || undefined
                }
                confirming={
                    emailRecipientsModal === "invoice" ? sendInvoiceLoading : orderSummarySending
                }
                onCancel={() => setEmailRecipientsModal(null)}
                onConfirm={async (additionalEmails) => {
                    const mode = emailRecipientsModal;
                    try {
                        if (mode === "invoice") {
                            await handleSendInvoiceEmail(additionalEmails);
                        } else if (mode === "quote") {
                            await handleRemovalSendQuoteClick(additionalEmails);
                        }
                    } finally {
                        setEmailRecipientsModal(null);
                    }
                }}
            />
            {
                <style>{`
                .custom-btn {
                    font-size: 14px;
                    font-weight: 600;
                }
                .custom-btn:hover {
                    background-color: #155dfc;
                    color: #fff;
                    font-size: 14px;
                    font-weight: 600;
                }
                `}</style>
            }
            <div className="page-container ps-0">
                <h3 className="m-0 text-center fw-bold addheaders ps-1">
                    Removal Job #{removalOrder?.order_number} - {type}
                </h3>

            </div>

            <div className="mb-4">
                <div className="align-items-center col-lg-12 d-flex d-lg-block flex-wrap justify-content-end justify-content-lg-between os-card">
                    {/* Removal Status Tracker Card */}
                    <div className="os-card" style={{
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                        borderRadius: '12px',
                        padding: '10px',
                        backgroundColor: 'white'
                    }}>
                        {/* Mobile/Tablet: Actions at top */}
                        {isViewMode && (
                            <div className="dropdown p-2 d-flex d-lg-none justify-content-end">
                                <button
                                    className="btn btn-outline-secondary dropdown-toggle btn-sm br-8 table-cells custom-btn px-3"
                                    type="button"
                                    id="orderActionsDropdown"
                                    data-bs-toggle="dropdown"
                                    aria-expanded="false"
                                >
                                    Actions
                                </button>
                                <ul className="dropdown-menu dropdown-menu-end" aria-labelledby="orderActionsDropdown">
                                    {
                                        removalOrder?.status <= 400 && (
                                            <Link
                                                to={`${baseRoute}/jobs/removalWorkorder/${id}`}
                                                state={{ type: "Edit" }}
                                                className="dropdown-item text-primary table-cells"
                                            >
                                                <i className="ri-pencil-fill me-2"></i>Edit
                                            </Link>
                                        )
                                    }
                                    {(baseRoute === "/signage" || baseRoute === "/signageteammember") &&
                                        quoteModeEnabled &&
                                        Number(removalOrder?.status ?? 0) !== REMOVAL_ORDER_CANCELLED_STATUS_CODE && (
                                            <li>
                                                <button
                                                    type="button"
                                                    className="dropdown-item"
                                                    disabled={orderSummarySending}
                                                    onClick={() => setEmailRecipientsModal("quote")}
                                                >
                                                    <i className="bi bi-file-earmark-text me-2" />
                                                    {orderSummarySending ? "Sending…" : "Send Quote"}
                                                </button>
                                            </li>

                                        )}
                                    <li>
                                        <button
                                            type="button"
                                            className="dropdown-item"
                                            onClick={() => handleRemovalOrderSummaryPreviewClick()}
                                        >
                                            <i className="bi bi-file-earmark-text me-2" />
                                            View/Order Summary
                                        </button>
                                    </li>
                                    {/* <button
                                        type="button"
                                        className="dropdown-item"
                                        disabled={sendInvoiceLoading}
                                        onClick={() => void handleSendInvoiceEmail()}
                                    >
                                        <i className="bi bi-send me-2" />
                                        {sendInvoiceLoading ? "Sending…" : "Send Invoice"}
                                    </button> */}

                                    {(removalOrder?.status <= 400) && (
                                        <button className="dropdown-item text-danger table-cells" style={{paddingLeft:12}} onClick={() => handleRemovalStatus(800)}>
                                            <i className="mdi mdi-close-octagon-outline me-2"></i>Void
                                        </button>
                                    )}
                                    {(removalOrder?.status <= 300) && (
                                        <button className="dropdown-item text-danger table-cells" style={{paddingLeft:12}} onClick={() => handleRemovalStatus(700)}>
                                            <i className="mdi mdi-archive-remove-outline me-2"></i>Cancel Job
                                        </button>
                                    )}
                                </ul>
                            </div>
                        )}

                        {/* Desktop: Actions on right side */}
                        <div className="d-none d-lg-flex align-items-start gap-3">
                            {/* Left: Removal Status Tracker */}
                            <div className="flex-grow-1">
                                <InstallationStatusTracker
                                    type="removal"
                                    status={removalOrder?.status}
                                    paymentStatus={removalOrder?.payment_status}
                                    quoteModeEnabled={quoteModeEnabled}
                                    ownerNodeId={signageId}
                                    className="mb-0 w-100"
                                />
                            </div>

                            {/* Right: Actions Dropdown */}
                            {isViewMode && (
                                <div className="dropdown p-4">
                                    <button
                                        className="btn btn-outline-secondary dropdown-toggle btn-sm br-8 table-cells custom-btn px-3"
                                        type="button"
                                        id="orderActionsDropdown"
                                        data-bs-toggle="dropdown"
                                        aria-expanded="false"
                                    >
                                        Actions
                                    </button>
                                    <ul className="dropdown-menu dropdown-menu-end" aria-labelledby="orderActionsDropdown">
                                        {
                                            removalOrder?.status < 400 && (
                                                <Link
                                                    to={`${baseRoute}/jobs/removalWorkorder/${id}`}
                                                    state={{ type: "Edit" }}
                                                    className="dropdown-item text-primary table-cells"
                                                >
                                                    <i className="ri-pencil-fill me-2"></i>Edit
                                                </Link>
                                            )
                                        }
                                        {(baseRoute === "/signage" || baseRoute === "/signageteammember") &&
                                            quoteModeEnabled &&
                                            Number(removalOrder?.status ?? 0) !== REMOVAL_ORDER_CANCELLED_STATUS_CODE && (
                                                <li>
                                                    <button
                                                        type="button"
                                                        className="dropdown-item"
                                                        disabled={orderSummarySending}
                                                        onClick={() => setEmailRecipientsModal("quote")}
                                                    >
                                                        <i className="bi bi-file-earmark-text me-2" />
                                                        {orderSummarySending ? "Sending…" : "Send Quote"}
                                                    </button>
                                                </li>
                                            )}
                                        <li>
                                            <button
                                                type="button"
                                                className="dropdown-item"
                                                onClick={() => handleRemovalOrderSummaryPreviewClick()}
                                            >
                                                <i className="bi bi-file-earmark-text me-2" />
                                                View/Order Summary
                                            </button>
                                        </li>
                                        {!!removalOrder?.invoice_date && (
                                        <li>
                                            <button className="dropdown-item" onClick={() => onPreviewInvoice("removal")}>
                                                <i className="bi bi-eye me-2" /> View/Download Invoice
                                            </button>
                                        </li>
                                        )}
                                        {!!removalOrder?.invoice_date &&
                                            Number(removalOrder?.status ?? 0) !== REMOVAL_ORDER_CANCELLED_STATUS_CODE && (
                                            <button
                                                type="button"
                                                className="dropdown-item"
                                                disabled={sendInvoiceLoading}
                                                onClick={() => setEmailRecipientsModal("invoice")}
                                            >
                                                <i className="bi bi-send me-2" />
                                                {sendInvoiceLoading ? "Sending…" : "Send Invoice"}
                                            </button>
                                        )}
                                        
                                        {/* 
                                        <button type="button" onClick={handleDownloadInvoice} className="dropdown-item text-primary table-cells">
                                            <i className="ri-file-download-line me-2"></i>Download Invoice
                                        </button> */}

                                        {(removalOrder?.status <= 400) && (
                                            <button className="dropdown-item text-danger table-cells" style={{ paddingLeft: 12 }} onClick={() => handleRemovalStatus(800)}>
                                                <i className="mdi mdi-close-octagon-outline me-2"></i>Void
                                            </button>
                                        )}
                                        {(removalOrder?.status <= 300) && (
                                            <button className="dropdown-item text-danger table-cells" style={{ paddingLeft: 12 }} onClick={() => handleRemovalStatus(700)}>
                                                <i className="mdi mdi-archive-remove-outline me-2"></i>Cancel Job
                                            </button>
                                        )}
                                    </ul>
                                </div>
                            )}
                        </div>

                        {/* Mobile/Tablet: Removal Status Tracker */}
                        <div className="d-block d-lg-none">
                            <InstallationStatusTracker
                                type="removal"
                                status={removalOrder?.status}
                                paymentStatus={removalOrder?.payment_status}
                                quoteModeEnabled={quoteModeEnabled}
                                ownerNodeId={signageId}
                                className="mb-0 w-100"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div >
                <div >
                    <form onSubmit={formik.handleSubmit} className="me-2 me-lg-0">
                        <div className="row col-lg-12 card-body shadow-lg pb-4 mb-4 ms-0 p-3">
                            <div className="mb-3 d-flex align-items-center justify-content-between gap-3">
                                <SectionHeader title="Customer Information" icon="ri-user-3-line" color="#155dfc" size="md" divider />
                                {isViewMode ? (
                                    <RefreshIconButton
                                        ariaLabel="Refresh customer information"
                                        title="Refresh customer information"
                                        onClick={async () => {
                                            const documentId = String(removalOrder?._id || removalOrder?.id || "").trim();
                                            const customerId = String(removalOrder?.customer_id || "").trim();
                                            if (token && documentId && customerId) {
                                                try {
                                                    await fetch(`${process.env.REACT_APP_API_URL}/sync-user-email-to-document`, {
                                                        method: "POST",
                                                        headers: {
                                                            Authorization: `Bearer ${token}`,
                                                            "Content-Type": "application/json",
                                                        },
                                                        body: JSON.stringify({
                                                            user_id: customerId,
                                                            id: documentId,
                                                            collection_name: "removal",
                                                        }),
                                                    });
                                                } catch (syncErr) {
                                                    safeConsole.warn("sync-user-email-to-document failed:", syncErr);
                                                }
                                            }
                                            await fetchRemovalOrder();
                                        }}
                                    />
                                ) : null}
                            </div>
                            <div className="col-lg-3 mb-3">
                                <label className="form-label table-cells">
                                    Removal Job
                                </label>
                                <input
                                    type="text"
                                    id="order_number"
                                    name="order_number"
                                    className="form-control table-cells"
                                    value={removalOrder?.order_number}
                                    disabled
                                />
                            </div>
                            <div className="col-lg-3 mb-3">
                                <label className="form-label table-cells">
                                    Linked Installation Job
                                </label>
                                <input
                                    type="text"
                                    id="installation_order_number"
                                    name="installation_order_number"
                                    className="form-control table-cells"
                                    value={removalOrder?.installation_order_number || ''}
                                    disabled
                                />
                            </div>
                            <div className="col-lg-3 mb-3">
                                <label htmlFor="userType" className="form-label table-cells">
                                    Buyer Type
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    id="userType"
                                    name="userType"
                                    value={formik.values.userType}
                                    disabled
                                    readOnly
                                />
                            </div>

                            {/* Brokerage Firm Name - Don't show for standalone agent orders */}
                            {removalOrder?.brokerage_firm_name && (
                                <div className="col-lg-3 mb-3">
                                    <label className="table-cells">
                                        Brokerage Firm
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={removalOrder?.brokerage_firm_name || ""}
                                        disabled
                                        readOnly
                                    />
                                </div>
                            )}

                            {/* Team Member Name */}
                            {removalOrder?.team_member_name && (
                                <div className="col-lg-3 mb-3">
                                    <label className="table-cells">
                                        Team Member
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={removalOrder?.team_member_name || ""}
                                        disabled
                                        readOnly
                                    />
                                </div>
                            )}

                            {/* Office Name */}
                            {removalOrder?.office?.officeName && (
                                <div className="col-lg-3 mb-3">
                                    <label className="table-cells">
                                        Office
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={removalOrder?.office?.officeName || ""}
                                        disabled
                                        readOnly
                                    />
                                </div>
                            )}

                            {/* Agent Name */}
                            {removalOrder?.agent_name && (
                                <div className="col-lg-3 mb-3">
                                    <label className="table-cells">
                                        Agent
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={removalOrder?.agent_name || ""}
                                        disabled
                                        readOnly
                                    />
                                </div>
                            )}
                            <div className="col-lg-3 mb-3">
                                <label htmlFor="userEmail" className="form-label table-headers">Email <span className="text-danger">*</span></label>
                                <input
                                    type="email"
                                    className={`form-control table-cells ${(formik.submitCount > 0 || formik.touched.userEmail) && formik.errors.userEmail ? "is-invalid" : ""}`}
                                    id="userEmail"
                                    name="userEmail"
                                    placeholder="Enter email"
                                    value={formik.values.userEmail}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    readOnly={Viewmode || !isFieldEditable(removalOrder?.status ?? 0, "userEmail")}
                                    disabled={Viewmode}
                                />
                                {(formik.submitCount > 0 || formik.touched.userEmail) &&
                                    typeof formik.errors.userEmail === "string" && (
                                        <div className="text-danger mt-1">{formik.errors.userEmail}</div>
                                    )}
                            </div>

                            <div className="col-lg-3 mb-3">
                                <label htmlFor="userPhone" className="form-label  table-headers">
                                    Phone <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="text"
                                    className={`form-control table-cells ${(formik.submitCount > 0 || formik.touched.userPhone) && formik.errors.userPhone ? "is-invalid" : ""}`}
                                    id="userPhone"
                                    name="userPhone"
                                    placeholder="Enter phone"
                                    value={formik.values.userPhone}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    readOnly={Viewmode || !isFieldEditable(removalOrder?.status ?? 0, "userPhone")}
                                    disabled={Viewmode}
                                />
                                {(formik.submitCount > 0 || formik.touched.userPhone) &&
                                    typeof formik.errors.userPhone === "string" && (
                                        <div className="text-danger mt-1">{formik.errors.userPhone}</div>
                                    )}
                            </div>
                        </div>
                        <div className="d-block d-lg-flex gap-3">
                            {status !== 500 && status !== 600 && (
                                <>
                                    <div className="col-lg-8 ">
                                        <div className="row col-lg-12 card-body shadow-lg mb-4 ms-0 p-3">
                                            <div className="mb-3">
                                                <SectionHeader title="Schedule & Assignment" icon="ri-calendar-line" color="#00a63e" size="md" divider />
                                            </div>
                                            <div className="col-lg-4 mb-3">
                                                <label className="form-label table-cells">
                                                    Removal Date
                                                </label>
                                                <input
                                                    type="date"
                                                    className="form-control"
                                                    id="scheduled_date"
                                                    name="scheduled_date"
                                                    placeholder="mm/dd/yyyy"
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                    onFocus={() => {
                                                        if (!isFieldEditable(removalOrder?.status ?? 0, "ScheduleDate")) return;
                                                        if (toScheduledDateInput(formik.values.scheduled_date)) return;
                                                        formik.setFieldValue("scheduled_date", new Date().toISOString().split("T")[0]);
                                                    }}
                                                    value={toScheduledDateInput(formik.values.scheduled_date)}
                                                    readOnly={!isFieldEditable(removalOrder?.status ?? 0, "ScheduleDate")}
                                                />
                                                {formik.touched.scheduled_date && typeof formik.errors.scheduled_date === "string" && (
                                                    <div className="text-danger mt-1">{formik.errors.scheduled_date}</div>
                                                )}
                                            </div>
                                            {/* Assigned To - Only show when parentSignageId is loaded for team members */}
                                            {(!isSignageTeammember || parentSignageId) ? (
                                                <div className="col-lg-4 mb-3">
                                                    <label className="form-label table-cells">
                                                        Assigned To <span className="text-danger">*</span>
                                                    </label>
                                                    <AsyncPaginate
                                                        id="installeId"
                                                        name="installeId"
                                                        classNamePrefix="rs"
                                                        placeholder="Search Assigned To..."
                                                        value={selectedSignageTeamMemberOption ? {
                                                            value: selectedSignageTeamMemberOption.value,
                                                            label: selectedSignageTeamMemberOption.label,
                                                            firstName: selectedSignageTeamMemberOption.firstName,
                                                            lastName: selectedSignageTeamMemberOption.lastName,
                                                            email: selectedSignageTeamMemberOption.email,
                                                            phone: selectedSignageTeamMemberOption.phone
                                                        } : null}
                                                        loadOptions={loadSignageTeamMemberOptions}
                                                        onChange={(selected) => {
                                                            if (selected) {
                                                                formik.setFieldValue("installeId", selected.value || "");
                                                                setSelectedSignageTeamMemberOption(selected);
                                                                setSelectedInstaller({ // For compatibility with existing code
                                                                    value: selected.value,
                                                                    label: selected.label,
                                                                    firstName: selected.firstName,
                                                                    lastName: selected.lastName,
                                                                    email: selected.email,
                                                                    phone: selected.phone
                                                                });
                                                            } else {
                                                                formik.setFieldValue("installeId", "");
                                                                setSelectedSignageTeamMemberOption(null);
                                                                setSelectedInstaller(null);
                                                            }
                                                        }}
                                                        onBlur={() => formik.setFieldTouched("installeId", true)}
                                                        additional={{ page: 1 }}
                                                        defaultOptions
                                                        debounceTimeout={300}
                                                        isClearable
                                                        isDisabled={!isFieldEditable(removalOrder?.status ?? 0, "Installer")}
                                                        components={{ DropdownIndicator }}
                                                        styles={customSelectStyles}
                                                    />
                                                    {formik.touched.installeId && typeof formik.errors.installeId === "string" && (
                                                        <div className="text-danger mt-1">{formik.errors.installeId}</div>
                                                    )}
                                                </div>
                                            ) : (
                                                <div className="col-lg-4 mb-3">
                                                    <label className="form-label table-cells">
                                                        Assigned To <span className="text-danger">*</span>
                                                    </label>
                                                    <div className="d-flex align-items-center justify-content-center" style={{ minHeight: "38px" }}>
                                                        <div className="spinner-border spinner-border-sm text-primary me-2" role="status">
                                                            <span className="visually-hidden">Loading...</span>
                                                        </div>
                                                        <span className="text-muted">Loading team members...</span>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Jobs Today */}
                                            <div className="col-lg-4 mb-3">
                                                <label className="form-label table-headers">Jobs Today</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    id="scheduled_jobs"
                                                    name="scheduled_jobs"
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                    value={formik.values.scheduled_jobs}
                                                    disabled
                                                />
                                                {formik.touched.scheduled_jobs &&
                                                    formik.errors.scheduled_jobs && (
                                                        <div className="invalid-feedback">
                                                            {formik.errors.scheduled_jobs}
                                                        </div>
                                                    )}
                                            </div>

                                            {/* ✅ Removal Priority */}
                                            <div className="col-lg-4 mb-3">
                                                <label className="form-label table-cells">
                                                    Removal Priority <span className="text-danger">*</span>
                                                </label>
                                                <Select
                                                    id="priority"
                                                    name="priority"
                                                    options={priorityOptions}
                                                    value={priorityOptions.find((option) => option.value === formik.values.priority) || null}
                                                    onChange={(option) =>
                                                        formik.setFieldValue("priority", option?.value || "")
                                                    }
                                                    onBlur={formik.handleBlur}
                                                    components={{ DropdownIndicator }}
                                                    styles={customSelectStyles}
                                                    classNamePrefix="rs"
                                                    isDisabled={!isFieldEditable(removalOrder?.status ?? 0, "Priority")}
                                                />
                                                {formik.touched.priority && typeof formik.errors.priority === "string" && (
                                                    <div className="text-danger mt-1">{formik.errors.priority}</div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="row col-lg-12 mb-4 card-body shadow-lg mb-4 ms-0 pb-3 p-3">
                                            <div className="align-items-center d-block d-lg-flex justify-content-between mb-3">
                                                {/* Left side: Address Information with purple icon */}
                                                <SectionHeader title="Address Information" icon="ri-map-pin-line" color="#e7000b" size="md" divider className="mb-3 mb-lg-0" />

                                                {/* Right side: Gate Code with functionality */}
                                                <div className="d-flex align-items-center gap-3">
                                                    <label className="table-cells mb-0">Gate Code</label>
                                                    <div className="d-flex align-items-center gap-2">
                                                        <input
                                                            type="text"
                                                            id="gatecode"
                                                            name="gatecode"
                                                            className="form-control"
                                                            style={{
                                                                width: '140px',
                                                                backgroundColor: formik.values.gateCodeEnabled ? '#ffffff' : '#f8f9fa',
                                                                color: formik.values.gateCodeEnabled ? '#495057' : '#6c757d',
                                                                cursor: formik.values.gateCodeEnabled ? 'text' : 'pointer'
                                                            }}
                                                            value={formik.values.gatecode}
                                                            onChange={formik.handleChange}
                                                            onBlur={formik.handleBlur}
                                                            placeholder="Enter Gate Code"
                                                            readOnly={!formik.values.gateCodeEnabled}
                                                            onClick={() => {
                                                                if (!formik.values.gateCodeEnabled) {
                                                                    formik.setFieldValue("gateCodeEnabled", true);
                                                                }
                                                            }}
                                                        />
                                                        <button
                                                            type="button"
                                                            className="btn btn-primary table-addbutton br-8 btn-sm"
                                                            disabled={!formik.values.gateCodeEnabled}
                                                            onClick={async () => {
                                                                // Save gate code and disable the field again
                                                                await handleSaveGateCode();
                                                                formik.setFieldValue("gateCodeEnabled", false);
                                                            }}
                                                        >
                                                            <i className="ri-save-fill fs-16"></i>
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-lg-12">
                                                {/* <div className="align-items-baseline col-lg-12 d-flex justify-content-between">
                                                    <div className="my-2 fw-bold">
                                                        <input
                                                            type="checkbox"
                                                            id="isSameAsBilling"
                                                            name="isSameAsBilling"
                                                            onChange={formik.handleChange}
                                                            onBlur={formik.handleBlur}
                                                            checked={formik.values.isSameAsBilling}
                                                        />{" "}
                                                        Billing and Installation Address are Same

                                                    </div>
                                                    <button className="btn btn-primary btn-sm mb-3 removal-installation" type="button">+ Address</button>
                                                </div> */}

                                                <div className="row">
                                                    <div className="col-lg-6 mb-3">
                                                        <label
                                                            htmlFor="billingAddressId"
                                                            className="form-label table-cells"
                                                        >
                                                            Billing Address <span className="text-danger">*</span>
                                                        </label>
                                                        <Select
                                                            // className="form-control"
                                                            id="billingAddress"
                                                            name="billingAddress"
                                                            options={addressOptions}
                                                            value={
                                                                addressOptions?.find(
                                                                    (option) =>
                                                                        option.value === formik.values.billingAddressId
                                                                ) || null
                                                            }
                                                            onChange={(selected: any) => {
                                                                // console.log("Selected Billing Address:", selected); // Debugging log
                                                                formik.setFieldValue("billingAddressId", selected?.value);
                                                                const selectedAddress = addressObjects?.find((addr) => addr._id === selected.value);
                                                                if (formik.values.officeId) {
                                                                    setSelectedOfficeBillingAddress(selectedAddress || null);
                                                                }
                                                                else {
                                                                    setSelectedBillingAddress(selectedAddress || null);
                                                                }
                                                                // Changing billing means it's no longer the same as removal address
                                                                formik.setFieldValue("isSameAsBilling", false);
                                                            }}
                                                            isDisabled={!isEdit || !isFieldEditable(removalOrder?.status ?? 0, "BillingAddress")}
                                                            components={{ DropdownIndicator }}
                                                            styles={customSelectStyles}
                                                            classNamePrefix="rs"
                                                        />
                                                        {formik.touched.billingAddressId && typeof formik.errors.billingAddressId === "string" && (
                                                            <div className="text-danger mt-1">{formik.errors.billingAddressId}</div>
                                                        )}
                                                    </div>
                                                    {/* {formik.values.isSameAsBilling ? null : ( */}
                                                    <div className="col-lg-6 mb-3">
                                                        <label
                                                            htmlFor="removalAddressId"
                                                            className="form-label table-cells"
                                                        >
                                                            Removal Address <span className="text-danger">*</span>
                                                        </label>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            value={fullAddress || ""}
                                                            disabled
                                                        />
                                                    </div>
                                                    {/* )} */}
                                                </div>
                                            </div>
                                            <div className="col-lg-12">
                                                <div style={mapContainerStyle}>
                                                    <GoogleMap
                                                        mapContainerStyle={mapContainerStyle}
                                                        zoom={10}
                                                        center={selectedLocation || center}
                                                    >
                                                        {selectedLocation && <Marker position={selectedLocation} />}
                                                    </GoogleMap>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="row col-lg-12 mb-5 card-body shadow-lg mb-3 ms-0 p-3">
                                            <div className="d-flex mb-3">
                                                <span
                                                    aria-hidden="true"
                                                    className="image-icons"
                                                    style={{
                                                        backgroundColor: "#9810fa",
                                                        WebkitMaskImage: `url(${ProductSelection})`,
                                                        maskImage: `url(${ProductSelection})`,
                                                    }}
                                                />
                                                <SectionHeader title="Product Selection" icon="" variant="success" size="md" divider />
                                            </div>
                                            {/* Desktop Table View */}
                                            <div className="col-lg-12 d-none d-lg-block">
                                                <table className="border table table-striped">
                                                    <thead className="">
                                                        <tr>
                                                            <th className="text-center table-headers" style={{ width: "15%" }}>Product Name</th>
                                                            <th className="text-center table-headers" style={{ width: "10%" }}>SKU</th>
                                                            {
                                                                isEdit &&
                                                                <th className="text-center table-headers" style={{ width: "12%" }}>installed Quantity </th>
                                                            }
                                                            <th className="text-center table-headers" style={{ width: "10%" }}>Qty to remove </th>
                                                            <th className="text-center table-headers" style={{ width: "17%" }}>Product Location Status</th>
                                                            {/* <th className="text-center">Action</th> */}
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {(() => {
                                                            const regularItems = updatedProducts?.filter((item: any) => item.inventory_source !== "rotational" && item.inventory_source !== "custom") ?? [];
                                                            const customItems = updatedProducts?.filter((item: any) => item.inventory_source === "custom") ?? [];
                                                            return (
                                                                <>
                                                                    {regularItems.map((item: any, index: number) => (
                                                                        <tr key={`reg-${item.product_id}-${index}`}>
                                                                            <td className="text-center align-middle table-cells text-ellipsis">{item.product_name}</td>
                                                                            <td className="text-center align-middle table-cells text-ellipsis">{displayRemovalWorkOrderSku(item)}</td>
                                                                            {isEdit && (
                                                                                <td className="text-center table-cells">
                                                                                    {(() => {
                                                                                        const base = Number(item.available_quantity) || 0;
                                                                                        const orig = Number(item.quantity_to_be_removed) || 0;
                                                                                        return base + orig; // <-- show pool
                                                                                    })()}
                                                                                </td>
                                                                            )}

                                                                            {/* Qty to remove (editable, clamped 1..pool) */}
                                                                            <td className="text-center table-cells">
                                                                                {(() => {
                                                                                    const pool =
                                                                                        (Number(item.available_quantity) || 0) +
                                                                                        (Number(item.quantity_to_be_removed) || 0);

                                                                                    return (
                                                                                        <input
                                                                                            type="number"
                                                                                            className="form-control text-center table-cells"
                                                                                            value={removalQuantities[item.product_id] ?? (item.quantity_to_be_removed || '')}
                                                                                            disabled={!isEdit || !isFieldEditable(removalOrder?.status ?? 0, "removalQuantity")}
                                                                                            min={0}
                                                                                            max={pool}
                                                                                            placeholder="0"
                                                                                            onChange={(e) => {
                                                                                                const rawValue = e.target.value;
                                                                                                const qty = rawValue === '' ? '' : Number(rawValue);
                                                                                                setRemovalQuantities((prev) => ({
                                                                                                    ...prev,
                                                                                                    [item.product_id]: qty as number,
                                                                                                }));
                                                                                            }}
                                                                                            onBlur={(e) => {
                                                                                                const currentValue = e.target.value;
                                                                                                if (currentValue === '' || Number(currentValue) <= 0) {
                                                                                                    setRemovalQuantities((prev) => ({
                                                                                                        ...prev,
                                                                                                        [item.product_id]: 1,
                                                                                                    }));
                                                                                                }
                                                                                            }}
                                                                                            onWheel={(e) => e.currentTarget.blur()}
                                                                                        />
                                                                                    );
                                                                                })()}
                                                                            </td>
                                                                            <td className="text-center align-middle table-cells">
                                                                                <span className="">
                                                                                    <StatusBadge status={item.current_status?.replace(/_/g, " ") || "-"} />
                                                                                </span>
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                                    {customItems.length > 0 && (
                                                                        <>
                                                                            <tr>
                                                                                <td colSpan={isEdit ? 5 : 4} className="small fw-semibold py-2" style={{ backgroundColor: "#f8f9fa" }}>
                                                                                    Custom Products
                                                                                </td>
                                                                            </tr>
                                                                            {customItems.map((item: any, index: number) => (
                                                                                <tr key={`custom-${item.product_id}-${index}`}>
                                                                                    <td className="text-center align-middle table-cells text-ellipsis">{item.product_name}</td>
                                                                                     <td className="text-center align-middle table-cells text-ellipsis">{displayRemovalWorkOrderSku(item)}</td>
                                                                                    {isEdit && (
                                                                                        <td className="text-center table-cells">
                                                                                            {(() => {
                                                                                                const base = Number(item.available_quantity) || 0;
                                                                                                const orig = Number(item.quantity_to_be_removed) || 0;
                                                                                                return base + orig;
                                                                                            })()}
                                                                                        </td>
                                                                                    )}
                                                                                    <td className="text-center table-cells">
                                                                                        <input
                                                                                            type="number"
                                                                                            className="form-control text-center table-cells"
                                                                                            value={item.quantity_to_be_removed ?? ''}
                                                                                            placeholder="—"
                                                                                            disabled
                                                                                            readOnly
                                                                                            style={{ backgroundColor: "#e9ecef", cursor: "not-allowed" }}
                                                                                        />
                                                                                    </td>
                                                                                    <td className="text-center align-middle table-cells">
                                                                                        <StatusBadge status={item.current_status?.replace(/_/g, " ") || "-"} />
                                                                                    </td>
                                                                                </tr>
                                                                            ))}
                                                                        </>
                                                                    )}
                                                                </>
                                                            );
                                                        })()}
                                                    </tbody>
                                                </table>
                                            </div>

                                            {/* Mobile/Tablet Card View for Normal Products */}
                                            <div className="col-lg-12 d-lg-none">
                                                <div className="row g-3">
                                                    {(() => {
                                                        const regularItems = updatedProducts?.filter((item: any) => item.inventory_source !== "rotational" && item.inventory_source !== "custom") ?? [];
                                                        const customItems = updatedProducts?.filter((item: any) => item.inventory_source === "custom") ?? [];
                                                        return (
                                                            <>
                                                                {regularItems.map((item: any, index: number) => {
                                                                    const pool = (Number(item.available_quantity) || 0) + (Number(item.quantity_to_be_removed) || 0);
                                                                    return (
                                                                        <div key={`reg-${item.product_id}-${index}`} className="col-12 mb-3">
                                                                            <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '8px' }}>
                                                                                <div className="card-body p-3">
                                                                                    <div className="mb-3">
                                                                                        <h6>Product Name</h6>
                                                                                        <h6 className="card-title mb-0 fw-medium">{item.product_name}</h6>
                                                                                    </div>
                                                                                    <div className="row g-2 mb-3">
                                                                                        {isEdit && (
                                                                                            <div className="col-6">
                                                                                                <label className="form-label small mb-1">Installed Qty</label>
                                                                                                <input
                                                                                                    type="number"
                                                                                                    className="form-control form-control-sm text-center"
                                                                                                    value={(() => {
                                                                                                        const base = Number(item.available_quantity) || 0;
                                                                                                        const orig = Number(item.quantity_to_be_removed) || 0;
                                                                                                        return base + orig;
                                                                                                    })()}
                                                                                                    readOnly
                                                                                                    style={{ backgroundColor: '#f8f9fa' }}
                                                                                                />
                                                                                            </div>
                                                                                        )}
                                                                                        <div className={isEdit ? "col-6" : "col-12"}>
                                                                                            <label className="form-label small mb-1">Qty to Remove</label>
                                                                                            <input
                                                                                                type="number"
                                                                                                className="form-control form-control-sm text-center"
                                                                                                value={removalQuantities[item.product_id] ?? (item.quantity_to_be_removed || '')}
                                                                                                placeholder="0"
                                                                                                min={0}
                                                                                                max={pool}
                                                                                                disabled={!isEdit || !isFieldEditable(removalOrder?.status ?? 0, "removalQuantity")}
                                                                                                onChange={(e) => {
                                                                                                    const rawValue = e.target.value;
                                                                                                    const qty = rawValue === '' ? '' : Number(rawValue);
                                                                                                    setRemovalQuantities((prev) => ({ ...prev, [item.product_id]: qty as number }));
                                                                                                }}
                                                                                                onBlur={(e) => {
                                                                                                    const currentValue = e.target.value;
                                                                                                    if (currentValue === '' || Number(currentValue) <= 0) {
                                                                                                        setRemovalQuantities((prev) => ({ ...prev, [item.product_id]: 1 }));
                                                                                                    }
                                                                                                }}
                                                                                                onWheel={(e) => e.currentTarget.blur()}
                                                                                            />
                                                                                        </div>
                                                                                    </div>
                                                                                    <div className="row g-2">
                                                                                        <div className="col-6">
                                                                                            <h6>SKU</h6>
                                                                                            <span className="fw-medium text-ellipsis">{displayRemovalWorkOrderSku(item)}</span>
                                                                                        </div>
                                                                                        <div className="col-6">
                                                                                            <h6>Product Location Status</h6>
                                                                                            <StatusBadge status={item.current_status?.replace(/_/g, " ") || "-"} />
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })}
                                                                {customItems.length > 0 && (
                                                                    <>
                                                                        <div className="col-12">
                                                                            <h6 className="small fw-semibold mb-2">Custom Products</h6>
                                                                        </div>
                                                                        {customItems.map((item: any, index: number) => (
                                                                            <div key={`custom-${item.product_id}-${index}`} className="col-12 mb-3">
                                                                                <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '8px' }}>
                                                                                    <div className="card-body p-3">
                                                                                        <div className="mb-3">
                                                                                            <h6>Product Name</h6>
                                                                                            <h6 className="card-title mb-0 fw-medium">{item.product_name}</h6>
                                                                                        </div>
                                                                                        <div className="row g-2 mb-3">
                                                                                            {isEdit && (
                                                                                                <div className="col-6">
                                                                                                    <label className="form-label small mb-1">Installed Qty</label>
                                                                                                    <input
                                                                                                        type="number"
                                                                                                        className="form-control form-control-sm text-center"
                                                                                                        value={(() => {
                                                                                                            const base = Number(item.available_quantity) || 0;
                                                                                                            const orig = Number(item.quantity_to_be_removed) || 0;
                                                                                                            return base + orig;
                                                                                                        })()}
                                                                                                        readOnly
                                                                                                        style={{ backgroundColor: '#f8f9fa' }}
                                                                                                    />
                                                                                                </div>
                                                                                            )}
                                                                                            <div className={isEdit ? "col-6" : "col-12"}>
                                                                                                <label className="form-label small mb-1">Qty to Remove</label>
                                                                                                <input
                                                                                                    type="number"
                                                                                                    className="form-control form-control-sm text-center"
                                                                                                    value={item.quantity_to_be_removed ?? ''}
                                                                                                    placeholder="—"
                                                                                                    disabled
                                                                                                    readOnly
                                                                                                    style={{ backgroundColor: '#e9ecef', cursor: 'not-allowed' }}
                                                                                                />
                                                                                            </div>
                                                                                        </div>
                                                                                        <div className="row g-2">
                                                                                            <div className="col-6">
                                                                                                <h6>SKU</h6>
                                                                                                <span className="fw-medium text-ellipsis">{displayRemovalWorkOrderSku(item)}</span>
                                                                                            </div>
                                                                                            <div className="col-6">
                                                                                                <h6>Product Location Status</h6>
                                                                                                <StatusBadge status={item.current_status?.replace(/_/g, " ") || "-"} />
                                                                                            </div>
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        ))}
                                                                    </>
                                                                )}
                                                            </>
                                                        );
                                                    })()}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="row col-lg-12 mb-3">
                                            <div className="col-lg-6 mb-3 mb-lg-0 pe-0">
                                                <Instructions
                                                    title="Instructions"
                                                    mode="instructions"
                                                    loginType="signage"
                                                    instructions={instructions}
                                                    placeholder="Add instructions here"
                                                    rows={4}
                                                    onSave={handleSaveInstructionsOnly}
                                                    className="mb-3"
                                                    iconColor="#f54a00"
                                                    iconBackground="#EAF7F1"
                                                />
                                            </div>

                                            <div className="align-items-center card-body col-lg-5 justify-content-end ms-0 ms-2 ms-lg-0 ms-lg-5 offset-1 p-4 shadow-lg">
                                                <div className="align-items-center d-flex mb-3">
                                                    <span
                                                        aria-hidden="true"
                                                        className="image-icons"
                                                        style={{
                                                            backgroundColor: "#00a63e",
                                                            WebkitMaskImage: `url(${Doller})`,
                                                            maskImage: `url(${Doller})`,
                                                        }}
                                                    />
                                                    <SectionHeader title="Cost Breakdown" icon="" color="" size="lg" divider />
                                                </div>

                                                {isEdit && (Number(removalOrder?.status ?? 0) <= 200) && (
                                                    <OrderDiscountCodeField
                                                        className="mb-2"
                                                        value={couponInput}
                                                        appliedCode={discountDisplay.code}
                                                        discountAmount={discountDisplay.amount}
                                                        disabled={isRecomputing}
                                                        onChange={setCouponInput}
                                                        onApply={(code) => {
                                                            void applyDiscountCode(code);
                                                        }}
                                                        onClear={() => {
                                                            void clearDiscountCode();
                                                        }}
                                                    />
                                                )}
                                                {zeroOrNegativeMsg && <div className="text-danger mt-2">{zeroOrNegativeMsg}</div>}

                                                <div className="d-flex justify-content-between align-items-center py-2">
                                                    <span className="fw-medium">Removal Cost:</span>
                                                    <span className="fw-semibold">${Number(removalCost || formik.values.installation_charge || 0).toFixed(2)}</span>
                                                </div>

                                                <div className="d-flex justify-content-between align-items-center py-2">
                                                    <span className="fw-medium">Sub-total:</span>
                                                    <span className="fw-semibold">${(
                                                        Number(formik.values.totals?.removal_subtotal || removalCost || 0)
                                                        // Number(formik.values.totals?.specialfee_subtotal || 0) -
                                                        // Number(formik.values.discount_amount || 0)
                                                    ).toFixed(2)}</span>
                                                </div>
                                                {showCostBreakdownDiscount && (
                                                    <div className="d-flex justify-content-between align-items-center py-2">
                                                        <span className="fw-medium" style={{ color: "#00a63e" }}>
                                                            Discount ({costBreakdownDiscount.code || formik.values.discount_code || "—"}):
                                                        </span>
                                                        <span className="fw-semibold" style={{ color: "#00a63e" }}>
                                                            -${effectiveDiscountAmount.toFixed(2)}
                                                        </span>
                                                    </div>
                                                )}

                                                <div className="d-flex justify-content-between align-items-center py-2">
                                                    {(() => {
                                                        const { labelText, tooltip } = buildSpecialFeeCostBreakdownDisplay(selectedSpecialFees);
                                                        return (
                                                            <>
                                                                <span className="fw-medium me-2" style={{ minWidth: 0 }} title={tooltip}>
                                                                    {labelText}:
                                                                </span>
                                                                <span className="fw-semibold flex-shrink-0">
                                                                    ${Number(formik.values.totals?.specialfee_subtotal || 0).toFixed(2)}
                                                                </span>
                                                            </>
                                                        );
                                                    })()}
                                                </div>

                                                <div className="d-flex justify-content-between align-items-center py-2">
                                                    <span className="fw-medium">Tax:</span>
                                                    <span className="fw-semibold">
                                                        ${resolvedTaxTotal.toFixed(2)}
                                                    </span>
                                                </div>

                                                {/* {formik.values.totals && (
                                                    <div className="mb-2">
                                                        <button
                                                            type="button"
                                                            className="btn btn-link btn-sm p-0 text-decoration-none"
                                                            onClick={() => setShowTaxDetails(!showTaxDetails)}
                                                        >
                                                            {showTaxDetails ? "Hide" : "Show"} Tax Details
                                                        </button>
                                                        {showTaxDetails && (
                                                            <div className="small text-muted mt-2">
                                                                <div>Removal Tax: ${Number(formik.values.totals.removal_tax_amount || 0).toFixed(2)}</div>
                                                                <div>Special Fee Tax: ${Number(formik.values.totals.specialfee_tax_amount || 0).toFixed(2)}</div>
                                                            </div>
                                                        )}
                                                    </div>
                                                )} */}

                                                <hr className="my-3" />

                                                <div className="d-flex justify-content-between align-items-center py-2">
                                                    <span className="fw-bold fs-18">Total Amount:</span>
                                                    <span className="fw-bold fs-18 text-primary">${currentGrandTotal}</span>
                                                </div>

                                                <hr className="my-3" />

                                                <div className="d-flex justify-content-between align-items-center py-2">
                                                    <span className="fw-medium">Already Paid:</span>
                                                    <span className="fw-semibold"> ${totalPaid.toFixed(2)}</span>
                                                </div>

                                                <div className="d-flex justify-content-between align-items-center py-2">
                                                    <span className="fw-medium">Remaining Balance:</span>
                                                    <span className="fw-semibold">
                                                        {removalOrder?.payment_status === "Overpaid"
                                                            ? ` +$${Math.abs(remaingamount).toFixed(2)}`
                                                            : ` $${remaingamount.toFixed(2)}`
                                                        }
                                                    </span>
                                                </div>

                                                {formik.values.enable_partial_payment && (
                                                    <div className="d-flex justify-content-between align-items-center py-2">
                                                        <span className="fw-medium">Partial Payment:</span>
                                                        <span className="fw-semibold">${Number(formik.values.partial_amount || 0).toFixed(2)}</span>
                                                    </div>
                                                )}
                                            </div>

                                            {/* {removalOrder?.status !== 600 &&  (
                                                mode === "view" &&
                                                taxType === "taxcloud" &&
                                                removalOrder?.tax_status !== "complete" && (
                                                    <div className="mt-3 d-flex gap-2">
                                                        <Button
                                                            onClick={handleGetLiveTax}
                                                            disabled={liveTaxLoading}
                                                            className="btn btn-primary table-addbutton br-8"
                                                        >
                                                            {liveTaxLoading ? "Getting Live Tax…" : "Get Live Tax"}
                                                        </Button>

                                                        {removalOrder?.tax_status === "draft" && (
                                                            <Button className="export-button border-0" onClick={handleLiveTaxNext}>
                                                                Report Live Taxes
                                                            </Button>
                                                        )}
                                                    </div>
                                                )
                                            )} */}

                                            {/* ✅ Live Tax Buttons (TaxCloud only, View mode, not yet complete) */}
                                            {/* {Viewmode && taxType === "taxcloud" && removalOrder?.tax_status !== "complete" && (
                                                <div className="mt-3 d-flex gap-2">
                                                    <button
                                                        type="button"
                                                        className="btn btn-primary btn-sm"
                                                        onClick={handleGetLiveTax}
                                                        disabled={liveTaxLoading}
                                                    >
                                                        {liveTaxLoading ? "Getting Live Tax…" : "Get Live Tax"}
                                                    </button>

                                                    {removalOrder?.tax_status === "draft" && (
                                                        <button
                                                            type="button"
                                                            className="btn btn-success btn-sm"
                                                            onClick={handleLiveTaxNext}
                                                        >
                                                            Next
                                                        </button>
                                                    )}
                                                </div>
                                            )} */}
                                        </div>

                                        <div className="mb-3">
                                            {(removalOrder?.status === 400 || removalOrder?.status === 500 || removalOrder?.status === 600) && (
                                                //         <div className="col-lg-4 mt-5">
                                                //             <label htmlFor="">Proof of Removal</label>
                                                //             <input
                                                //                 type="file"
                                                //                 className="form-control"
                                                //                 id="image"
                                                //                 name="image"
                                                //                 multiple
                                                //                 onChange={(e) => {
                                                //                     const files = Array.from(e.target.files || []);
                                                //                     const newPreviews = files?.map((file) => ({
                                                //                         file,
                                                //                         url: URL.createObjectURL(file),
                                                //                     }));
                                                //                     setPreviewFiles((prev) => [...prev, ...newPreviews]);
                                                //                 }}
                                                //             />
                                                //             {previewFiles?.length > 0 && (
                                                //                 <button
                                                //                     type="button"
                                                //                     className="btn btn-success mt-3"
                                                //                     onClick={async () => {
                                                //                         try {
                                                //                             const uploads = await Promise.all(
                                                //                                 previewFiles?.map(async ({ file }) => {
                                                //                                     const path = await uploadFile(file); // ✅ Reuse function
                                                //                                     return {
                                                //                                         file_name: file.name,
                                                //                                         file_url: path,
                                                //                                         content_type: file.type,
                                                //                                         uploaded_date: new Date().toISOString(),
                                                //                                     };
                                                //                                 })
                                                //                             );

                                                //                             const allFiles = [...uploadedFileMeta, ...uploads];
                                                //                             const uniqueByName = allFiles.filter(
                                                //                                 (file, index, self) =>
                                                //                                     index === self.findIndex((f) => f.file_name === file.file_name)
                                                //                             );

                                                //                             setUploadedFileMeta(uniqueByName);
                                                //                             setPreviewFiles([]); // clear previews after upload

                                                //                             // 4. Call Proof API with final files
                                                //                             // 🚀 Only send the NEW ones
                                                //                             await uploadProofOfRemovals(uploads);
                                                //                             showSuccess("Files uploaded successfully!");
                                                //                         } catch (err) {
                                                //                             console.error("Upload error:", err);
                                                //                             showError("Failed to upload files.");
                                                //                         }
                                                //                     }}
                                                //                 >
                                                //                     Upload
                                                //                 </button>
                                                //             )}

                                                //             <div className="row mt-3">
                                                //                 {uploadedFileMeta?.map((file, index) => (
                                                //                     <div key={index} className="col-lg-3 position-relative mb-3">
                                                //                         {/* <div
                                                //     className="position-absolute top-0 end-0 bg-danger text-white rounded-circle"
                                                //     style={{
                                                //         width: "20px",
                                                //         height: "20px",
                                                //         textAlign: "center",
                                                //         cursor: "pointer",
                                                //         zIndex: 2,
                                                //     }}
                                                //     onClick={() => {
                                                //         setUploadedFileMeta((prev) => prev.filter((_, i) => i !== index));
                                                //     }}
                                                // >
                                                //     &times;
                                                // </div> */}
                                                //                         <div className="border p-2">
                                                //                             <img
                                                //                                 src={`${process.env.REACT_APP_BASE_URL}${file.file_url}`}
                                                //                                 alt={file.file_name}
                                                //                                 className="img-fluid"
                                                //                                 style={{ height: "150px", objectFit: "cover", width: "100%" }}
                                                //                             />
                                                //                             <div className="small text-center mt-1 text-muted">{file.file_name}</div>
                                                //                         </div>
                                                //                     </div>
                                                //                 ))}

                                                //                 {previewFiles?.map((item, index) => (
                                                //                     <div key={`preview-${index}`} className="col-lg-3 position-relative mb-3">
                                                //                         <div
                                                //                             className="position-absolute top-0 end-0 bg-danger text-white rounded-circle"
                                                //                             style={{
                                                //                                 width: "20px",
                                                //                                 height: "20px",
                                                //                                 textAlign: "center",
                                                //                                 cursor: "pointer",
                                                //                                 zIndex: 2,
                                                //                             }}
                                                //                             onClick={() => {
                                                //                                 URL.revokeObjectURL(item.url);
                                                //                                 setPreviewFiles(previewFiles.filter((_, i) => i !== index));
                                                //                             }}
                                                //                         >
                                                //                             &times;
                                                //                         </div>
                                                //                         <div className="border p-2">
                                                //                             <img
                                                //                                 src={item.url}
                                                //                                 alt={`preview-${index}`}
                                                //                                 className="img-fluid"
                                                //                                 style={{ height: "150px", objectFit: "cover", width: "100%" }}
                                                //                             />
                                                //                             <div className="small text-center mt-1 text-muted">{item.file.name}</div>
                                                //                         </div>
                                                //                     </div>
                                                //                 ))}
                                                //             </div>
                                                //         </div>
                                                <ProofSection
                                                    title="Proof of Removal"
                                                    canEdit={!!canEditProof}
                                                    existing={uploadedFileMeta}
                                                    setExisting={setUploadedFileMeta}
                                                    showNotifyCustomer={removalOrder?.status === 500}
                                                    onNotifyCustomer={notifyCustomerProofAvailable}
                                                    onRegisterUploadHandler={(uploadFn) => {
                                                        proofUploadHandlerRef.current = uploadFn;
                                                    }}
                                                    // uploadFile={uploadFile}
                                                    onSubmitProof={async (newFiles) => {
                                                        await uploadProofOfRemovals(newFiles);
                                                    }}
                                                />
                                            )}
                                        </div>

                                        {/* for desktop view back and submit buttom */}
                                        <div className="row mb-3 col-lg-12 rcust-margin-4 d-none d-lg-block" style={{ marginTop: "27px" }}>
                                            <div className="col-lg-12 d-block d-lg-flex justify-content-between">
                                                <div className="col-lg-2 mb-3">
                                                    <button
                                                        className="btn back-btn surface-btn fw-bold fs-16 w-100"
                                                        type="button"
                                                        onClick={() => safeNavigate(`${baseRoute}/jobs/removalRequests`)}
                                                    >
                                                        Back
                                                    </button>
                                                </div>

                                                <div className="align-items-baseline col-lg-3 d-lg-flex justify-content-end">
                                                    {renderViewModeActions()}
                                                    {!isViewMode && (
                                                        <>
                                                            {/* Desktop */}
                                                            <button
                                                                type="submit"
                                                                className="btn btn-primary save-btn surface-btn fw-bold fs-16 d-none d-lg-block"
                                                                disabled={formSubmitting || isRecomputing}
                                                            >
                                                                {isRecomputing ? "Calculating..." : "Save"}
                                                            </button>

                                                            {/* Mobile */}
                                                            <button
                                                                type="submit"
                                                                className="btn btn-primary save-btn surface-btn fw-bold fs-16 d-block d-lg-none w-100"
                                                                disabled={formSubmitting || isRecomputing}
                                                            >
                                                                {isRecomputing ? "Calculating..." : "Save"}
                                                            </button>
                                                        </>
                                                    )}

                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-lg-4">
                                        <PONumber
                                            title="PO Number"
                                            value={String((removalOrder as any)?.po_number ?? (removalOrder as any)?.poNumber ?? "")}
                                            placeholder="e.g. PO-2026-00124"
                                            className="mb-3"
                                            orderId={String(removalOrder?._id || removalOrder?.id || id || "")}
                                            orderType="removal"
                                            onSaved={async () => {
                                                await fetchRemovalOrder();
                                            }}
                                        />
                                        {/* ✅ Right Column: Special Fees (View & Edit Mode) */}
                                        <SpecialFeeui
                                            token={token!}
                                            personId={effectiveCouponCreatedById || ""}
                                            supplierId={removalOrder?.seller_owner_node_id || removalOrder?.signage_id || ""}
                                            formik={formik}
                                            name="specialfee"
                                            apiBase={process.env.REACT_APP_API_URL || ""}
                                            readOnly={!isEdit}
                                            disabled={isRecomputing}  // ✅ Disable special fee selection when recompute is running
                                            title="Special Fees"
                                            onOptionsLoaded={(allOptions: any) => {
                                                setAllAvailableSpecialFees(allOptions);
                                            }}
                                            onUserChange={(nextFees: any) => {
                                                if (isRecomputingRef.current) {
                                                    safeConsole.log("⏸️ Recompute in progress, skipping special fee change");
                                                    return;
                                                }

                                                const currentSig = buildRemovalFeeSignature(selectedSpecialFees);
                                                const nextSig = buildRemovalFeeSignature(nextFees);

                                                setSelectedSpecialFees(nextFees);

                                                if (isEdit && currentSig !== nextSig) {
                                                    prevFeesRef.current = nextSig;
                                                    void recomputeRef.current?.({ fees: nextFees });
                                                }
                                            }}
                                        />
                                        {
                                            (removalOrder?.payment_status === "Paid") ? <></> :
                                                <div className="col-lg-12 mb-3">
                                                    {/* {status <= 600 && status !== 100 && Viewmode && removalOrder?.tax_status !== "" && ( */}
                                                    {status <= 600 && Viewmode && (
                                                        <PaymentPanel
                                                            variant="signage"   // or brokerage/agent depending on type
                                                            remainingAmount={Number(remaingamount)}
                                                            orderStatus={removalOrder?.status ?? 0}
                                                            paymentStatus={removalOrder?.payment_status ?? ""}
                                                            selectedPaymentMethod={selectedPaymentMethod}
                                                            setSelectedPaymentMethod={handlePaymentMethodSelect}
                                                            formik={formik}
                                                            maxAllowed={maxAllowed}
                                                            paymentInfo={removalOrder?.payment_info ?? []}
                                                            dynamicMethods={paymentMethod} // [{ _id, name }]
                                                            onSubmitOffline={(key) => completeOrder(key)}
                                                            orderType="removalOrder"
                                                            onlinePaymentNode={
                                                                stripePromise && clientSecret ? (
                                                                    <Elements key={clientSecret} stripe={stripePromise} options={{ clientSecret }}>
                                                                        <PaymentFormManualOrder
                                                                            url="removal_orders"
                                                                            method="POST"
                                                                            supplierId={(isSignageTeammember && parentSignageId ? parentSignageId : userId)?.toString() || ""}
                                                                            totalAmount={remaingamount ? remaingamount : Number(currentGrandTotal || removalOrder?.total_amount || 0)}
                                                                            paidAmount={
                                                                                formik.values.partial_amount === 0
                                                                                    ? (Number(removalOrder?.total_amount || 0) - totalPaid)
                                                                                    : formik.values.partial_amount
                                                                            }
                                                                            orderId={String(removalOrder?._id || removalOrder?.id || "")}
                                                                            orderNumber={String(removalOrder?.order_number || "")}
                                                                            customerName={String(removalOrder?.user_name || "")}
                                                                            customerPhone={String(removalOrder?.user_phone || "")}
                                                                            selectedPaymentMethod={selectedPaymentMethod?.name || "online"}
                                                                            skipRedirectOnSuccess
                                                                            onPaymentSuccess={async (paymentIntentId, updatedOrderFromResponse?: any) => {
                                                                                // Send "Payment applied for Removal Job" emails after successful online payment
                                                                                try {
                                                                                    const from = process.env.REACT_APP_FROM_EMAIL2;
                                                                                    if (from && removalOrder) {
                                                                                        safeConsole.log("onPaymentSuccess: Starting email send process for removal payment:", paymentIntentId);
                                                                                        
                                                                                        // Use updated order from response if available, otherwise fetch it
                                                                                        let updatedOrder = updatedOrderFromResponse || removalOrder;
                                                                                        
                                                                                        // If we don't have updated order from response, fetch it with retry
                                                                                        if (!updatedOrderFromResponse) {
                                                                                            safeConsole.log("No updated order from response, fetching from API...");
                                                                                            // Wait a moment for backend to process payment before fetching updated order
                                                                                            await new Promise(resolve => setTimeout(resolve, 1000));
                                                                                            
                                                                                            let retries = 3;
                                                                                            while (retries > 0) {
                                                                                                try {
                                                                                                    const updatedOrderRes = await fetch(`${process.env.REACT_APP_API_URL}/removal_orders/${removalOrder._id}`, {
                                                                                                        headers: { Authorization: `Bearer ${token}` },
                                                                                                    });
                                                                                                    if (updatedOrderRes.ok) {
                                                                                                        const fetchedOrder = await updatedOrderRes.json();
                                                                                                        safeConsole.log("Fetched updated removal order, payment_info length:", fetchedOrder.payment_info?.length || 0);
                                                                                                        // Check if payment_info has been updated (has at least one payment)
                                                                                                        if (Array.isArray(fetchedOrder.payment_info) && fetchedOrder.payment_info.length > 0) {
                                                                                                            updatedOrder = fetchedOrder;
                                                                                                            safeConsole.log("Payment info found, using updated order");
                                                                                                            break;
                                                                                                        } else if (retries === 1) {
                                                                                                            // Last retry, use fetched order even if payment_info is empty
                                                                                                            updatedOrder = fetchedOrder;
                                                                                                            safeConsole.warn("Using fetched order even though payment_info is empty");
                                                                                                        }
                                                                                                    }
                                                                                                } catch (fetchErr) {
                                                                                                    safeConsole.warn(`Failed to fetch updated removal order for email (retries left: ${retries - 1}):`, fetchErr);
                                                                                                }
                                                                                                if (retries > 1) {
                                                                                                    await new Promise(resolve => setTimeout(resolve, 500));
                                                                                                }
                                                                                                retries--;
                                                                                            }
                                                                                        } else {
                                                                                            safeConsole.log("Using updated order from payment API response");
                                                                                        }
                                                                                        
                                                                                        // Get payment amount from updated order
                                                                                        const paymentInfoArray = Array.isArray(updatedOrder.payment_info) ? updatedOrder.payment_info : [];
                                                                                        const latestPayment = paymentInfoArray.length > 0
                                                                                            ? paymentInfoArray.reduce((latest: any, current: any) => {
                                                                                                const latestDate = new Date(latest?.payment_date || 0).getTime();
                                                                                                const currentDate = new Date(current?.payment_date || 0).getTime();
                                                                                                return currentDate > latestDate ? current : latest;
                                                                                            }, paymentInfoArray[0])
                                                                                            : null;
                                                                                        const paymentAmount = latestPayment?.amount_paid 
                                                                                            ? Number(latestPayment.amount_paid)
                                                                                            : (formik.values.enable_partial_payment && formik.values.partial_amount > 0
                                                                                                ? Number(formik.values.partial_amount)
                                                                                                : Number(remaingamount));
                                                                                        
                                                                                        const paymentMethodName = latestPayment?.method || selectedPaymentMethod?.name || "online";
                                                                                        
                                                                                        safeConsole.log("Calling sendRemovalPaymentAppliedEmails with:", {
                                                                                            orderId: updatedOrder._id || updatedOrder.id,
                                                                                            paymentAmount,
                                                                                            paymentMethodName,
                                                                                            paymentInfoLength: paymentInfoArray.length
                                                                                        });
                                                                                        
                                                                                        await sendRemovalPaymentAppliedEmails(
                                                                                            updatedOrder,
                                                                                            paymentAmount,
                                                                                            paymentMethodName,
                                                                                            from,
                                                                                            !!selectedPaymentMethod && selectedPaymentMethod._id !== selectedPaymentMethod.name
                                                                                        );
                                                                                        
                                                                                        safeConsole.log("Removal payment email sending completed successfully");
                                                                                    } else {
                                                                                        safeConsole.warn("onPaymentSuccess: Missing from email or removal order", { from: !!from, removalOrder: !!removalOrder });
                                                                                    }
                                                                                } catch (emailError) {
                                                                                    safeConsole.error("Failed to send removal payment applied emails:", emailError);
                                                                                    // Don't block payment success if email fails, but log the error
                                                                                }
                                                                                localStorage.removeItem("cart_id");
                                                                                safeNavigate(0);
                                                                            }}
                                                                            userId={removalOrder?.inventory_source === "office" ? removalOrder?.team_member_id : removalOrder?.agent_id}
                                                                            userEmail={removalOrder?.user_email}
                                                                            paidBy_id={userId?.toString() ?? ""}
                                                                            paidBy_name={userName ?? ""}
                                                                            payments_status={removalOrder?.payment_status ?? ""}
                                                                        />
                                                                    </Elements>
                                                                ) : null
                                                            }
                                                        />
                                                    )}
                                                </div>
                                        }
                                        <div className="col-lg-12 rounded-3 shadow-sm">
                                            <OrderLog title="Removal Log" orderKind="removal" events={removalLogEvents} />
                                        </div>
                                    </div>

                                    {/* for mobile and tab view back and submit button */}
                                    <div className="row mb-3 col-lg-12 rcust-margin-4 d-block d-lg-none">
                                        <div className="col-lg-12 d-block d-lg-flex justify-content-between">
                                            <div className="col-lg-2 mb-3">
                                                <button
                                                    className="btn back-btn surface-btn fw-bold fs-16 w-100"
                                                    type="button"
                                                    onClick={() => safeNavigate(`${baseRoute}/jobs/removalRequests`)}
                                                >
                                                    Back
                                                </button>
                                            </div>

                                            <div className="align-items-baseline col-lg-3 d-lg-flex justify-content-end">
                                                {renderViewModeActions()}
                                                {!isViewMode && (
                                                    <>
                                                        {/* Desktop */}
                                                        <button
                                                            type="submit"
                                                            className="btn btn-primary save-btn surface-btn fw-bold fs-16 d-none d-lg-block"
                                                            disabled={formSubmitting || isRecomputing}
                                                        >
                                                            {isRecomputing ? "Calculating..." : "Save"}
                                                        </button>

                                                        {/* Mobile */}
                                                        <button
                                                            type="submit"
                                                            className="btn btn-primary save-btn surface-btn fw-bold fs-16 d-block d-lg-none w-100"
                                                            disabled={formSubmitting || isRecomputing}
                                                        >
                                                            {isRecomputing ? "Calculating..." : "Save"}
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}
                            {(removalOrder?.status === 500 || removalOrder?.status === 600) &&

                                <>
                                    <div className="col-lg-8 ">
                                        <div className="row col-lg-12 card-body shadow-lg mb-4 ms-0 p-3">
                                            <div className="mb-3">
                                                <SectionHeader title="Schedule & Assignment" icon="ri-calendar-line" color="#00a63e" size="md" divider />
                                            </div>
                                            {/* Removal Date */}
                                            <div className="col-lg-4 mb-3">
                                                <label className="form-label table-cells">
                                                    Removal Date
                                                </label>
                                                <input
                                                    type="date"
                                                    className="form-control"
                                                    id="scheduled_date"
                                                    name="scheduled_date"
                                                    placeholder="mm/dd/yyyy"
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                    onFocus={() => {
                                                        if (!isFieldEditable(removalOrder?.status ?? 0, "ScheduleDate")) return;
                                                        if (toScheduledDateInput(formik.values.scheduled_date)) return;
                                                        formik.setFieldValue("scheduled_date", new Date().toISOString().split("T")[0]);
                                                    }}
                                                    value={toScheduledDateInput(formik.values.scheduled_date)}
                                                    readOnly={!isFieldEditable(removalOrder?.status ?? 0, "ScheduleDate")}
                                                />
                                                {formik.touched.scheduled_date && typeof formik.errors.scheduled_date === "string" && (
                                                    <div className="text-danger mt-1">{formik.errors.scheduled_date}</div>
                                                )}
                                            </div>
                                            {/* Assigned To - Only show when parentSignageId is loaded for team members */}
                                            {(!isSignageTeammember || parentSignageId) ? (
                                                <div className="col-lg-4 mb-3">
                                                    <label className="form-label table-cells">
                                                        Assigned To <span className="text-danger">*</span>
                                                    </label>
                                                    <AsyncPaginate
                                                        id="installeId"
                                                        name="installeId"
                                                        classNamePrefix="rs"
                                                        placeholder="Search Assigned To..."
                                                        value={selectedSignageTeamMemberOption ? {
                                                            value: selectedSignageTeamMemberOption.value,
                                                            label: selectedSignageTeamMemberOption.label,
                                                            firstName: selectedSignageTeamMemberOption.firstName,
                                                            lastName: selectedSignageTeamMemberOption.lastName,
                                                            email: selectedSignageTeamMemberOption.email,
                                                            phone: selectedSignageTeamMemberOption.phone
                                                        } : null}
                                                        loadOptions={loadSignageTeamMemberOptions}
                                                        onChange={(selected) => {
                                                            if (selected) {
                                                                formik.setFieldValue("installeId", selected.value || "");
                                                                setSelectedSignageTeamMemberOption(selected);
                                                                setSelectedInstaller({ // For compatibility with existing code
                                                                    value: selected.value,
                                                                    label: selected.label,
                                                                    firstName: selected.firstName,
                                                                    lastName: selected.lastName,
                                                                    email: selected.email,
                                                                    phone: selected.phone
                                                                });
                                                            } else {
                                                                formik.setFieldValue("installeId", "");
                                                                setSelectedSignageTeamMemberOption(null);
                                                                setSelectedInstaller(null);
                                                            }
                                                        }}
                                                        onBlur={() => formik.setFieldTouched("installeId", true)}
                                                        additional={{ page: 1 }}
                                                        defaultOptions
                                                        debounceTimeout={300}
                                                        isClearable
                                                        isDisabled={!isFieldEditable(removalOrder?.status ?? 0, "Installer")}
                                                        components={{ DropdownIndicator }}
                                                        styles={customSelectStyles}
                                                    />
                                                    {formik.touched.installeId && typeof formik.errors.installeId === "string" && (
                                                        <div className="text-danger mt-1">{formik.errors.installeId}</div>
                                                    )}
                                                </div>
                                            ) : (
                                                <div className="col-lg-4 mb-3">
                                                    <label className="form-label table-cells">
                                                        Assigned To <span className="text-danger">*</span>
                                                    </label>
                                                    <div className="d-flex align-items-center justify-content-center" style={{ minHeight: "38px" }}>
                                                        <div className="spinner-border spinner-border-sm text-primary me-2" role="status">
                                                            <span className="visually-hidden">Loading...</span>
                                                        </div>
                                                        <span className="text-muted">Loading team members...</span>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Jobs Today */}
                                            <div className="col-lg-4 mb-3">
                                                <label className="form-label table-headers">Jobs Today</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    id="scheduled_jobs"
                                                    name="scheduled_jobs"
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                    value={formik.values.scheduled_jobs}
                                                    disabled
                                                />
                                                {formik.touched.scheduled_jobs &&
                                                    formik.errors.scheduled_jobs && (
                                                        <div className="invalid-feedback">
                                                            {formik.errors.scheduled_jobs}
                                                        </div>
                                                    )}
                                            </div>

                                            {/* ✅ Removal Priority */}
                                            <div className="col-lg-4 mb-3">
                                                <label className="form-label table-cells">
                                                    Removal Priority <span className="text-danger">*</span>
                                                </label>
                                                <Select
                                                    id="priority"
                                                    name="priority"
                                                    options={priorityOptions}
                                                    value={priorityOptions.find((option) => option.value === formik.values.priority) || null}
                                                    onChange={(option) =>
                                                        formik.setFieldValue("priority", option?.value || "")
                                                    }
                                                    onBlur={formik.handleBlur}
                                                    isDisabled={!isFieldEditable(removalOrder?.status ?? 0, "Priority")}
                                                    components={{ DropdownIndicator }}
                                                    styles={customSelectStyles}
                                                    classNamePrefix="rs"
                                                />
                                                {formik.touched.priority && typeof formik.errors.priority === "string" && (
                                                    <div className="text-danger mt-1">{formik.errors.priority}</div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="row col-lg-12 mb-4 card-body shadow-lg mb-4 ms-0 pb-3 p-3">
                                            <div className="align-items-center d-block d-lg-flex justify-content-between mb-3">
                                                {/* Left side: Address Information with purple icon */}
                                                <SectionHeader title="Address Information" icon="ri-map-pin-line" color="#e7000b" size="md" divider className="mb-3 mb-lg-0" />

                                                {/* Right side: Gate Code with functionality */}
                                                <div className="d-flex align-items-center gap-3">
                                                    <label className="table-cells mb-0">Gate Code</label>
                                                    <div className="d-flex align-items-center gap-2">
                                                        <input
                                                            type="text"
                                                            id="gatecode"
                                                            name="gatecode"
                                                            className="form-control"
                                                            style={{
                                                                width: '140px',
                                                                backgroundColor: formik.values.gateCodeEnabled ? '#ffffff' : '#f8f9fa',
                                                                color: formik.values.gateCodeEnabled ? '#495057' : '#6c757d',
                                                                cursor: formik.values.gateCodeEnabled ? 'text' : 'pointer'
                                                            }}
                                                            value={formik.values.gatecode}
                                                            onChange={formik.handleChange}
                                                            onBlur={formik.handleBlur}
                                                            placeholder="Enter Gate Code"
                                                            readOnly={!formik.values.gateCodeEnabled}
                                                            onClick={() => {
                                                                if (!formik.values.gateCodeEnabled) {
                                                                    formik.setFieldValue("gateCodeEnabled", true);
                                                                }
                                                            }}
                                                        />
                                                        <button
                                                            type="button"
                                                            className="btn btn-secondary btn-sm"
                                                            disabled={!formik.values.gateCodeEnabled}
                                                            onClick={async () => {
                                                                // Save gate code and disable the field again
                                                                await handleSaveGateCode();
                                                                formik.setFieldValue("gateCodeEnabled", false);
                                                            }}
                                                        >
                                                            <i className="ri-save-fill fs-16"></i>
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-lg-12">
                                                {/* <div className="align-items-baseline col-lg-12 d-flex justify-content-between">
                                                    <div className="my-2 fw-bold">
                                                        <input
                                                            type="checkbox"
                                                            id="isSameAsBilling"
                                                            name="isSameAsBilling"
                                                            onChange={formik.handleChange}
                                                            onBlur={formik.handleBlur}
                                                            checked={formik.values.isSameAsBilling}
                                                        />{" "}
                                                        Billing and Installation Address are Same

                                                    </div>
                                                    <button className="btn btn-primary btn-sm mb-3 removal-installation" type="button">+ Address</button>
                                                </div> */}

                                                <div className="row">
                                                    <div className="col-lg-6 mb-3">
                                                        <label
                                                            htmlFor="billingAddressId"
                                                            className="form-label table-cells"
                                                        >
                                                            Billing Address <span className="text-danger">*</span>
                                                        </label>
                                                        <Select
                                                            // className="form-control"
                                                            id="billingAddress"
                                                            name="billingAddress"
                                                            options={addressOptions}
                                                            value={
                                                                addressOptions?.find(
                                                                    (option) =>
                                                                        option.value === formik.values.billingAddressId
                                                                ) || null
                                                            }
                                                            onChange={(selected: any) => {
                                                                // console.log("Selected Billing Address:", selected); // Debugging log
                                                                formik.setFieldValue("billingAddressId", selected?.value);
                                                                const selectedAddress = addressObjects?.find(
                                                                    (addr) => (addr.address_details ? addr.address_details._id : addr._id) === selected?.value
                                                                );
                                                                if (formik.values.officeId && !formik.values.agentId) {
                                                                    setSelectedOfficeBillingAddress(selectedAddress || null);
                                                                }
                                                                else {
                                                                    setSelectedBillingAddress(selectedAddress?.address_details || null);
                                                                }
                                                                formik.setFieldValue("isSameAsBilling", false);
                                                            }}
                                                            isDisabled={!isEdit || !isFieldEditable(removalOrder?.status ?? 0, "BillingAddress")}
                                                            components={{ DropdownIndicator }}
                                                            styles={customSelectStyles}
                                                            classNamePrefix="rs"
                                                        />
                                                        {formik.touched.billingAddressId && typeof formik.errors.billingAddressId === "string" && (
                                                            <div className="text-danger mt-1">{formik.errors.billingAddressId}</div>
                                                        )}
                                                    </div>
                                                    {/* {formik.values.isSameAsBilling ? null : ( */}
                                                    <div className="col-lg-6 mb-3">
                                                        <label
                                                            htmlFor="removalAddressId"
                                                            className="form-label table-cells"
                                                        >
                                                            Removal Address
                                                        </label>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            value={fullAddress || ""}
                                                            disabled
                                                        />

                                                    </div>
                                                    {/* )} */}
                                                </div>
                                            </div>
                                            <div className="col-lg-12">
                                                <div style={mapContainerStyle}>
                                                    <GoogleMap
                                                        mapContainerStyle={mapContainerStyle}
                                                        zoom={10}
                                                        center={selectedLocation || center}
                                                    >
                                                        {selectedLocation && <Marker position={selectedLocation} />}
                                                    </GoogleMap>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-lg-4 d-none d-lg-block">
                                        {
                                            (removalOrder?.payment_status === "Paid") ? <></> :
                                                <div className="col-lg-12 mb-3">
                                                    {status <= 600 && Viewmode && (
                                                        <PaymentPanel
                                                            variant="signage"   // or brokerage/agent depending on type
                                                            remainingAmount={Number(remaingamount)}
                                                            orderStatus={removalOrder?.status ?? 0}
                                                            paymentStatus={removalOrder?.payment_status ?? ""}
                                                            selectedPaymentMethod={selectedPaymentMethod}
                                                            setSelectedPaymentMethod={handlePaymentMethodSelect}
                                                            formik={formik}
                                                            maxAllowed={maxAllowed}
                                                            paymentInfo={removalOrder?.payment_info ?? []}
                                                            dynamicMethods={paymentMethod} // [{ _id, name }]
                                                            onSubmitOffline={(key) => completeOrder(key)}
                                                            orderType="removalOrder"
                                                            onlinePaymentNode={
                                                                stripePromise && clientSecret ? (
                                                                    <Elements key={clientSecret} stripe={stripePromise} options={{ clientSecret }}>
                                                                        <PaymentFormManualOrder
                                                                            url="removal_orders"
                                                                            method="POST"
                                                                            supplierId={(isSignageTeammember && parentSignageId ? parentSignageId : userId)?.toString() || ""}
                                                                            totalAmount={remaingamount ? remaingamount : Number(currentGrandTotal || removalOrder?.total_amount || 0)}
                                                                            paidAmount={
                                                                                formik.values.partial_amount === 0
                                                                                    ? (Number(removalOrder?.total_amount || 0) - totalPaid)
                                                                                    : formik.values.partial_amount
                                                                            }
                                                                            orderId={String(removalOrder?._id || removalOrder?.id || "")}
                                                                            orderNumber={String(removalOrder?.order_number || "")}
                                                                            customerName={String(removalOrder?.user_name || "")}
                                                                            customerPhone={String(removalOrder?.user_phone || "")}
                                                                            selectedPaymentMethod={selectedPaymentMethod?.name || "online"}
                                                                            skipRedirectOnSuccess
                                                                            onPaymentSuccess={async (paymentIntentId, updatedOrderFromResponse?: any) => {
                                                                                // Send "Payment applied for Removal Job" emails after successful online payment
                                                                                try {
                                                                                    const from = process.env.REACT_APP_FROM_EMAIL2;
                                                                                    if (from && removalOrder) {
                                                                                        safeConsole.log("onPaymentSuccess: Starting email send process for removal payment:", paymentIntentId);
                                                                                        
                                                                                        // Use updated order from response if available, otherwise fetch it
                                                                                        let updatedOrder = updatedOrderFromResponse || removalOrder;
                                                                                        
                                                                                        // If we don't have updated order from response, fetch it with retry
                                                                                        if (!updatedOrderFromResponse) {
                                                                                            safeConsole.log("No updated order from response, fetching from API...");
                                                                                            // Wait a moment for backend to process payment before fetching updated order
                                                                                            await new Promise(resolve => setTimeout(resolve, 1000));
                                                                                            
                                                                                            let retries = 3;
                                                                                            while (retries > 0) {
                                                                                                try {
                                                                                                    const updatedOrderRes = await fetch(`${process.env.REACT_APP_API_URL}/removal_orders/${removalOrder._id}`, {
                                                                                                        headers: { Authorization: `Bearer ${token}` },
                                                                                                    });
                                                                                                    if (updatedOrderRes.ok) {
                                                                                                        const fetchedOrder = await updatedOrderRes.json();
                                                                                                        safeConsole.log("Fetched updated removal order, payment_info length:", fetchedOrder.payment_info?.length || 0);
                                                                                                        // Check if payment_info has been updated (has at least one payment)
                                                                                                        if (Array.isArray(fetchedOrder.payment_info) && fetchedOrder.payment_info.length > 0) {
                                                                                                            updatedOrder = fetchedOrder;
                                                                                                            safeConsole.log("Payment info found, using updated order");
                                                                                                            break;
                                                                                                        } else if (retries === 1) {
                                                                                                            // Last retry, use fetched order even if payment_info is empty
                                                                                                            updatedOrder = fetchedOrder;
                                                                                                            safeConsole.warn("Using fetched order even though payment_info is empty");
                                                                                                        }
                                                                                                    }
                                                                                                } catch (fetchErr) {
                                                                                                    safeConsole.warn(`Failed to fetch updated removal order for email (retries left: ${retries - 1}):`, fetchErr);
                                                                                                }
                                                                                                if (retries > 1) {
                                                                                                    await new Promise(resolve => setTimeout(resolve, 500));
                                                                                                }
                                                                                                retries--;
                                                                                            }
                                                                                        } else {
                                                                                            safeConsole.log("Using updated order from payment API response");
                                                                                        }
                                                                                        
                                                                                        // Get payment amount from updated order
                                                                                        const paymentInfoArray = Array.isArray(updatedOrder.payment_info) ? updatedOrder.payment_info : [];
                                                                                        const latestPayment = paymentInfoArray.length > 0
                                                                                            ? paymentInfoArray.reduce((latest: any, current: any) => {
                                                                                                const latestDate = new Date(latest?.payment_date || 0).getTime();
                                                                                                const currentDate = new Date(current?.payment_date || 0).getTime();
                                                                                                return currentDate > latestDate ? current : latest;
                                                                                            }, paymentInfoArray[0])
                                                                                            : null;
                                                                                        const paymentAmount = latestPayment?.amount_paid 
                                                                                            ? Number(latestPayment.amount_paid)
                                                                                            : (formik.values.enable_partial_payment && formik.values.partial_amount > 0
                                                                                                ? Number(formik.values.partial_amount)
                                                                                                : Number(remaingamount));
                                                                                        
                                                                                        const paymentMethodName = latestPayment?.method || selectedPaymentMethod?.name || "online";
                                                                                        
                                                                                        safeConsole.log("Calling sendRemovalPaymentAppliedEmails with:", {
                                                                                            orderId: updatedOrder._id || updatedOrder.id,
                                                                                            paymentAmount,
                                                                                            paymentMethodName,
                                                                                            paymentInfoLength: paymentInfoArray.length
                                                                                        });
                                                                                        
                                                                                        await sendRemovalPaymentAppliedEmails(
                                                                                            updatedOrder,
                                                                                            paymentAmount,
                                                                                            paymentMethodName,
                                                                                            from,
                                                                                            !!selectedPaymentMethod && selectedPaymentMethod._id !== selectedPaymentMethod.name
                                                                                        );
                                                                                        
                                                                                        safeConsole.log("Removal payment email sending completed successfully");
                                                                                    } else {
                                                                                        safeConsole.warn("onPaymentSuccess: Missing from email or removal order", { from: !!from, removalOrder: !!removalOrder });
                                                                                    }
                                                                                } catch (emailError) {
                                                                                    safeConsole.error("Failed to send removal payment applied emails:", emailError);
                                                                                    // Don't block payment success if email fails, but log the error
                                                                                }
                                                                                localStorage.removeItem("cart_id");
                                                                                safeNavigate(0);
                                                                            }}
                                                                            userId={removalOrder?.inventory_source === "office" ? removalOrder?.team_member_id : removalOrder?.agent_id}
                                                                            userEmail={removalOrder?.user_email}
                                                                            paidBy_id={userId?.toString() ?? ""}
                                                                            paidBy_name={userName ?? ""}
                                                                            payments_status={removalOrder?.payment_status ?? ""}
                                                                        />
                                                                    </Elements>
                                                                ) : null
                                                            }
                                                        />
                                                    )}
                                                </div>
                                        }
                                    </div>
                                </>
                            }
                        </div>
                        {(removalOrder?.status === 500 || removalOrder?.status === 600) &&
                            <>

                                <div className="row col-lg-12 mb-3 ms-0">
                                    <div className="row col-lg-12 mb-3 card-body shadow-lg p-3">
                                        <div className="d-flex mb-3">
                                            <span
                                                aria-hidden="true"
                                                className="image-icons"
                                                style={{
                                                    backgroundColor: "#9810fa",
                                                    WebkitMaskImage: `url(${ProductSelection})`,
                                                    maskImage: `url(${ProductSelection})`,
                                                }}
                                            />
                                            <SectionHeader title="Product Selection" icon="" variant="success" size="md" divider />
                                        </div>
                                        <div className="col-lg-12 d-none d-lg-block" id="row-overlap">
                                            <table className="border table table-striped">
                                                <thead className="">
                                                    <tr>
                                                        <th className="text-center table-headers" style={{ width: "15%" }}>Product Name</th>
                                                        <th className="text-center table-headers" style={{ width: "7%" }}>SKU</th>
                                                        <th className="text-center table-headers" style={{ width: "10%" }}>Qty to Remove </th>
                                                        <th className="text-center table-headers" style={{ width: "14%" }}>Product Location</th>
                                                        {/* {
                                                        (removalOrder?.status === 500 || removalOrder?.status == 600) && */}
                                                        {/* <> */}
                                                        <th className="text-center table-headers" style={{ width: "7%" }}>Re-stock </th>
                                                        <th className="text-center table-headers" style={{ width: "15%" }}>Bin Location</th>
                                                        {/* <th className="text-center table-headers">Write Off </th> */}
                                                        <th className="text-center table-headers" style={{ width: "7%" }}>Lost </th>
                                                        <th className="text-center table-headers" style={{ width: "7%" }}>Damaged </th>
                                                        {/* <th className="text-center table-headers" style={{ width: "10%" }}>Reason</th> */}
                                                        <th className="text-center table-headers" style={{ width: "17%" }}>Note</th>
                                                        {/* </> */}
                                                        {/* } */}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {(() => {
                                                        const regularItems = updatedProducts?.filter((item: any) => item.inventory_source !== "rotational" && item.inventory_source !== "custom") ?? [];
                                                        const customItems = updatedProducts?.filter((item: any) => item.inventory_source === "custom") ?? [];
                                                        return (
                                                            <>
                                                                {regularItems.map((item: any, regularIndex: number) => {
                                                                    const index = updatedProducts?.findIndex((p: any) => p === item) ?? regularIndex;
                                                                    return (
                                                        <tr key={`normal-${item.product_id}-${index}`}>
                                                            <td className="text-center table-cells align-middle text-ellipsis">{item.product_name}</td>
                                                            <td className="text-center table-cells align-middle text-ellipsis">{displayRemovalWorkOrderSku(item)}</td>
                                                            <td className="text-center table-cells align-middle">{item.quantity_to_be_removed}</td>
                                                            <td className="text-center table-cells align-middle">
                                                                <StatusBadge status={item.current_status?.replace(/_/g, " ") || "-"} />
                                                            </td>

                                                            {/* {
                                                            (removalOrder?.status === 500 || removalOrder?.status === 600) && <> */}

                                                            <td className="align-middle">
                                                                <input
                                                                    type="number"
                                                                    className="form-control table-cells"
                                                                    min={0}
                                                                    max={getMaxQty(item)}
                                                                    value={item.restock_quantity || ''}
                                                                    placeholder="0"
                                                                    disabled={shouldDisableField(item, "restock_quantity")}
                                                                    data-path={`normal[${index}].restock`}
                                                                    onChange={(e) => {
                                                                        const rawValue = e.target.value;
                                                                        const qty = rawValue === '' ? 0 : parseInt(rawValue) || 0;
                                                                        updateProductField(index, "restock_quantity", qty);
                                                                    }}
                                                                    onBlur={(e) => {
                                                                        if (e.target.value === '') {
                                                                            updateProductField(index, "restock_quantity", 0);
                                                                        }
                                                                    }}
                                                                />
                                                            </td>

                                                            <td className="align-middle">
                                                                {(() => {
                                                                    // When status is 600, show bin location as disabled input field
                                                                    if (removalOrder?.status === 600) {
                                                                        const binLocationDisplay = productNotes[item.product_id]?.bin_location_display || "";
                                                                        return (
                                                                            <input
                                                                                type="text"
                                                                                className="form-control table-cells"
                                                                                value={binLocationDisplay}
                                                                                disabled={true}
                                                                                title={binLocationDisplay}
                                                                                data-path={`normal[${index}].binLocation`}
                                                                            />
                                                                        );
                                                                    }

                                                                    const productStorage = item?.storage_locations?.[0]?.location?.[0];
                                                                    const hasValidStorageBin =
                                                                        productStorage?.bin_id &&
                                                                        productStorage?.warehouse_name &&
                                                                        productStorage?.zone_name &&
                                                                        productStorage?.rack_name &&
                                                                        productStorage?.bin_name;

                                                                    const storageBinOption = hasValidStorageBin
                                                                        ? {
                                                                            value: productStorage.bin_id,
                                                                            label: `${productStorage.warehouse_name} > ${productStorage.zone_name} > ${productStorage.rack_name} > ${productStorage.bin_name}`,
                                                                            bin: {
                                                                                warehouse_id: productStorage.warehouse_id,
                                                                                warehouse: productStorage.warehouse_name,
                                                                                zone_id: productStorage.zone_id,
                                                                                zone: productStorage.zone_name,
                                                                                rack_id: productStorage.rack_id,
                                                                                rack: productStorage.rack_name,
                                                                                bin_id: productStorage.bin_id,
                                                                                bin: productStorage.bin_name
                                                                            }
                                                                        }
                                                                        : null;

                                                                    // Build current value - prioritize user selection (same as Assets table)
                                                                    let currentValue = null;
                                                                    if (productNotes[item.product_id]?.bin_location) {
                                                                        // User has selected a bin location - show this FIRST
                                                                        currentValue = {
                                                                            value: productNotes[item.product_id].bin_location,
                                                                            label: productNotes[item.product_id].bin_location_display || productNotes[item.product_id].bin_location,
                                                                        };
                                                                    } else if (storageBinOption) {
                                                                        // Fall back to storage location if no selection made
                                                                        currentValue = storageBinOption;
                                                                    }

                                                                    return (
                                                                        <div title={currentValue?.label || "Select bin location"}>
                                                                            <AsyncPaginate
                                                                                key={`bin-normal-${item.product_id}-${item.restock_quantity || 0}`}
                                                                                classNamePrefix="rs"
                                                                                placeholder="Search bin location..."
                                                                                value={currentValue}
                                                                                loadOptions={loadBinLocationOptions(item.restock_quantity || 1)}
                                                                                onChange={(option) => {
                                                                                    // console.log("option in bin location", option);
                                                                                    updateNoteField(item.product_id, "bin_location", option?.value || "", option?.label || "");
                                                                                }}
                                                                                additional={{ page: 1 }}
                                                                                defaultOptions
                                                                                debounceTimeout={300}
                                                                                isDisabled={!shouldEnableBinLocation(item)}
                                                                                components={{ DropdownIndicator }}
                                                                                styles={customSelectStyles}
                                                                                data-path={`normal[${index}].binLocation`}
                                                                            />
                                                                        </div>
                                                                    );
                                                                })()}
                                                            </td>


                                                            {/* <td>
                                                    <input
                                                        type="number"
                                                        className="form-control table-cells"
                                                        // value={item.writeoff_quantity || 0}
                                                        // disabled
                                                    /> 
                                                </td> */}
                                                            <td className="align-middle">
                                                                <input
                                                                    type="number"
                                                                    className="form-control table-cells"
                                                                    min={0}
                                                                    max={getMaxQty(item)}
                                                                    value={item.writeoff_quantity || ''}
                                                                    placeholder="0"
                                                                    disabled={shouldDisableField(item, "writeoff_quantity")}
                                                                    data-path={`normal[${index}].writeoff`}
                                                                    onChange={(e) => {
                                                                        const rawValue = e.target.value;
                                                                        const qty = rawValue === '' ? 0 : parseInt(rawValue) || 0;
                                                                        updateProductField(index, "writeoff_quantity", qty);
                                                                    }}
                                                                    onBlur={(e) => {
                                                                        if (e.target.value === '') {
                                                                            updateProductField(index, "writeoff_quantity", 0);
                                                                        }
                                                                    }}
                                                                />
                                                            </td>
                                                            <td className="align-middle">
                                                                <input
                                                                    type="number"
                                                                    className="form-control table-cells"
                                                                    min={0}
                                                                    max={getMaxQty(item)}
                                                                    value={item.damaged_quantity || ''}
                                                                    placeholder="0"
                                                                    disabled={shouldDisableField(item, "damaged_quantity")}
                                                                    data-path={`normal[${index}].damaged`}
                                                                    onChange={(e) => {
                                                                        const rawValue = e.target.value;
                                                                        const qty = rawValue === '' ? 0 : parseInt(rawValue) || 0;
                                                                        updateProductField(index, "damaged_quantity", qty);
                                                                    }}
                                                                    onBlur={(e) => {
                                                                        if (e.target.value === '') {
                                                                            updateProductField(index, "damaged_quantity", 0);
                                                                        }
                                                                    }}
                                                                />
                                                            </td>

                                                            {/* <td className="align-middle">
                                                                <input
                                                                    type="text"
                                                                    className="form-control"
                                                                    value={item.reason_to_writeoff || ""}
                                                                    onChange={(e) =>
                                                                        updateProductField(index, "reason_to_writeoff", e.target.value)
                                                                    }
                                                                    placeholder="Enter reason..."
                                                                />
                                                            </td> */}

                                                            <td className="align-middle">
                                                                <textarea
                                                                    className="form-control table-cells br-8"
                                                                    value={productNotes[item.product_id]?.note || ""}
                                                                    placeholder="Enter note..."
                                                                    onChange={(e) =>
                                                                        updateNoteField(item.product_id, "note", e.target.value)
                                                                    }
                                                                    disabled={removalOrder?.status === 600}
                                                                    style={{ resize: 'none' }}
                                                                ></textarea>
                                                            </td>
                                                            {/* </>
                                                        } */}
                                                        </tr>
                                                                    );
                                                                })}
                                                                {customItems.length > 0 && (
                                                                    <>
                                                                        <tr>
                                                                            <td colSpan={9} className="small fw-semibold py-2" style={{ backgroundColor: "#f8f9fa" }}>
                                                                                Custom Products
                                                                            </td>
                                                                        </tr>
                                                                        {customItems.map((item: any, customIndex: number) => {
                                                                            const index = updatedProducts?.findIndex((p: any) => p === item) ?? customIndex;
                                                                            return (
                                                                            <tr key={`custom-status500-${item.product_id}-${index}`}>
                                                                                <td className="text-center table-cells align-middle text-ellipsis">{item.product_name}</td>
                                                                                <td className="text-center table-cells align-middle text-ellipsis">{displayRemovalWorkOrderSku(item)}</td>
                                                                                <td className="text-center table-cells align-middle">{item.quantity_to_be_removed}</td>
                                                                                <td className="text-center table-cells align-middle">
                                                                                    <StatusBadge status={item.current_status?.replace(/_/g, " ") || "-"} />
                                                                                </td>
                                                                                <td className="align-middle">
                                                                                    <input
                                                                                        type="number"
                                                                                        className="form-control table-cells"
                                                                                        min={0}
                                                                                        max={getMaxQty(item)}
                                                                                        value={item.restock_quantity || ''}
                                                                                        placeholder="0"
                                                                                        disabled={shouldDisableField(item, "restock_quantity")}
                                                                                        data-path={`normal[${index}].restock`}
                                                                                        onChange={(e) => {
                                                                                            const rawValue = e.target.value;
                                                                                            const qty = rawValue === '' ? 0 : parseInt(rawValue) || 0;
                                                                                            updateProductField(index, "restock_quantity", qty);
                                                                                        }}
                                                                                        onBlur={(e) => {
                                                                                            if (e.target.value === '') {
                                                                                                updateProductField(index, "restock_quantity", 0);
                                                                                            }
                                                                                        }}
                                                                                    />
                                                                                </td>
                                                                                <td className="align-middle text-center table-cells">—</td>
                                                                                <td className="align-middle text-center table-cells">—</td>
                                                                                <td className="align-middle text-center table-cells">—</td>
                                                                                <td className="align-middle">
                                                                                    <span className="text-muted small">—</span>
                                                                                </td>
                                                                            </tr>
                                                                            );
                                                                        })}
                                                                    </>
                                                                )}
                                                            </>
                                                        );
                                                    })()}
                                                </tbody>
                                            </table>
                                        </div>
                                        {/* Mobile/Tablet Card View for Restock Products */}
                                        <div className="col-lg-12 d-lg-none">
                                            <div className="row g-3">
                                                {(() => {
                                                    const regularItems = updatedProducts?.filter((item: any) => item.inventory_source !== "rotational" && item.inventory_source !== "custom") ?? [];
                                                    const customItems = updatedProducts?.filter((item: any) => item.inventory_source === "custom") ?? [];
                                                    return (
                                                        <>
                                                            {regularItems.map((item: any, regularIndex: number) => {
                                                                const index = updatedProducts?.findIndex((p: any) => p === item) ?? regularIndex;
                                                                return (
                                                    <div key={`normal-mobile-${item.product_id}-${index}`} className="col-12 mb-3">
                                                        <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '8px' }}>
                                                            <div className="card-body p-3">
                                                                {/* Product Number - Top Left */}
                                                                <div className="position-absolute top-0 start-0 m-2">
                                                                    <span className="badge bg-secondary fs-6">Product {index + 1}</span>
                                                                </div>
                                                                {/* Product Name - Top Row */}
                                                                <div className="mb-3 mt-4">
                                                                    <h6>Product Name</h6>
                                                                    <h6 className="card-title mb-0 fw-medium">
                                                                        {item.product_name}
                                                                    </h6>
                                                                </div>

                                                                {/* SKU and Status - Second Row */}
                                                                <div className="row g-2 mb-3">
                                                                    <div className="col-6">
                                                                        <h6>SKU</h6>
                                                                        <span className="fw-medium text-ellipsis">{displayRemovalWorkOrderSku(item)}</span>
                                                                    </div>
                                                                    <div className="col-6">
                                                                        <h6>Product Location Status</h6>
                                                                        <StatusBadge status={item.current_status?.replace(/_/g, " ") || "-"} />
                                                                    </div>
                                                                </div>

                                                                {/* Quantity to Remove - Third Row */}
                                                                <div className="mb-3">
                                                                    <h6>Qty to Remove</h6>
                                                                    <span className="fw-medium">{item.quantity_to_be_removed}</span>
                                                                </div>

                                                                {/* Restock, Lost, Damaged - Fourth Row */}
                                                                <div className="row g-2 mb-3">
                                                                    <div className="col-4">
                                                                        <label className="form-label small mb-1">Re-stock</label>
                                                                        <input
                                                                            type="number"
                                                                            className="form-control form-control-sm text-center"
                                                                            min={0}
                                                                            max={getMaxQty(item)}
                                                                            value={item.restock_quantity || ''}
                                                                            placeholder="0"
                                                                            disabled={shouldDisableField(item, "restock_quantity")}
                                                                            data-path={`normal[${index}].restock`}
                                                                            onChange={(e) => {
                                                                                const rawValue = e.target.value;
                                                                                const qty = rawValue === '' ? 0 : parseInt(rawValue) || 0;
                                                                                updateProductField(index, "restock_quantity", qty);
                                                                            }}
                                                                            onBlur={(e) => {
                                                                                if (e.target.value === '') {
                                                                                    updateProductField(index, "restock_quantity", 0);
                                                                                }
                                                                            }}
                                                                        />
                                                                    </div>
                                                                    <div className="col-4">
                                                                        <label className="form-label small mb-1">Lost</label>
                                                                        <input
                                                                            type="number"
                                                                            className="form-control form-control-sm text-center"
                                                                            min={0}
                                                                            max={getMaxQty(item)}
                                                                            value={item.writeoff_quantity || ''}
                                                                            placeholder="0"
                                                                            disabled={shouldDisableField(item, "writeoff_quantity")}
                                                                            data-path={`normal[${index}].writeoff`}
                                                                            onChange={(e) => {
                                                                                const rawValue = e.target.value;
                                                                                const qty = rawValue === '' ? 0 : parseInt(rawValue) || 0;
                                                                                updateProductField(index, "writeoff_quantity", qty);
                                                                            }}
                                                                            onBlur={(e) => {
                                                                                if (e.target.value === '') {
                                                                                    updateProductField(index, "writeoff_quantity", 0);
                                                                                }
                                                                            }}
                                                                        />
                                                                    </div>
                                                                    <div className="col-4">
                                                                        <label className="form-label small mb-1">Damaged</label>
                                                                        <input
                                                                            type="number"
                                                                            className="form-control form-control-sm text-center"
                                                                            min={0}
                                                                            max={getMaxQty(item)}
                                                                            value={item.damaged_quantity || ''}
                                                                            placeholder="0"
                                                                            disabled={shouldDisableField(item, "damaged_quantity")}
                                                                            data-path={`normal[${index}].damaged`}
                                                                            onChange={(e) => {
                                                                                const rawValue = e.target.value;
                                                                                const qty = rawValue === '' ? 0 : parseInt(rawValue) || 0;
                                                                                updateProductField(index, "damaged_quantity", qty);
                                                                            }}
                                                                            onBlur={(e) => {
                                                                                if (e.target.value === '') {
                                                                                    updateProductField(index, "damaged_quantity", 0);
                                                                                }
                                                                            }}
                                                                        />
                                                                    </div>
                                                                </div>

                                                                {/* Bin Location - Fifth Row */}
                                                                <div className="mb-3">
                                                                    <label className="form-label small mb-1">Bin Location</label>
                                                                    {removalOrder?.status === 600 ? (
                                                                        <input
                                                                            type="text"
                                                                            className="form-control form-control-sm"
                                                                            value={productNotes[item.product_id]?.bin_location_display || ""}
                                                                            disabled={true}
                                                                            title={productNotes[item.product_id]?.bin_location_display || ""}
                                                                            data-path={`normal[${index}].binLocation`}
                                                                        />
                                                                    ) : (
                                                                        (() => {
                                                                            const productStorage = item?.storage_locations?.[0]?.location?.[0];
                                                                            const hasValidStorageBin =
                                                                                productStorage?.bin_id &&
                                                                                productStorage?.warehouse_name &&
                                                                                productStorage?.zone_name &&
                                                                                productStorage?.rack_name &&
                                                                                productStorage?.bin_name;

                                                                            const storageBinOption = hasValidStorageBin
                                                                                ? {
                                                                                    value: productStorage.bin_id,
                                                                                    label: `${productStorage.warehouse_name} > ${productStorage.zone_name} > ${productStorage.rack_name} > ${productStorage.bin_name}`,
                                                                                    bin: {
                                                                                        warehouse_id: productStorage.warehouse_id,
                                                                                        warehouse: productStorage.warehouse_name,
                                                                                        zone_id: productStorage.zone_id,
                                                                                        zone: productStorage.zone_name,
                                                                                        rack_id: productStorage.rack_id,
                                                                                        rack: productStorage.rack_name,
                                                                                        bin_id: productStorage.bin_id,
                                                                                        bin: productStorage.bin_name
                                                                                    }
                                                                                }
                                                                                : null;

                                                                            // Build current value - prioritize user selection (same as Assets table)
                                                                            let currentValue = null;
                                                                            if (productNotes[item.product_id]?.bin_location) {
                                                                                // User has selected a bin location - show this FIRST
                                                                                currentValue = {
                                                                                    value: productNotes[item.product_id].bin_location,
                                                                                    label: productNotes[item.product_id].bin_location_display || productNotes[item.product_id].bin_location,
                                                                                };
                                                                            } else if (storageBinOption) {
                                                                                // Fall back to storage location if no selection made
                                                                                currentValue = storageBinOption;
                                                                            }

                                                                            return (
                                                                                <div title={currentValue?.label || "Select bin location"}>
                                                                                    <AsyncPaginate
                                                                                        key={`bin-normal-mobile-${item.product_id}-${item.restock_quantity || 0}`}
                                                                                        classNamePrefix="rs"
                                                                                        placeholder="Search bin location..."
                                                                                        value={currentValue}
                                                                                        loadOptions={loadBinLocationOptions(item.restock_quantity || 1)}
                                                                                        onChange={(option) => {
                                                                                            updateNoteField(item.product_id, "bin_location", option?.value || "", option?.label || "");
                                                                                        }}
                                                                                        additional={{ page: 1 }}
                                                                                        defaultOptions
                                                                                        debounceTimeout={300}
                                                                                        isDisabled={!shouldEnableBinLocation(item)}
                                                                                        components={{ DropdownIndicator }}
                                                                                        styles={customSelectStyles}
                                                                                        data-path={`normal[${index}].binLocation`}
                                                                                    />
                                                                                </div>
                                                                            );
                                                                        })()
                                                                    )}
                                                                </div>

                                                                {/* Note - Bottom Row */}
                                                                <div className="mb-3">
                                                                    <label className="form-label small mb-1">Note</label>
                                                                    <textarea
                                                                        className="form-control form-control-sm br-8"
                                                                        value={productNotes[item.product_id]?.note || ""}
                                                                        placeholder="Enter note..."
                                                                        onChange={(e) =>
                                                                            updateNoteField(item.product_id, "note", e.target.value)
                                                                        }
                                                                        disabled={removalOrder?.status === 600}
                                                                        rows={2}
                                                                        style={{ resize: 'none' }}
                                                                    ></textarea>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                                );
                                                            })}
                                                            {customItems.length > 0 && (
                                                                <>
                                                                    <div className="col-12">
                                                                        <h6 className="small fw-semibold text-muted mb-2">Custom Products</h6>
                                                                    </div>
                                                                    {customItems.map((item: any, customIndex: number) => {
                                                                        const index = updatedProducts?.findIndex((p: any) => p === item) ?? customIndex;
                                                                        return (
                                                                        <div key={`custom-mobile-500-${item.product_id}-${index}`} className="col-12 mb-3">
                                                                            <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '8px' }}>
                                                                                <div className="card-body p-3">
                                                                                    <div className="mb-3 mt-2">
                                                                                        <h6>Product Name</h6>
                                                                                        <h6 className="card-title mb-0 fw-medium">{item.product_name}</h6>
                                                                                    </div>
                                                                                    <div className="row g-2 mb-3">
                                                                                        <div className="col-6">
                                                                                            <h6>SKU</h6>
                                                                                            <span className="fw-medium text-ellipsis">{displayRemovalWorkOrderSku(item)}</span>
                                                                                        </div>
                                                                                        <div className="col-6">
                                                                                            <h6>Product Location Status</h6>
                                                                                            <StatusBadge status={item.current_status?.replace(/_/g, " ") || "-"} />
                                                                                        </div>
                                                                                    </div>
                                                                                    <div className="mb-3">
                                                                                        <h6>Qty to Remove</h6>
                                                                                        <span className="fw-medium">{item.quantity_to_be_removed ?? "—"}</span>
                                                                                    </div>
                                                                                    <div className="row g-2 mb-3">
                                                                                        <div className="col-4">
                                                                                            <label className="form-label small mb-1">Re-stock</label>
                                                                                            <input
                                                                                                type="number"
                                                                                                className="form-control form-control-sm text-center"
                                                                                                min={0}
                                                                                                max={getMaxQty(item)}
                                                                                                value={item.restock_quantity || ''}
                                                                                                placeholder="0"
                                                                                                disabled={shouldDisableField(item, "restock_quantity")}
                                                                                                data-path={`normal[${index}].restock`}
                                                                                                onChange={(e) => {
                                                                                                    const rawValue = e.target.value;
                                                                                                    const qty = rawValue === '' ? 0 : parseInt(rawValue) || 0;
                                                                                                    updateProductField(index, "restock_quantity", qty);
                                                                                                }}
                                                                                                onBlur={(e) => {
                                                                                                    if (e.target.value === '') {
                                                                                                        updateProductField(index, "restock_quantity", 0);
                                                                                                    }
                                                                                                }}
                                                                                            />
                                                                                        </div>
                                                                                        <div className="col-4">
                                                                                            <label className="form-label small mb-1">Lost</label>
                                                                                            <span className="form-control form-control-sm text-center d-block" style={{ backgroundColor: "#e9ecef" }}>—</span>
                                                                                        </div>
                                                                                        <div className="col-4">
                                                                                            <label className="form-label small mb-1">Damaged</label>
                                                                                            <span className="form-control form-control-sm text-center d-block" style={{ backgroundColor: "#e9ecef" }}>—</span>
                                                                                        </div>
                                                                                    </div>
                                                                                    <div className="mb-3">
                                                                                        <label className="form-label small mb-1">Bin Location</label>
                                                                                        <span className="form-control form-control-sm d-block" style={{ backgroundColor: "#e9ecef" }}>—</span>
                                                                                    </div>
                                                                                    <div className="mb-0">
                                                                                        <label className="form-label small mb-1">Note</label>
                                                                                        <span className="text-muted small">—</span>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                        );
                                                                    })}
                                                                </>
                                                            )}
                                                        </>
                                                    );
                                                })()}
                                            </div>
                                        </div>
                                    </div>


                                    <div className="row col-lg-12 mb-3 card-body shadow-lg p-3">
                                        {updatedProducts?.filter((item: any) => item.inventory_source == "rotational").length > 0 &&
                                            <>
                                                <div className="d-flex mb-3">
                                                    <span
                                                        aria-hidden="true"
                                                        className="image-icons"
                                                        style={{
                                                            backgroundColor: "#4f39f6",
                                                            WebkitMaskImage: `url(${ProductSelection})`,
                                                            maskImage: `url(${ProductSelection})`,
                                                        }}
                                                    />
                                                    <SectionHeader title="Assets" icon="" variant="success" size="md" divider />
                                                </div>
                                                <div className="col-lg-12 d-none d-lg-block" id="row-overlap">
                                                    <table className="border table table-striped">
                                                        <thead className="">
                                                            <tr>
                                                                <th className="text-center table-headers" style={{ width: "15%" }}>Product Name</th>
                                                                <th className="text-center table-headers" style={{ width: "7%" }}>SKU</th>
                                                                <th className="text-center table-headers" style={{ width: "3%" }}>Qty </th>
                                                                {/* <th className="text-center table-headers" style={{ width: "7%" }}>Serial No.</th> */}
                                                                {/* {
                                                                    (removalOrder?.status === 500 || removalOrder?.status === 600) && <> */}
                                                                <th className="text-center table-headers" style={{ width: "7%" }}>Re-stock </th>
                                                                <th className="text-center table-headers" style={{ width: "15%" }}>Bin Location</th>
                                                                {/* <th className="text-center table-headers">Write Off </th> */}
                                                                <th className="text-center table-headers" style={{ width: "7%" }}>Lost </th>
                                                                <th className="text-center table-headers" style={{ width: "7%" }}>Damaged </th>
                                                                <th className="text-center table-headers" style={{ width: "7%" }}>Repair</th>
                                                                <th className="text-center table-headers" style={{ width: "10%" }}>Note</th>
                                                                {/* </>
                                                                } */}
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {updatedProducts
                                                                ?.filter((item: any) => item.inventory_source === "rotational" && Array.isArray(item.serial_allocations))
                                                                ?.flatMap((item: any) =>
                                                                    item.serial_allocations?.map((serial: any, i: number) => (
                                                                        <tr key={`rotational-${item.product_id}-${serial.serial_id}`}>
                                                                            <td className="text-center table-cells align-middle text-ellipsis">{item.product_name || "—"}</td>
                                                                            <td className="text-center table-cells align-middle text-ellipsis">{displayRemovalWorkOrderSku(item) || "—"}</td>
                                                                            <td className="text-center table-cells align-middle">{1}</td>
                                                                            {/* <td className="text-center table-cells align-middle">{serial.serial_code}</td> */}
                                                                            {/* <td className="text-center table-cells align-middle">
                                                            <Select
                                                                options={[{ value: "on_site", label: "On Site" }, { value: "with_supplier", label: "With Supplier" }]}
                                                                value={{
                                                                    value: updatedRotationalUnits[serial.serial_id]?.current_status || item.current_status,
                                                                    label: updatedRotationalUnits[serial.serial_id]?.current_status || item.current_status,
                                                                }}
                                                                onChange={option =>
                                                                    updateRotationalUnit(serial.serial_id, "current_status", option?.value || "", item.available_quantity)
                                                                }
                                                            />
                                                        </td> */}

                                                                            {/* {
                                                                                (removalOrder?.status === 500 || removalOrder?.status === 600) && <> */}
                                                                            <td className="align-middle">
                                                                                <input
                                                                                    type="number"
                                                                                    className="form-control table-cells"
                                                                                    min={0}
                                                                                    max={1}
                                                                                    value={updatedRotationalUnits[serial.serial_id]?.restock_quantity || ''}
                                                                                    placeholder="0"
                                                                                    disabled={shouldDisableRotationalField(serial.serial_id, "restock_quantity")}
                                                                                    data-path={`rotational[${serial.serial_id}].restock`}
                                                                                    onChange={(e) => {
                                                                                        const rawValue = e.target.value;
                                                                                        const qty = rawValue === '' ? 0 : parseInt(rawValue) || 0;
                                                                                        updateRotationalUnit(serial.serial_id, "restock_quantity", qty, item.available_quantity);
                                                                                    }}
                                                                                    onBlur={(e) => {
                                                                                        if (e.target.value === '') {
                                                                                            updateRotationalUnit(serial.serial_id, "restock_quantity", 0, item.available_quantity);
                                                                                        }
                                                                                    }}
                                                                                />
                                                                            </td>
                                                                            <td className="align-middle">
                                                                                {removalOrder?.status === 600 ? (
                                                                                    <input
                                                                                        type="text"
                                                                                        className="form-control table-cells"
                                                                                        value={
                                                                                            updatedRotationalUnits[serial.serial_id]?.bin_location_display ||
                                                                                            [serial.warehouse_name, serial.zone_name, serial.rack_name, serial.bin_name].filter(Boolean).join(' > ') ||
                                                                                            ""
                                                                                        }
                                                                                        disabled={true}
                                                                                        title={
                                                                                            updatedRotationalUnits[serial.serial_id]?.bin_location_display ||
                                                                                            [serial.warehouse_name, serial.zone_name, serial.rack_name, serial.bin_name].filter(Boolean).join(' > ') ||
                                                                                            ""
                                                                                        }
                                                                                        data-path={`rotational[${serial.serial_id}].binLocation`}
                                                                                    />
                                                                                ) : (
                                                                                    (() => {
                                                                                        // Create fallback option from serial data
                                                                                        const fallbackLabel = [serial.warehouse_name, serial.zone_name, serial.rack_name, serial.bin_name].filter(Boolean).join(' > ');
                                                                                        const fallbackOption = fallbackLabel ? {
                                                                                            value: serial.bin_id,
                                                                                            label: fallbackLabel,
                                                                                            bin: {
                                                                                                warehouse_id: serial.warehouse_id,
                                                                                                warehouse_name: serial.warehouse_name,
                                                                                                zone_id: serial.zone_id,
                                                                                                zone_name: serial.zone_name,
                                                                                                rack_id: serial.rack_id,
                                                                                                rack_name: serial.rack_name,
                                                                                                bin_id: serial.bin_id,
                                                                                                bin_name: serial.bin_name,
                                                                                            }
                                                                                        } : null;

                                                                                        // Build current value from updatedRotationalUnits
                                                                                        let currentValue = null;
                                                                                        if (updatedRotationalUnits[serial.serial_id]?.bin_location) {
                                                                                            currentValue = {
                                                                                                value: updatedRotationalUnits[serial.serial_id].bin_location,
                                                                                                label: updatedRotationalUnits[serial.serial_id].bin_location_display || updatedRotationalUnits[serial.serial_id].bin_location,
                                                                                            };
                                                                                        } else if (fallbackOption) {
                                                                                            currentValue = fallbackOption;
                                                                                        }

                                                                                        const restockQty = updatedRotationalUnits[serial.serial_id]?.restock_quantity || 0;

                                                                                        return (
                                                                                            <div title={currentValue?.label || "Select bin location"}>
                                                                                                <AsyncPaginate
                                                                                                    key={`bin-rotational-${serial.serial_id}-${restockQty}`}
                                                                                                    classNamePrefix="rs"
                                                                                                    placeholder="Search bin location..."
                                                                                                    value={currentValue || null}
                                                                                                    loadOptions={loadBinLocationOptions(restockQty || 1)}
                                                                                                    onChange={(option) => {
                                                                                                        updateRotationalUnit(serial.serial_id, "bin_location", option?.value || "", item.available_quantity, option?.label || "");
                                                                                                    }}
                                                                                                    additional={{ page: 1 }}
                                                                                                    defaultOptions
                                                                                                    debounceTimeout={300}
                                                                                                    isDisabled={!shouldEnableRotationalBinLocation(serial.serial_id)}
                                                                                                    components={{ DropdownIndicator }}
                                                                                                    styles={customSelectStyles}
                                                                                                    data-path={`rotational[${serial.serial_id}].binLocation`}
                                                                                                />
                                                                                            </div>
                                                                                        );
                                                                                    })()
                                                                                )}
                                                                            </td>
                                                                            {/* <td>
                                                            <input
                                                                type="number"
                                                                className="form-control table-cells align-middle"
                                                                value={updatedRotationalUnits[serial.serial_id]?.writeoff_quantity ?? item.available_quantity}
                                                                disabled
                                                            />
                                                        </td> */}

                                                                            <td className="align-middle">
                                                                                <input
                                                                                    type="number"
                                                                                    className="form-control table-cells"
                                                                                    min={0}
                                                                                    max={1}
                                                                                    value={updatedRotationalUnits[serial.serial_id]?.writeoff_quantity || ''}
                                                                                    placeholder="0"
                                                                                    disabled={shouldDisableRotationalField(serial.serial_id, "writeoff_quantity")}
                                                                                    data-path={`rotational[${serial.serial_id}].writeoff`}
                                                                                    onChange={(e) => {
                                                                                        const rawValue = e.target.value;
                                                                                        const qty = rawValue === '' ? 0 : parseInt(rawValue) || 0;
                                                                                        updateRotationalUnit(serial.serial_id, "writeoff_quantity", qty);
                                                                                    }}
                                                                                    onBlur={(e) => {
                                                                                        if (e.target.value === '') {
                                                                                            updateRotationalUnit(serial.serial_id, "writeoff_quantity", 0);
                                                                                        }
                                                                                    }}
                                                                                />
                                                                            </td>
                                                                            <td className="align-middle">
                                                                                <input
                                                                                    type="number"
                                                                                    className="form-control table-cells"
                                                                                    min={0}
                                                                                    max={1}
                                                                                    value={updatedRotationalUnits[serial.serial_id]?.damaged_quantity || ''}
                                                                                    placeholder="0"
                                                                                    disabled={shouldDisableRotationalField(serial.serial_id, "damaged_quantity")}
                                                                                    data-path={`rotational[${serial.serial_id}].damaged`}
                                                                                    onChange={(e) => {
                                                                                        const rawValue = e.target.value;
                                                                                        const qty = rawValue === '' ? 0 : parseInt(rawValue) || 0;
                                                                                        updateRotationalUnit(serial.serial_id, "damaged_quantity", qty);
                                                                                    }}
                                                                                    onBlur={(e) => {
                                                                                        if (e.target.value === '') {
                                                                                            updateRotationalUnit(serial.serial_id, "damaged_quantity", 0);
                                                                                        }
                                                                                    }}
                                                                                />
                                                                            </td>
                                                                            <td className="align-middle">
                                                                                <input
                                                                                    type="number"
                                                                                    className="form-control"
                                                                                    min={0}
                                                                                    max={1}
                                                                                    value={updatedRotationalUnits[serial.serial_id]?.repair || ''}
                                                                                    placeholder="0"
                                                                                    disabled={shouldDisableRotationalField(serial.serial_id, "repair")}
                                                                                    data-path={`rotational[${serial.serial_id}].repair`}
                                                                                    onChange={(e) => {
                                                                                        const rawValue = e.target.value;
                                                                                        const qty = rawValue === '' ? 0 : parseInt(rawValue) || 0;
                                                                                        updateRotationalUnit(serial.serial_id, "repair", qty, item.available_quantity);
                                                                                    }}
                                                                                    onBlur={(e) => {
                                                                                        if (e.target.value === '') {
                                                                                            updateRotationalUnit(serial.serial_id, "repair", 0, item.available_quantity);
                                                                                        }
                                                                                    }}
                                                                                />
                                                                            </td>
                                                                            <td className="align-middle">
                                                                                <textarea
                                                                                    className="form-control table-cells br-8"
                                                                                    placeholder="Enter note..."
                                                                                    value={updatedRotationalUnits[serial.serial_id]?.note || ""}
                                                                                    onChange={e =>
                                                                                        updateRotationalUnit(serial.serial_id, "note", e.target.value, item.available_quantity)
                                                                                    }
                                                                                    disabled={removalOrder?.status === 600}
                                                                                    style={{ resize: 'none' }}
                                                                                ></textarea>
                                                                            </td>
                                                                            {/* </>
                                                                            } */}
                                                                        </tr>
                                                                    )))}
                                                        </tbody>
                                                    </table>
                                                </div>

                                                {/* Mobile/Tablet Card View for Assets */}
                                                <div className="col-lg-12 d-lg-none">
                                                    <div className="row g-3">
                                                        {updatedProducts
                                                            ?.filter((item: any) => item.inventory_source === "rotational" && Array.isArray(item.serial_allocations))
                                                            ?.flatMap((item: any) =>
                                                                item.serial_allocations?.map((serial: any, i: number) => (
                                                                    <div key={`rotational-mobile-${item.product_id}-${serial.serial_id}`} className="col-12 mb-3">
                                                                        <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '8px' }}>
                                                                            <div className="card-body p-3">
                                                                                {/* Product Number - Top Left */}
                                                                                <div className="position-absolute top-0 start-0 m-2">
                                                                                    <span className="badge bg-secondary fs-6">Asset {i + 1}</span>
                                                                                </div>
                                                                                {/* Product Name - Top Row */}
                                                                                <div className="mb-3 mt-4">
                                                                                    <h6>Product Name</h6>
                                                                                    <h6 className="card-title mb-0 fw-medium">
                                                                                        {item.product_name || "—"}
                                                                                    </h6>
                                                                                </div>

                                                                                {/* SKU and Quantity - Second Row */}
                                                                                <div className="row g-2 mb-3">
                                                                                    <div className="col-6">
                                                                                        <h6>SKU</h6>
                                                                                        <span className="fw-medium text-ellipsis">{displayRemovalWorkOrderSku(item) || "—"}</span>
                                                                                    </div>
                                                                                    <div className="col-6">
                                                                                        <h6>Qty</h6>
                                                                                        <span className="fw-medium">{item.available_quantity}</span>
                                                                                    </div>
                                                                                </div>

                                                                                {/* Serial Number - Third Row */}
                                                                                {/* <div className="mb-3">
                                                                                    <h6>Serial No.</h6>
                                                                                    <span className="fw-medium">{serial.serial_code}</span>
                                                                                </div> */}

                                                                                {/* Bin Location - Fifth Row */}
                                                                                <div className="mb-3">
                                                                                    <label className="form-label small mb-1">Bin Location</label>
                                                                                    {removalOrder?.status === 600 ? (
                                                                                        <input
                                                                                            type="text"
                                                                                            className="form-control form-control-sm"
                                                                                            value={
                                                                                                updatedRotationalUnits[serial.serial_id]?.bin_location_display ||
                                                                                                [serial.warehouse_name, serial.zone_name, serial.rack_name, serial.bin_name].filter(Boolean).join(' > ') ||
                                                                                                ""
                                                                                            }
                                                                                            disabled={true}
                                                                                            title={
                                                                                                updatedRotationalUnits[serial.serial_id]?.bin_location_display ||
                                                                                                [serial.warehouse_name, serial.zone_name, serial.rack_name, serial.bin_name].filter(Boolean).join(' > ') ||
                                                                                                ""
                                                                                            }
                                                                                            data-path={`rotational[${serial.serial_id}].binLocation`}
                                                                                        />
                                                                                    ) : (
                                                                                        (() => {
                                                                                            // Create fallback option from serial data
                                                                                            const fallbackLabel = [serial.warehouse_name, serial.zone_name, serial.rack_name, serial.bin_name].filter(Boolean).join(' > ');
                                                                                            const fallbackOption = fallbackLabel ? {
                                                                                                value: serial.bin_id,
                                                                                                label: fallbackLabel,
                                                                                                bin: {
                                                                                                    warehouse_id: serial.warehouse_id,
                                                                                                    warehouse_name: serial.warehouse_name,
                                                                                                    zone_id: serial.zone_id,
                                                                                                    zone_name: serial.zone_name,
                                                                                                    rack_id: serial.rack_id,
                                                                                                    rack_name: serial.rack_name,
                                                                                                    bin_id: serial.bin_id,
                                                                                                    bin_name: serial.bin_name,
                                                                                                }
                                                                                            } : null;

                                                                                            // Build current value from updatedRotationalUnits
                                                                                            let currentValue = null;
                                                                                            if (updatedRotationalUnits[serial.serial_id]?.bin_location) {
                                                                                                currentValue = {
                                                                                                    value: updatedRotationalUnits[serial.serial_id].bin_location,
                                                                                                    label: updatedRotationalUnits[serial.serial_id].bin_location_display || updatedRotationalUnits[serial.serial_id].bin_location,
                                                                                                };
                                                                                            } else if (fallbackOption) {
                                                                                                currentValue = fallbackOption;
                                                                                            }

                                                                                            const restockQty = updatedRotationalUnits[serial.serial_id]?.restock_quantity || 0;

                                                                                            return (
                                                                                                <div title={currentValue?.label || "Select bin location"}>
                                                                                                    <AsyncPaginate
                                                                                                        key={`bin-rotational-mobile-${serial.serial_id}-${restockQty}`}
                                                                                                        classNamePrefix="rs"
                                                                                                        placeholder="Search bin location..."
                                                                                                        value={currentValue || null}
                                                                                                        loadOptions={loadBinLocationOptions(restockQty || 1)}
                                                                                                        onChange={(option) => {
                                                                                                            updateRotationalUnit(serial.serial_id, "bin_location", option?.value || "", item.available_quantity, option?.label || "");
                                                                                                        }}
                                                                                                        additional={{ page: 1 }}
                                                                                                        defaultOptions
                                                                                                        debounceTimeout={300}
                                                                                                        isDisabled={!shouldEnableRotationalBinLocation(serial.serial_id)}
                                                                                                        components={{ DropdownIndicator }}
                                                                                                        styles={customSelectStyles}
                                                                                                        data-path={`rotational[${serial.serial_id}].binLocation`}
                                                                                                    />
                                                                                                </div>
                                                                                            );
                                                                                        })()
                                                                                    )}
                                                                                </div>

                                                                                {/* Restock, Lost, Damaged, Repair - Fourth Row */}
                                                                                <div className="row g-2 mb-3">
                                                                                    <div className="col-3">
                                                                                        <label className="form-label small mb-1">Re-stock</label>
                                                                                        <input
                                                                                            type="number"
                                                                                            className="form-control form-control-sm text-center"
                                                                                            min={0}
                                                                                            max={1}
                                                                                            value={updatedRotationalUnits[serial.serial_id]?.restock_quantity || ''}
                                                                                            placeholder="0"
                                                                                            disabled={shouldDisableRotationalField(serial.serial_id, "restock_quantity")}
                                                                                            data-path={`rotational[${serial.serial_id}].restock`}
                                                                                            onChange={(e) => {
                                                                                                const rawValue = e.target.value;
                                                                                                const qty = rawValue === '' ? 0 : parseInt(rawValue) || 0;
                                                                                                updateRotationalUnit(serial.serial_id, "restock_quantity", qty, item.available_quantity);
                                                                                            }}
                                                                                            onBlur={(e) => {
                                                                                                if (e.target.value === '') {
                                                                                                    updateRotationalUnit(serial.serial_id, "restock_quantity", 0, item.available_quantity);
                                                                                                }
                                                                                            }}
                                                                                        />
                                                                                    </div>
                                                                                    <div className="col-3">
                                                                                        <label className="form-label small mb-1">Lost</label>
                                                                                        <input
                                                                                            type="number"
                                                                                            className="form-control form-control-sm text-center"
                                                                                            min={0}
                                                                                            max={1}
                                                                                            value={updatedRotationalUnits[serial.serial_id]?.writeoff_quantity || ''}
                                                                                            placeholder="0"
                                                                                            disabled={shouldDisableRotationalField(serial.serial_id, "writeoff_quantity")}
                                                                                            data-path={`rotational[${serial.serial_id}].writeoff`}
                                                                                            onChange={(e) => {
                                                                                                const rawValue = e.target.value;
                                                                                                const qty = rawValue === '' ? 0 : parseInt(rawValue) || 0;
                                                                                                updateRotationalUnit(serial.serial_id, "writeoff_quantity", qty);
                                                                                            }}
                                                                                            onBlur={(e) => {
                                                                                                if (e.target.value === '') {
                                                                                                    updateRotationalUnit(serial.serial_id, "writeoff_quantity", 0);
                                                                                                }
                                                                                            }}
                                                                                        />
                                                                                    </div>
                                                                                    <div className="col-3">
                                                                                        <label className="form-label small mb-1">Damaged</label>
                                                                                        <input
                                                                                            type="number"
                                                                                            className="form-control form-control-sm text-center"
                                                                                            min={0}
                                                                                            max={1}
                                                                                            value={updatedRotationalUnits[serial.serial_id]?.damaged_quantity || ''}
                                                                                            placeholder="0"
                                                                                            disabled={shouldDisableRotationalField(serial.serial_id, "damaged_quantity")}
                                                                                            data-path={`rotational[${serial.serial_id}].damaged`}
                                                                                            onChange={(e) => {
                                                                                                const rawValue = e.target.value;
                                                                                                const qty = rawValue === '' ? 0 : parseInt(rawValue) || 0;
                                                                                                updateRotationalUnit(serial.serial_id, "damaged_quantity", qty);
                                                                                            }}
                                                                                            onBlur={(e) => {
                                                                                                if (e.target.value === '') {
                                                                                                    updateRotationalUnit(serial.serial_id, "damaged_quantity", 0);
                                                                                                }
                                                                                            }}
                                                                                        />
                                                                                    </div>
                                                                                    <div className="col-3">
                                                                                        <label className="form-label small mb-1">Repair</label>
                                                                                        <input
                                                                                            type="number"
                                                                                            className="form-control form-control-sm text-center"
                                                                                            min={0}
                                                                                            max={1}
                                                                                            value={updatedRotationalUnits[serial.serial_id]?.repair || ''}
                                                                                            placeholder="0"
                                                                                            disabled={shouldDisableRotationalField(serial.serial_id, "repair")}
                                                                                            data-path={`rotational[${serial.serial_id}].repair`}
                                                                                            onChange={(e) => {
                                                                                                const rawValue = e.target.value;
                                                                                                const qty = rawValue === '' ? 0 : parseInt(rawValue) || 0;
                                                                                                updateRotationalUnit(serial.serial_id, "repair", qty, item.available_quantity);
                                                                                            }}
                                                                                            onBlur={(e) => {
                                                                                                if (e.target.value === '') {
                                                                                                    updateRotationalUnit(serial.serial_id, "repair", 0, item.available_quantity);
                                                                                                }
                                                                                            }}
                                                                                        />
                                                                                    </div>
                                                                                </div>

                                                                                {/* Note - Bottom Row */}
                                                                                <div className="mb-3">
                                                                                    <label className="form-label small mb-1">Note</label>
                                                                                    <textarea
                                                                                        className="form-control form-control-sm br-8"
                                                                                        value={updatedRotationalUnits[serial.serial_id]?.note || ""}
                                                                                        onChange={e =>
                                                                                            updateRotationalUnit(serial.serial_id, "note", e.target.value, item.available_quantity)
                                                                                        }
                                                                                        disabled={removalOrder?.status === 600}
                                                                                        rows={2}
                                                                                        placeholder="Enter note..."
                                                                                        style={{ resize: 'none' }}
                                                                                    ></textarea>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                ))
                                                            )}
                                                    </div>
                                                </div>
                                            </>
                                        }
                                    </div>
                                </div>
                                <div className="row col-lg-12 mb-3">
                                    <div className="col-lg-6 mb-3 mb-lg-0 pe-0">
                                        <Instructions
                                            title="Instructions"
                                            mode="instructions"
                                            loginType="signage"
                                            instructions={instructions}
                                            placeholder="Add instructions here"
                                            rows={4}
                                            onSave={handleSaveInstructionsOnly}
                                            className="mb-3"
                                            iconColor="#f54a00"
                                            iconBackground="#EAF7F1"
                                        />
                                    </div>
                                    <div className="align-items-center card-body col-lg-5 justify-content-end ms-0 ms-2 ms-lg-0 ms-lg-5 offset-1 p-4 shadow-lg">
                                        <div className="align-items-center d-flex mb-3">
                                            <span
                                                aria-hidden="true"
                                                className="image-icons"
                                                style={{
                                                    backgroundColor: "#00a63e",
                                                    WebkitMaskImage: `url(${Doller})`,
                                                    maskImage: `url(${Doller})`,
                                                }}
                                            />
                                            <SectionHeader title="Cost Breakdown" icon="" color="" size="lg" divider />
                                        </div>

                                        {isEdit && (Number(removalOrder?.status ?? 0) <= 200) && (
                                            <OrderDiscountCodeField
                                                className="mb-2"
                                                value={couponInput}
                                                appliedCode={discountDisplay.code}
                                                discountAmount={discountDisplay.amount}
                                                disabled={isRecomputing}
                                                onChange={setCouponInput}
                                                onApply={(code) => {
                                                    void applyDiscountCode(code);
                                                }}
                                                onClear={() => {
                                                    void clearDiscountCode();
                                                }}
                                            />
                                        )}
                                        {zeroOrNegativeMsg && <div className="text-danger mt-2">{zeroOrNegativeMsg}</div>}

                                        <div className="d-flex justify-content-between align-items-center py-2">
                                            <span className="fw-medium">Removal Cost:</span>
                                            <span className="fw-semibold">${Number(removalCost || 0).toFixed(2)}</span>
                                        </div>

                                        {showCostBreakdownDiscount && (
                                            <div className="d-flex justify-content-between align-items-center py-2">
                                                <span className="fw-medium" style={{ color: "#00a63e" }}>
                                                    Discount ({costBreakdownDiscount.code || formik.values.discount_code || "—"}):
                                                </span>
                                                <span className="fw-semibold" style={{ color: "#00a63e" }}>
                                                    -${effectiveDiscountAmount.toFixed(2)}
                                                </span>
                                            </div>
                                        )}

                                        <div className="d-flex justify-content-between align-items-center py-2">
                                            <span className="fw-medium">Sub-total:</span>
                                            {/* <span className="fw-semibold">${Number(formik.values.totals?.subtotal_amount || formik.values.subtotal || 0).toFixed(2)}</span> */}
                                            <span className="fw-semibold text-muted">${Number(removalCost || 0).toFixed(2)}</span>
                                        </div>

                                        <div className="d-flex justify-content-between align-items-center py-2">
                                            {(() => {
                                                const { labelText, tooltip } = buildSpecialFeeCostBreakdownDisplay(selectedSpecialFees);
                                                return (
                                                    <>
                                                        <span className="fw-medium me-2" style={{ minWidth: 0 }} title={tooltip}>
                                                            {labelText}:
                                                        </span>
                                                        <span className="fw-semibold flex-shrink-0">
                                                            ${Number(formik.values.totals?.specialfee_subtotal || 0).toFixed(2)}
                                                        </span>
                                                    </>
                                                );
                                            })()}
                                        </div>

                                        <div className="d-flex justify-content-between align-items-center py-2">
                                            <span className="fw-medium">Tax:</span>
                                            <span className="fw-semibold">
                                                ${resolvedTaxTotal.toFixed(2)}
                                            </span>
                                        </div>

                                        {formik.values.totals && (
                                            <div className="mb-2">
                                                <button
                                                    type="button"
                                                    className="btn btn-link btn-sm p-0 text-decoration-none"
                                                    onClick={() => setShowTaxDetails(!showTaxDetails)}
                                                >
                                                    {showTaxDetails ? "Hide" : "Show"} Tax Details
                                                </button>
                                                {showTaxDetails && (
                                                    <div className="small text-muted mt-2">
                                                        <div>Removal Tax: ${Number(formik.values.totals.removal_tax_amount || 0).toFixed(2)}</div>
                                                        <div>Special Fee Tax: ${Number(formik.values.totals.specialfee_tax_amount || 0).toFixed(2)}</div>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* ✅ Show Special Fee Details if present (View Mode) */}
                                        {Viewmode && selectedSpecialFees && selectedSpecialFees.length > 0 && (
                                            <div className="mt-2 pt-2" style={{ borderTop: "1px solid #dee2e6" }}>
                                                <h6 className="mb-2">Special Fees Applied:</h6>
                                                {selectedSpecialFees.map((fee: any, idx: number) => (
                                                    <div key={idx} className="d-flex justify-content-between align-items-center py-1 small">
                                                        <span className="text-muted">{fee.name}</span>
                                                        <span className="fw-semibold">${Number(fee.amount || 0).toFixed(2)}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        <hr className="my-3" />

                                        <div className="d-flex justify-content-between align-items-center py-2">
                                            <span className="fw-bold fs-18">Total Amount:</span>
                                            <span className="fw-bold fs-18 text-primary">${currentGrandTotal}</span>
                                        </div>

                                        <hr className="my-3" />

                                        <div className="d-flex justify-content-between align-items-center py-2">
                                            <span className="fw-medium">Already Paid:</span>
                                            <span className="fw-semibold"> ${totalPaid.toFixed(2)}</span>
                                        </div>

                                        <div className="d-flex justify-content-between align-items-center py-2">
                                            <span className="fw-medium">Remaining Balance:</span>
                                            <span className="fw-semibold">
                                                {removalOrder?.payment_status === "Overpaid"
                                                    ? `+$${Math.abs(remaingamount).toFixed(2)}`
                                                    : ` $${remaingamount.toFixed(2)}`
                                                }
                                            </span>
                                        </div>

                                        {formik.values.enable_partial_payment && (
                                            <div className="d-flex justify-content-between align-items-center py-2">
                                                <span className="fw-medium">Partial Payment:</span>
                                                <span className="fw-semibold">${Number(formik.values.partial_amount || 0).toFixed(2)}</span>
                                            </div>
                                        )}
                                    </div>

                                    {/* ✅ Live Tax Buttons (TaxCloud only, View mode, not yet complete) */}
                                    {/* {Viewmode && taxType === "taxcloud" && removalOrder?.tax_status !== "complete" && (
                                        <div className="mt-3 d-flex gap-2">
                                            <button
                                                type="button"
                                                className="btn btn-primary table-addbutton br-8 btn-sm"
                                                onClick={handleGetLiveTax}
                                                disabled={liveTaxLoading}
                                            >
                                                {liveTaxLoading ? "Getting Live Tax…" : "Get Live Tax"}
                                            </button>

                                            {removalOrder?.tax_status === "draft" && (
                                                <button
                                                    type="button"
                                                    className="export-button border-0"
                                                    onClick={handleLiveTaxNext}
                                                >
                                                    Capture
                                                </button>
                                            )}
                                        </div>
                                    )} */}
                                </div>

                                <div className="row col-lg-12">
                                    <div className="mb-3">
                                        {(removalOrder?.status === 400 || removalOrder?.status === 500 || removalOrder?.status === 600) && (
                                            <ProofSection
                                                title="Proof of Removal"
                                                canEdit={!!canEditProof}
                                                existing={uploadedFileMeta}
                                                setExisting={setUploadedFileMeta}
                                                showNotifyCustomer={removalOrder?.status === 500}
                                                onNotifyCustomer={notifyCustomerProofAvailable}
                                                onRegisterUploadHandler={(uploadFn) => {
                                                    proofUploadHandlerRef.current = uploadFn;
                                                }}
                                                // uploadFile={uploadFile}
                                                onSubmitProof={async (newFiles) => {
                                                    await uploadProofOfRemovals(newFiles);
                                                }}
                                            />
                                        )}
                                    </div>
                                </div>

                                <div className="row mb-3 d-block d-lg-none">
                                    {
                                        (removalOrder?.payment_status === "Paid") ? <></> :
                                            <div className="col-lg-12 mb-3">
                                                {status <= 600 && Viewmode && (
                                                    <PaymentPanel
                                                        variant="signage"   // or brokerage/agent depending on type
                                                        remainingAmount={Number(remaingamount)}
                                                        orderStatus={removalOrder?.status ?? 0}
                                                        paymentStatus={removalOrder?.payment_status ?? ""}
                                                        selectedPaymentMethod={selectedPaymentMethod}
                                                        setSelectedPaymentMethod={handlePaymentMethodSelect}
                                                        formik={formik}
                                                        maxAllowed={maxAllowed}
                                                        paymentInfo={removalOrder?.payment_info ?? []}
                                                        dynamicMethods={paymentMethod} // [{ _id, name }]
                                                        onSubmitOffline={(key) => completeOrder(key)}
                                                        orderType="removalOrder"
                                                        onlinePaymentNode={
                                                            stripePromise && clientSecret ? (
                                                                <Elements key={clientSecret} stripe={stripePromise} options={{ clientSecret }}>
                                                                    <PaymentFormManualOrder
                                                                        url="removal_orders"
                                                                        method="POST"
                                                                        supplierId={(isSignageTeammember && parentSignageId ? parentSignageId : userId)?.toString() || ""}
                                                                        totalAmount={remaingamount ? remaingamount : Number(currentGrandTotal || removalOrder?.total_amount || 0)}
                                                                        paidAmount={
                                                                            formik.values.partial_amount === 0
                                                                                ? (Number(removalOrder?.total_amount || 0) - totalPaid)
                                                                                : formik.values.partial_amount
                                                                        }
                                                                        orderId={String(removalOrder?._id || removalOrder?.id || "")}
                                                                        orderNumber={String(removalOrder?.order_number || "")}
                                                                        customerName={String(removalOrder?.user_name || "")}
                                                                        customerPhone={String(removalOrder?.user_phone || "")}
                                                                        selectedPaymentMethod={selectedPaymentMethod?.name || "online"}
                                                                        skipRedirectOnSuccess
                                                                        onPaymentSuccess={async () => {
                                                                            localStorage.removeItem("cart_id");
                                                                            safeNavigate(0);
                                                                        }}
                                                                        userId={removalOrder?.inventory_source === "office" ? removalOrder?.team_member_id : removalOrder?.agent_id}
                                                                        userEmail={removalOrder?.user_email}
                                                                        paidBy_id={userId?.toString() ?? ""}
                                                                        paidBy_name={userName ?? ""}
                                                                        payments_status={removalOrder?.payment_status ?? ""}
                                                                    />
                                                                </Elements>
                                                            ) : null
                                                        }
                                                    />
                                                )}
                                            </div>
                                    }
                                </div>

                                <div className="col-lg-12 rounded-3 shadow-sm d-none d-lg-block">
                                    <OrderLog title="Removal Log" orderKind="removal" events={removalLogEvents} />
                                </div>

                                {/* for desktop view back and submit button */}
                                <div className="row col-lg-12 mb-3 rcust-margin-4 d-none d-lg-block" style={{ marginTop: '27px' }}>
                                    <div className="col-lg-12 d-flex justify-content-between">
                                        <div className="col-lg-3">
                                            <button
                                                type="button"
                                                onClick={() => navigate(`${baseRoute}/jobs/removalRequests`)}
                                                className="btn back-btn surface-btn fw-bold fs-16 w-100"
                                            >
                                                <i className="bi bi-chevron-left me-2" />
                                                Back
                                            </button>
                                        </div>

                                        <div className="col-lg-9 d-flex align-items-baseline justify-content-end">
                                            {renderViewModeActions()}
                                            {!isViewMode && (
                                                <>
                                                    {/* Desktop */}
                                                    <button
                                                        type="submit"
                                                        className="btn btn-primary save-btn surface-btn fw-bold fs-16 d-none d-lg-block"
                                                        disabled={formSubmitting}
                                                    >
                                                        Save
                                                    </button>

                                                    {/* Mobile */}
                                                    <button
                                                        type="submit"
                                                        className="btn btn-primary save-btn surface-btn fw-bold fs-16 d-block d-lg-none w-100"
                                                        disabled={formSubmitting}
                                                    >
                                                        Save
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* for mobile and tab view back and submit button */}
                                <div className="row mb-3 col-lg-12 rcust-margin-4 d-block d-lg-none" style={{ marginTop: '27px' }}>
                                    <div className="col-lg-12 d-block d-lg-flex justify-content-between">
                                        <div className="col-lg-2 mb-3">
                                            <button
                                                type="button"
                                                onClick={() => navigate(`${baseRoute}/jobs/removalRequests`)}
                                                className="btn back-btn surface-btn fw-bold fs-16 w-100"
                                            >
                                                <i className="bi bi-chevron-left me-2" />
                                                Back
                                            </button>
                                        </div>

                                        <div className="align-items-baseline col-lg-3 d-lg-flex justify-content-end">
                                            {renderViewModeActions()}
                                            {!isViewMode && (
                                                <>
                                                    {/* Desktop */}
                                                    <button
                                                        type="submit"
                                                        className="btn btn-primary save-btn surface-btn fw-bold fs-16 d-none d-lg-block"
                                                        disabled={formSubmitting || isRecomputing}
                                                    >
                                                        {isRecomputing ? "Calculating..." : "Save"}
                                                    </button>

                                                    {/* Mobile */}
                                                    <button
                                                        type="submit"
                                                        className="btn btn-primary save-btn surface-btn fw-bold fs-16 d-block d-lg-none w-100"
                                                        disabled={formSubmitting || isRecomputing}
                                                    >
                                                        {isRecomputing ? "Calculating..." : "Save"}
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </>
                        }
                    </form>
                </div>
                {/* <DeleteConfirmModal
                    open={showReportTaxConfirmModal}
                    dynamicName="transaction"
                    title="Confirm"
                    message={<>Click <strong>Report Live Taxes</strong> to proceed and complete the transaction.</>}
                    confirmLabel="OK"
                    onCancel={() => setShowReportTaxConfirmModal(false)}
                    onDelete={() => setShowReportTaxConfirmModal(false)}
                /> */}
            </div >
        </div >
    );
};

export default SignageRemovalWorkOrder;
